
// === constants.js ===
/**
 * MapConstants - Shared constants for all map modules
 *
 * @fileoverview Centralizes magic numbers and configuration values.
 * All modules should reference these instead of hardcoding values.
 */
(function(window) {
    'use strict';

    var MapConstants = {
        // ============================================
        // Breakpoints
        // ============================================
        MOBILE_BREAKPOINT_PX: 768,

        // ============================================
        // Timing (milliseconds)
        // ============================================
        POPUP_DELAY_MOBILE: 200,
        POPUP_DELAY_DESKTOP: 350,
        DEBOUNCE_SEARCH: 300,
        MAP_RESIZE_DELAY: 350,
        HINT_AUTO_DISMISS: 5000,
        ANIMATION_DURATION: 200,

        // ============================================
        // Marker Sizes (pixels)
        // ============================================
        MARKER_SIZE_MOBILE: 36,
        MARKER_SIZE_DESKTOP: 28,
        MARKER_INNER_RATIO: 0.65,
        MARKER_FONT_RATIO: 0.5,

        // ============================================
        // Cluster Sizes (pixels)
        // ============================================
        CLUSTER_SIZE_SMALL: 40,
        CLUSTER_SIZE_MEDIUM: 48,
        CLUSTER_SIZE_LARGE: 56,
        CLUSTER_THRESHOLD_MEDIUM: 10,
        CLUSTER_THRESHOLD_LARGE: 50,

        // ============================================
        // Clustering Behavior
        // ============================================
        CLUSTER_RADIUS_MOBILE: 40,
        CLUSTER_RADIUS_DESKTOP: 50,
        UNCLUSTER_ZOOM_MOBILE: 13,
        UNCLUSTER_ZOOM_DESKTOP: 14,
        SPIDERFY_DISTANCE_MOBILE: 2.2,
        SPIDERFY_DISTANCE_DESKTOP: 1.8,
        LOCATION_PRECISION: 4,
        MAX_EXPANDED_MARKERS: 12,

        // ============================================
        // Zoom Levels
        // ============================================
        ZOOM_PARISH_FAVICON: 14,
        ZOOM_VIEWPORT_THRESHOLD: 6,
        ZOOM_FOCUS: 15,
        ZOOM_SELECT: 13,

        // ============================================
        // Touch & Gestures
        // ============================================
        SWIPE_THRESHOLD_PX: 80,
        SWIPE_MAX_TRANSLATE_PX: 200,
        TOUCH_START_THRESHOLD: 20,

        // ============================================
        // Data Thresholds
        // ============================================
        LARGE_DATASET_THRESHOLD: 5000,

        // ============================================
        // Pagination
        // ============================================
        SIDEBAR_PAGE_SIZE: 25,  // Reduced for faster initial render

        // ============================================
        // Nearby / Geolocation
        // ============================================
        NEARBY_PREVIEW_COUNT: 2,
        NEARBY_MAX_COUNT: 10,
        NEARBY_RADIUS_MI: 10,
        GEO_TIMEOUT_MS: 10000,
        GEO_MAX_AGE_MS: 60000,

        // ============================================
        // Colors (CSS custom properties preferred, these are fallbacks)
        // ============================================
        COLOR_PRIMARY: '#3b82f6',
        COLOR_CLUSTER_MEDIUM: '#8b5cf6',
        COLOR_CLUSTER_LARGE: '#d97706',
        COLOR_SUCCESS: '#4ade80',
        COLOR_WARNING: '#fbbf24',
        COLOR_MUTED: '#8899a6',

        // ============================================
        // Storage Keys
        // ============================================
        STORAGE_SIDEBAR_COLLAPSED: 'sidebarCollapsed',
        STORAGE_USE_CLUSTERING: 'useClustering',
        STORAGE_MAP_THEME: 'mapTheme',
        STORAGE_HINT_SHOWN: 'ubimissa_map_hint_shown',
        STORAGE_BADGE_LEFT: 'statsBadgeLeft',
        STORAGE_BADGE_BOTTOM: 'statsBadgeBottom',
        STORAGE_USER_LOCATION: 'ubimissa_user_location'
    };

    // Freeze to prevent accidental mutation
    if (Object.freeze) {
        Object.freeze(MapConstants);
    }

    window.MapConstants = MapConstants;
})(window);


// === state.js ===
/**
 * MapState - Centralized state management for church map
 *
 * All map modules read/write state through this singleton.
 * Pattern: IIFE exposing window.MapState
 *
 * @fileoverview Central state container for map application
 * @depends MapConstants
 */

/**
 * @typedef {Object} Church
 * @property {string} name - Church name
 * @property {string} city - City name
 * @property {string} state - State abbreviation
 * @property {number} lat - Latitude
 * @property {number} lon - Longitude
 * @property {string} [website] - Website URL
 * @property {string} [address] - Street address
 * @property {string} [zip_code] - ZIP code
 * @property {string} [mass_times] - Mass times description
 * @property {Array|Object} [mass_times_json] - Structured mass times
 * @property {string} [confession_times] - Confession schedule
 * @property {string} [adoration_times] - Adoration schedule
 * @property {string[]} [languages] - Languages for Mass
 * @property {number} [distance] - Distance from user (when sorted by location)
 */

/**
 * @typedef {Object} MassTime
 * @property {string} day - Day of week
 * @property {string} time - Time string (e.g., "9:00 AM")
 * @property {string} [type] - Type: 'sunday', 'vigil', 'weekday'
 * @property {string} [language] - Language if not English
 */

(function(window) {
    'use strict';

    var C = window.MapConstants || {};

    // Core map state
    var state = {
        // Leaflet map instance
        map: null,

        // Data
        allChurches: [],
        filteredItems: [],
        viewportItems: [],

        // Markers
        itemMarkers: {},
        markersClusterLayer: null,
        markersLogoLayer: null,
        markersLayer: null,
        useClustering: false,

        // Selection
        selectedMarker: null,
        selectedItem: null,
        userLocationMarker: null,

        // Filters
        selectedDay: 'all',

        // Pagination
        sidebarPage: 0,
        cachedSidebarEntities: [],

        // View state
        currentView: 'markers',
        viewportOnly: false,
        mapTheme: 'dark',
        baseLayer: null,

        // Visualization layers
        hexLayer: null,
        columnsLayer: null,
        densityHeatLayer: null,

        // Hex filter state (for click-to-filter on hex/bars)
        hexFilterActive: false,
        hexFilterBounds: null,

        // Auto-switch view on zoom
        autoSwitchEnabled: false,
        lastAutoSwitch: 0,

        // Cache
        liturgyCache: {},

        // Chart (legacy, may be removed)
        programChart: null
    };

    // Config access (from window.MAP_CONFIG)
    function getConfig() {
        return window.MAP_CONFIG || {};
    }

    // Get denomination colors from config
    function getCategoryColors() {
        var config = getConfig();
        return config.categoryColors || {
            'Catholic': '#6B2C91',
            'Roman Catholic': '#6B2C91',
            'Byzantine Catholic': '#8B0000',
            'Eastern Catholic': '#4B0082',
            'Anglican': '#0047AB',
            'Episcopal': '#800000',
            'default': '#6B2C91'
        };
    }

    // Get denomination emojis from config
    function getCategoryEmojis() {
        var config = getConfig();
        return config.categoryEmojis || {
            'Catholic': '⛪',
            'Anglican': '⛪',
            'Episcopal': '⛪',
            'default': '⛪'
        };
    }

    // Get labels from config
    function getLabels() {
        var config = getConfig();
        return config.labels || {
            item: 'church',
            items: 'churches',
            category: 'denomination',
            categories: 'denominations'
        };
    }

    // SmartDefaults - adapts behavior based on data size
    function getSmartDefaults() {
        return window.SmartDefaults || {
            get: function(count) {
                var isSmall = count < 2000;
                return {
                    tier: isSmall ? 'small' : 'large',
                    sidebar: { pageSize: 9999 },
                    markers: { maxLogos: isSmall ? count : 300, useH3Filtering: !isSmall }
                };
            },
            getTier: function(count) { return count < 2000 ? 'small' : 'large'; },
            getMarkerLimit: function(zoom, tier, total) { return tier === 'small' ? total : 300; },
            getH3Resolution: function(zoom) { return zoom <= 6 ? 4 : zoom <= 9 ? 5 : 6; }
        };
    }

    // Debug logger
    var DEBUG = false;
    function initDebug() {
        DEBUG = window.location.hostname === 'localhost' ||
                window.location.search.indexOf('debug=1') !== -1;
    }

    function log() {
        if (DEBUG && console && console.log) {
            console.log.apply(console, ['[MapState]'].concat(Array.prototype.slice.call(arguments)));
        }
    }

    /**
     * Check if current viewport is mobile-sized
     * @returns {boolean}
     */
    function isMobile() {
        var breakpoint = C.MOBILE_BREAKPOINT_PX || 768;
        return window.innerWidth <= breakpoint;
    }

    // Diocese detection
    function getDiocese() {
        return window.DIOCESE || null;
    }

    function isAnglican() {
        var diocese = getDiocese();
        if (diocese === 'anglican' || diocese === 'episcopal') return true;
        var hostname = window.location.hostname.toLowerCase();
        return hostname.indexOf('anglican') !== -1 || hostname.indexOf('episcopal') !== -1;
    }

    // Public API
    window.MapState = {
        // State access
        get: function(key) {
            return state[key];
        },
        set: function(key, value) {
            state[key] = value;
            log('Set', key, '=', typeof value === 'object' ? '[object]' : value);
        },

        // Batch update
        update: function(updates) {
            for (var key in updates) {
                if (updates.hasOwnProperty(key)) {
                    state[key] = updates[key];
                }
            }
            log('Batch update:', Object.keys(updates).join(', '));
        },

        // Config helpers
        getConfig: getConfig,
        getCategoryColors: getCategoryColors,
        getCategoryEmojis: getCategoryEmojis,
        getLabels: getLabels,
        getSmartDefaults: getSmartDefaults,

        // Utilities
        log: log,
        isMobile: isMobile,
        initDebug: initDebug,
        getDiocese: getDiocese,
        isAnglican: isAnglican,

        // Direct state access (for modules that need iteration)
        _state: state
    };

    // Initialize debug mode
    initDebug();

})(window);


// === utils.js ===
/**
 * MapUtils - Shared utility functions
 *
 * Common utilities used across map modules.
 * Depends on: MapState, MapShared (optional)
 */
(function(window) {
    'use strict';

    var State = window.MapState;
    var Shared = window.MapShared || {};

    /**
     * XSS Protection: Escape HTML entities
     */
    var escapeHtml = Shared.escapeHtml || function(text) {
        if (!text) return '';
        var div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    };

    /**
     * Escape string for use in HTML attributes (onclick, etc.)
     */
    function escapeAttr(str) {
        if (str === null || str === undefined) return '';
        return String(str)
            .replace(/\\/g, '\\\\')
            .replace(/'/g, "\\'")
            .replace(/"/g, '\\"');
    }

    /**
     * Translation helper with fallback
     */
    function translate(key, fallback, params) {
        try {
            var result = null;

            // Try i18n.t()
            if (window.i18n && window.i18n.t) {
                result = window.i18n.t(key, fallback);
                if (result && result !== key) {
                    return applyParams(result, params);
                }
            }

            // Try i18n.translate()
            if (window.i18n && window.i18n.translate) {
                result = window.i18n.translate(key, fallback);
                if (result && result !== key) {
                    return applyParams(result, params);
                }
            }

            // Try translations object directly
            if (window.translations && window.currentLang) {
                var lang = window.currentLang || 'en';
                var translations = window.translations[lang] || window.translations.en || {};
                if (translations[key]) {
                    return applyParams(translations[key], params);
                }
            }

            return applyParams(fallback || key, params);
        } catch (e) {
            console.warn('Translation error for key:', key, e);
            return fallback || key;
        }
    }

    function applyParams(str, params) {
        if (!params || !str) return str;
        Object.keys(params).forEach(function(k) {
            str = str.replace(new RegExp('\\{' + k + '\\}', 'g'), params[k]);
        });
        return str;
    }

    /**
     * Build normalized website URL
     */
    function buildWebsiteUrl(website) {
        if (!website) return '';

        // Remove any existing protocol
        var domain = website.replace(/^https?:\/\//, '').replace(/\/$/, '');

        // Fix common typos
        domain = domain.replace(/\.chruch\b/g, '.church');

        // Special case handling
        if (domain.toLowerCase().indexOf('olmcboonton.org') !== -1) {
            domain = domain.replace(/^www\./i, '');
        } else {
            // Add www if not present (most sites use www)
            if (domain.indexOf('www.') !== 0) {
                domain = 'www.' + domain;
            }
        }

        return 'https://' + domain;
    }

    /**
     * Get color for a program/denomination
     */
    function getProgramColor(program) {
        var colors = State.getCategoryColors();
        if (!program) return colors['default'] || '#8899a6';

        // Direct match
        if (colors[program]) return colors[program];

        // Partial match
        var programLower = program.toLowerCase();
        for (var key in colors) {
            if (key === 'default') continue;
            var keyLower = key.toLowerCase();
            if (programLower.indexOf(keyLower) !== -1 || keyLower.indexOf(programLower) !== -1) {
                return colors[key];
            }
        }

        return colors['default'] || '#8899a6';
    }

    /**
     * Get emoji for a program/denomination
     */
    function getProgramEmoji(program) {
        var emojis = State.getCategoryEmojis();
        if (!program) return emojis['default'] || '⛪';

        // Direct match
        if (emojis[program]) return emojis[program];

        // Partial match
        var programLower = program.toLowerCase();
        for (var key in emojis) {
            if (key === 'default') continue;
            var keyLower = key.toLowerCase();
            if (programLower.indexOf(keyLower) !== -1 || keyLower.indexOf(programLower) !== -1) {
                return emojis[key];
            }
        }

        // Fuzzy matching for common terms
        if (programLower.indexOf('catholic') !== -1) return '⛪';
        if (programLower.indexOf('episcopal') !== -1 || programLower.indexOf('anglican') !== -1) return '⛪';
        if (programLower.indexOf('orthodox') !== -1) return '☦️';

        return emojis['default'] || '⛪';
    }

    /**
     * Haversine distance calculation (miles)
     */
    var haversineDistance = Shared.haversineDistance || function(lat1, lon1, lat2, lon2) {
        var R = 3959; // Earth's radius in miles
        var dLat = (lat2 - lat1) * Math.PI / 180;
        var dLon = (lon2 - lon1) * Math.PI / 180;
        var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
        var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    /**
     * Format distance for display
     */
    var formatDistance = Shared.formatDistance || function(miles) {
        if (miles < 0.1) return '< 0.1 mi';
        if (miles < 10) return miles.toFixed(1) + ' mi';
        return Math.round(miles) + ' mi';
    };

    /**
     * Format time to 12-hour format
     */
    function formatTime12Hour(timeStr) {
        if (!timeStr) return '';

        // Already in 12-hour format
        if (timeStr.match(/\d{1,2}:\d{2}\s*(AM|PM)/i)) {
            return timeStr.replace(/\s*(am|pm)/i, function(m) {
                return ' ' + m.trim().toUpperCase();
            });
        }

        // Convert 24-hour to 12-hour
        var match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
        if (match) {
            var h = parseInt(match[1], 10);
            var m = match[2];
            var ampm = h >= 12 ? 'PM' : 'AM';
            if (h > 12) h -= 12;
            if (h === 0) h = 12;
            return h + ':' + m + ' ' + ampm;
        }

        return timeStr;
    }

    /**
     * Get diocese-specific default favicon
     */
    function getDefaultFavicon() {
        // Try diocese config
        if (window.dioceseConfig) {
            try {
                var config = window.dioceseConfig.getDioceseConfig();
                if (config) {
                    if (config.branding && config.branding.faviconUrl) {
                        return config.branding.faviconUrl;
                    }
                    if (config.slug) {
                        return '/assets/favicons/dioceses/' + config.slug + '.svg';
                    }
                }
            } catch (e) {
                // Config not loaded yet
            }
        }

        // Try to detect from hostname
        var hostname = window.location.hostname.toLowerCase();
        var slugMatch = hostname.match(/^([a-z]+)churches/);
        if (slugMatch) {
            return '/assets/favicons/dioceses/' + slugMatch[1] + '.svg';
        }

        return '/favicon.svg';
    }

    /**
     * Generate slug from church name + city for uniqueness
     */
    function generateSlug(name, city) {
        if (!name) return '';
        var nameSlug = name.toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
        if (city) {
            var citySlug = city.toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '');
            return nameSlug + '-' + citySlug;
        }
        return nameSlug;
    }

    /**
     * Translate church name based on current language
     */
    function translateChurchName(name, lang) {
        if (window.translateChurchName) {
            return window.translateChurchName(name, lang);
        }
        return name;
    }

    /**
     * Get current language
     */
    function getCurrentLanguage() {
        if (window.i18n && window.i18n.getCurrentLanguage) {
            return window.i18n.getCurrentLanguage();
        }
        return window.currentLang || 'en';
    }

    /**
     * Build localized URL
     */
    function buildLocalizedUrl(path) {
        if (window.buildLocalizedUrl) {
            return window.buildLocalizedUrl(path);
        }
        return path;
    }

    // Public API
    window.MapUtils = {
        escapeHtml: escapeHtml,
        escapeAttr: escapeAttr,
        translate: translate,
        buildWebsiteUrl: buildWebsiteUrl,
        getProgramColor: getProgramColor,
        getProgramEmoji: getProgramEmoji,
        haversineDistance: haversineDistance,
        formatDistance: formatDistance,
        formatTime12Hour: formatTime12Hour,
        getDefaultFavicon: getDefaultFavicon,
        generateSlug: generateSlug,
        translateChurchName: translateChurchName,
        getCurrentLanguage: getCurrentLanguage,
        buildLocalizedUrl: buildLocalizedUrl
    };

})(window);


// === init.js ===
/**
 * MapInit - Map initialization module
 *
 * Handles Leaflet map creation, base layers, and cluster setup.
 * Depends on: MapState, Leaflet, MarkerCluster
 */
(function(window) {
    'use strict';

    var State = window.MapState;
    var log = State.log;

    /**
     * Create base tile layer
     */
    function createBaseLayer(theme) {
        var url = theme === 'light'
            ? 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
            : 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';

        return L.tileLayer(url, {
            attribution: '© OpenStreetMap contributors © CARTO',
            subdomains: 'abcd',
            maxZoom: 19
        });
    }

    /**
     * Create marker cluster layer with favicon mosaic icons
     */
    function createClusterLayer(map) {
        var C = window.MapConstants;
        var isMobile = State.isMobile();

        return L.markerClusterGroup({
            chunkedLoading: true,
            spiderfyOnMaxZoom: true,
            spiderfyDistanceMultiplier: isMobile ? C.SPIDERFY_DISTANCE_MOBILE : C.SPIDERFY_DISTANCE_DESKTOP,
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            maxClusterRadius: isMobile ? C.CLUSTER_RADIUS_MOBILE : C.CLUSTER_RADIUS_DESKTOP,
            disableClusteringAtZoom: isMobile ? C.UNCLUSTER_ZOOM_MOBILE : C.UNCLUSTER_ZOOM_DESKTOP,
            iconCreateFunction: function(cluster) {
                var count = cluster.getChildCount();
                var size, borderColor, fontSize, borderRadius;

                // Size, color, and styling based on count
                if (count < 10) {
                    size = 48;
                    borderColor = '#3b82f6'; // Blue
                    fontSize = 11;
                    borderRadius = 12;
                } else if (count < 50) {
                    size = 56;
                    borderColor = '#8b5cf6'; // Purple
                    fontSize = 12;
                    borderRadius = 14;
                } else if (count < 100) {
                    size = 64;
                    borderColor = '#a855f7'; // Violet
                    fontSize = 13;
                    borderRadius = 14;
                } else if (count < 500) {
                    size = 72;
                    borderColor = '#f59e0b'; // Amber
                    fontSize = 14;
                    borderRadius = 16;
                } else {
                    size = 80;
                    borderColor = '#ef4444'; // Red
                    fontSize = 15;
                    borderRadius = 16;
                }

                // Get churches from cluster for favicon display
                var churches = [];
                cluster.getAllChildMarkers().forEach(function(marker) {
                    if (marker.options && marker.options.church) {
                        churches.push(marker.options.church);
                    }
                });

                // Create favicon grid HTML
                var faviconHtml = '';
                if (window.MapMarkerIcons && window.MapMarkerIcons.createClusterFaviconGrid) {
                    faviconHtml = window.MapMarkerIcons.createClusterFaviconGrid(churches, size);
                }

                // Cluster icon: rounded square "card" with favicon grid and count badge
                return L.divIcon({
                    html: '<div style="' +
                        'position:relative;' +
                        'width:' + size + 'px;' +
                        'height:' + size + 'px;' +
                        'background:rgba(15,23,42,0.92);' +
                        'border:3px solid ' + borderColor + ';' +
                        'border-radius:' + borderRadius + 'px;' +
                        'box-shadow:0 4px 16px rgba(0,0,0,0.5),inset 0 0 0 1px rgba(255,255,255,0.08);' +
                        'overflow:visible;' +
                        '">' +
                        faviconHtml +
                        '<div style="' +
                        'position:absolute;' +
                        'top:-10px;' +
                        'right:-10px;' +
                        'background:' + borderColor + ';' +
                        'color:white;' +
                        'padding:3px 8px;' +
                        'border-radius:12px;' +
                        'font-weight:700;' +
                        'font-size:' + fontSize + 'px;' +
                        'font-family:Inter,-apple-system,sans-serif;' +
                        'min-width:24px;' +
                        'text-align:center;' +
                        'box-shadow:0 2px 8px rgba(0,0,0,0.4),0 0 0 2px rgba(255,255,255,0.15);' +
                        'letter-spacing:-0.5px;' +
                        '">' + count + '</div></div>',
                    className: 'favicon-cluster',
                    iconSize: [size + 20, size + 20],
                    iconAnchor: [(size + 20)/2, (size + 20)/2]
                });
            }
        });
    }

    /**
     * Initialize the Leaflet map
     */
    function init() {
        log('Initializing map...');

        // Check if Leaflet is loaded
        if (typeof L === 'undefined') {
            console.error('Leaflet library not loaded - waiting and retrying...');
            setTimeout(init, 100);
            return;
        }

        var config = State.getConfig();
        var mapDefaults = config.map || {};
        var isMobile = State.isMobile();

        // Check for map container
        var mapElement = document.getElementById('map');
        if (!mapElement) {
            throw new Error('Map container element #map not found');
        }

        // Get center from diocese config or defaults
        var defaultCenter = window.MAP_CENTER || mapDefaults.center || [39.8283, -98.5795];

        // Check if this is ubimissa.com (global view)
        var isUbimissa = window.location.hostname === 'ubimissa.com' || window.location.hostname.includes('ubimissa');
        var isDioceseGlobal = State.getDiocese && State.getDiocese() === 'global';

        // Set appropriate zoom for global vs diocese views
        var defaultZoom = window.MAP_ZOOM || (typeof mapDefaults.zoom === 'number' ? mapDefaults.zoom :
            ((isUbimissa || isDioceseGlobal) ? (isMobile ? 3 : 3) : (isMobile ? 3 : 4)));

        // Try to get diocese center
        if (window.dioceseConfig) {
            try {
                var dioceseCenter = window.dioceseConfig.getDioceseCenter();
                if (dioceseCenter && dioceseCenter.lat && dioceseCenter.lng) {
                    defaultCenter = [dioceseCenter.lat, dioceseCenter.lng];
                }
            } catch (e) {
                // Diocese config not loaded yet
            }
        }

        var minZoom = window.MAP_MIN_ZOOM || (typeof mapDefaults.minZoom === 'number' ? mapDefaults.minZoom : 1);
        var maxZoom = typeof mapDefaults.maxZoom === 'number' ? mapDefaults.maxZoom : 18;
        var targetZoom = Math.max(defaultZoom, minZoom);

        // Create Leaflet map
        var map;
        try {
            map = L.map('map', {
                center: defaultCenter,
                zoom: targetZoom,
                minZoom: minZoom,
                maxZoom: maxZoom,
                scrollWheelZoom: true,
                tap: true,
                touchZoom: true,
                zoomControl: true,
                attributionControl: false
            });
        } catch (e) {
            console.error('Failed to initialize map:', e);
            mapElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#ef4444;font-family:sans-serif;text-align:center;padding:20px;"><div><h2>Map Failed to Load</h2><p>Please refresh the page or try again later.</p><p style="font-size:12px;color:#666;">' + e.message + '</p></div></div>';
            throw e;
        }

        // Set up base layer
        var mapTheme = localStorage.getItem('mapTheme') || 'dark';
        var baseLayer = createBaseLayer(mapTheme).addTo(map);

        // Create marker layers
        var markersClusterLayer = createClusterLayer(map);
        var markersLogoLayer = L.layerGroup();

        // Create visualization layers
        var hexLayer = L.layerGroup();
        var columnsLayer = L.layerGroup();

        // All markers go through cluster layer for spatial de-confliction
        // Logo layer is used only for expanded stack fan-out markers
        markersClusterLayer.addTo(map);
        markersLogoLayer.addTo(map);

        // Store in state
        State.update({
            map: map,
            mapTheme: mapTheme,
            baseLayer: baseLayer,
            markersClusterLayer: markersClusterLayer,
            markersLogoLayer: markersLogoLayer,
            markersLayer: markersClusterLayer,
            useClustering: true,
            currentView: 'markers',
            hexLayer: hexLayer,
            columnsLayer: columnsLayer
        });

        // For ubimissa.com, fit to continental US bounds after map creation
        if ((isUbimissa || isDioceseGlobal) && !window.location.search.includes('state=') && !window.location.search.includes('diocese=')) {
            // Continental US bounds with padding
            var usBounds = [[24.396308, -125.0], [49.384358, -66.93457]];
            setTimeout(function() {
                if (map) {
                    map.fitBounds(usBounds, { padding: [20, 20] });
                }
            }, 100);
        }

        // Dismiss skeleton shimmer once first tiles load
        baseLayer.once('load', function() {
            var skeleton = document.getElementById('mapSkeleton');
            if (skeleton) {
                skeleton.classList.add('loaded');
                setTimeout(function() {
                    if (skeleton.parentNode) skeleton.parentNode.removeChild(skeleton);
                }, 500);
            }
        });
        // Fallback: dismiss skeleton after 3s even if tiles haven't loaded
        setTimeout(function() {
            var skeleton = document.getElementById('mapSkeleton');
            if (skeleton && !skeleton.classList.contains('loaded')) {
                skeleton.classList.add('loaded');
                setTimeout(function() {
                    if (skeleton.parentNode) skeleton.parentNode.removeChild(skeleton);
                }, 500);
            }
        }, 3000);

        // Expose map globally for backwards compatibility and debugging
        window.map = map;
        window.leafletMap = map;
        window.mapInstance = map;

        // DISABLED: FastMapLoader breaks tile loading
        // if (window.FastMapLoader) {
        //     window.fastLoader = new window.FastMapLoader(map, {
        //         clustering: defaultUseClustering
        //     });
        //     log('FastMapLoader initialized for optimized performance');
        // }

        // Handle diocese config async update
        if (window.dioceseConfig && window.dioceseConfig.getDioceseConfigAsync) {
            window.dioceseConfig.getDioceseConfigAsync().then(function(dioceseConfig) {
                if (dioceseConfig && dioceseConfig.center) {
                    var newCenter = [dioceseConfig.center.lat, dioceseConfig.center.lng];
                    var finalZoom = Math.max(defaultZoom, minZoom);

                    if (dioceseConfig.bounds) {
                        var bounds = [
                            [dioceseConfig.bounds.south, dioceseConfig.bounds.west],
                            [dioceseConfig.bounds.north, dioceseConfig.bounds.east]
                        ];
                        map.fitBounds(bounds, { padding: [50, 50] });
                    } else {
                        map.setView(newCenter, finalZoom);
                    }
                }
            }).catch(function(err) {
                console.warn('Failed to load diocese config for map:', err);
            });
        }

        log('Map initialized successfully');
        return map;
    }

    /**
     * Toggle map theme (light/dark)
     */
    function toggleTheme() {
        var map = State.get('map');
        var currentTheme = State.get('mapTheme');
        var baseLayer = State.get('baseLayer');

        var newTheme = currentTheme === 'dark' ? 'light' : 'dark';

        if (baseLayer) {
            map.removeLayer(baseLayer);
        }

        var newBaseLayer = createBaseLayer(newTheme).addTo(map);

        State.update({
            mapTheme: newTheme,
            baseLayer: newBaseLayer
        });

        localStorage.setItem('mapTheme', newTheme);
        log('Theme toggled to', newTheme);
    }

    // Public API
    window.MapInit = {
        init: init,
        createBaseLayer: createBaseLayer,
        createClusterLayer: createClusterLayer,
        toggleTheme: toggleTheme
    };

    // Also expose theme toggle globally for backwards compatibility
    window.toggleMapTheme = toggleTheme;

})(window);


// === data-loader.js ===
/**
 * MapDataLoader - Data loading and processing module
 *
 * Handles API loading, data processing, and caching.
 * Depends on: MapState
 */
(function(window) {
    'use strict';

    var State = window.MapState;
    var log = State.log;

    // Cache TTL
    var CACHE_TTL = 5 * 60 * 1000; // 5 minutes

    /**
     * Get cache key based on URL parameters
     * Different states/dioceses get separate cache entries
     */
    function getCacheKey() {
        var params = new URLSearchParams(window.location.search);
        var isGlobalView = window.dioceseConfig && window.dioceseConfig.isGlobalView && window.dioceseConfig.isGlobalView();
        // Only the global platform view supports query-param overrides like ?state=CA or ?diocese=la.
        var state = isGlobalView ? params.get('state') : null;
        var diocese = isGlobalView ? params.get('diocese') : null;
        var suffix = state ? '_state_' + state.toUpperCase() : (diocese ? '_diocese_' + diocese : '');
        return 'ubimissa_data_cache' + suffix;
    }

    /**
     * Load cached data from localStorage
     */
    function loadCachedData() {
        try {
            var cacheKey = getCacheKey();
            var cached = localStorage.getItem(cacheKey);
            if (!cached) return null;

            var data = JSON.parse(cached);
            var now = Date.now();

            // Check if cache is still valid
            if (data.timestamp && (now - data.timestamp) < CACHE_TTL) {
                log('Cache hit:', data.churches.length, 'churches');
                return data.churches;
            }

            // Cache expired
            log('Cache expired');
            localStorage.removeItem(cacheKey);
            return null;
        } catch (e) {
            console.warn('Error loading cache:', e);
            return null;
        }
    }

    /**
     * Save data to localStorage cache
     */
    function saveCacheData(churches) {
        try {
            var cacheKey = getCacheKey();
            var data = {
                timestamp: Date.now(),
                churches: churches
            };
            localStorage.setItem(cacheKey, JSON.stringify(data));
            log('Cached', churches.length, 'churches to', cacheKey);
        } catch (e) {
            console.warn('Error saving cache:', e);
        }
    }

    /**
     * Show progressive loading message (less intrusive than overlay)
     */
    function showProgressiveLoading(message, stage) {
        // Remove blocking overlay for progressive loading
        hideLoadingOverlay();

        var indicator = document.getElementById('progressiveIndicator');
        if (!indicator) {
            indicator = document.createElement('div');
            indicator.id = 'progressiveIndicator';
            indicator.style.cssText =
                'position:fixed;top:10px;right:10px;background:rgba(0,0,0,0.8);color:white;' +
                'padding:8px 12px;border-radius:6px;font-size:12px;z-index:9999;' +
                'transition:opacity 0.3s;opacity:0.9;';
            document.body.appendChild(indicator);
        }

        indicator.textContent = message;

        // Auto-hide after delay based on stage
        var delay = stage === 'priority' ? 2000 : (stage === 'viewport' ? 3000 : 5000);
        setTimeout(function() {
            if (indicator) {
                indicator.style.opacity = '0';
                setTimeout(function() {
                    if (indicator && indicator.parentNode) {
                        indicator.parentNode.removeChild(indicator);
                    }
                }, 300);
            }
        }, delay);
    }

    /**
     * Show loading overlay (only for critical failures)
     */
    function showLoadingOverlay(message) {
        var overlay = document.getElementById('mapLoadingOverlay');
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'mapLoadingOverlay';
            overlay.className = 'map-loading-overlay';
            overlay.innerHTML =
                '<div class="map-loading-content">' +
                '<div class="map-loading-spinner"></div>' +
                '<div class="map-loading-text">Loading churches...</div>' +
                '</div>';
            document.body.appendChild(overlay);
        }
        if (message) {
            var textEl = overlay.querySelector('.map-loading-text');
            if (textEl) textEl.textContent = message;
        }
        overlay.style.display = 'flex';
    }

    /**
     * Hide loading overlay
     */
    function hideLoadingOverlay() {
        var overlay = document.getElementById('mapLoadingOverlay');
        if (overlay) {
            overlay.style.display = 'none';
        }
    }

    /**
     * Load church data from API
     * Uses the optimized /api/churches/map endpoint for faster initial load
     */
    function loadFromAPI(callback) {
        log('Loading from API...');
        showLoadingOverlay('Loading churches...');

        // Build API URL with optional filters
        var urlParams = new URLSearchParams(window.location.search);
        var isGlobalView = window.dioceseConfig && window.dioceseConfig.isGlobalView && window.dioceseConfig.isGlobalView();
        var apiUrl = '/api/churches/map'; // Use optimized map endpoint

        // Only global view supports query-param overrides like ?state=CA or ?diocese=la.
        var stateParam = isGlobalView ? urlParams.get('state') : null;
        var dioceseParam = isGlobalView ? urlParams.get('diocese') : null;

        // Dedicated domains are hard-scoped. Still pass the inferred diocese to:
        // 1) Bypass stale cached /api/churches/map responses without params
        // 2) Guard against backend hostname detection regressions
        if (!isGlobalView) {
            var tenantId = null;
            try {
                if (window.dioceseConfig && window.dioceseConfig.getDioceseConfig) {
                    var tenantCfg = window.dioceseConfig.getDioceseConfig();
                    tenantId = tenantCfg && (tenantCfg.slug || tenantCfg.id);
                }
            } catch (e) {
                // Ignore and fall back
            }
            if (!tenantId && window.DIOCESE) tenantId = window.DIOCESE;
            if (tenantId && tenantId !== 'global') {
                dioceseParam = tenantId;
            }
        }
        var queryParts = [];
        if (stateParam) {
            queryParts.push('state=' + encodeURIComponent(stateParam));
            showLoadingOverlay('Loading ' + stateParam + ' churches...');
        } else if (dioceseParam) {
            queryParts.push('diocese=' + encodeURIComponent(dioceseParam));
        }
        if (queryParts.length > 0) {
            apiUrl += '?' + queryParts.join('&');
        }

        var fallbackUrl = '/api/churches/all?compact=1';
        if (queryParts.length > 0) {
            fallbackUrl += '&' + queryParts.join('&');
        }

        function loadFromFallback(initialError) {
            log('Map API failed, trying fallback:', initialError && initialError.message ? initialError.message : initialError);
            showProgressiveLoading('Loading churches (fallback)...', 'priority');

            fetch(fallbackUrl)
                .then(function(response) {
                    if (!response.ok) {
                        throw new Error('Fallback API returned ' + response.status);
                    }
                    return response.json();
                })
                .then(function(data) {
                    hideLoadingOverlay();
                    var churches = data.churches || data || [];
                    if (Array.isArray(churches) && churches.length > 0) {
                        log('Loaded', churches.length, 'churches from fallback API');
                        var processedFallback = processAPIData(churches);
                        saveCacheData(processedFallback);
                        callback(null, processedFallback);
                    } else {
                        callback(new Error('No church data in fallback response'));
                    }
                })
                .catch(function(error) {
                    hideLoadingOverlay();
                    console.error('Fallback API error:', error);
                    callback(error);
                });
        }

        fetch(apiUrl)
            .then(function(response) {
                if (!response.ok) {
                    throw new Error('API returned ' + response.status);
                }
                return response.json();
            })
            .then(function(data) {
                hideLoadingOverlay();
                if (data && data.success && Array.isArray(data.churches) && data.churches.length > 0) {
                    log('Loaded', data.churches.length, 'churches from API');

                    // Set icon defaults if available
                    if (window.iconResolver && data.iconDefaults) {
                        window.iconResolver.setDioceseDefaults(data.iconDefaults);
                    }

                    var processed = processMapAPIData(data.churches);
                    saveCacheData(processed);
                    callback(null, processed);
                } else {
                    loadFromFallback(new Error('No church data in response'));
                }
            })
            .catch(function(error) {
                hideLoadingOverlay();
                console.error('API error:', error);
                loadFromFallback(error);
            });
    }

    /**
     * Process data from the optimized /api/churches/map endpoint
     * Expands shortened field names from the compact response
     */
    function processMapAPIData(churches) {
        var processed = [];

        churches.forEach(function(church) {
            // Skip if missing coordinates
            if (typeof church.lat !== 'number' || typeof church.lng !== 'number') {
                return;
            }

            // Expand shortened field names from map API
            var expanded = {
                id: church.id || '',
                name: church.n || '',
                lat: church.lat,
                lon: church.lng,
                city: church.c || '',
                state: church.s || '',
                country: church.co || '',
                website: church.w || '',
                denomination: church.dn || 'Catholic',
                diocese_id: church.d || '',
                languages: church.l ? church.l.split(',').map(function(l) { return l.trim(); }) : [],
                hasConfession: church.hc || false,
                hasAdoration: church.ha || false,
                icon: church.ie || null,  // emoji icon
                logo: church.iu || '',    // icon URL
                // Default values for fields not in map API (loaded on-demand)
                mass_times: '',
                mass_times_json: null,
                description: '',
                programs: [church.dn || 'Catholic'],
                geocoding_confidence: 'unknown'
            };

            processed.push(expanded);
        });

        // Deduplicate by coordinates
        var coordSeen = {};
        var unique = [];

        processed.forEach(function(church) {
            var latKey = church.lat.toFixed(4);
            var lonKey = church.lon.toFixed(4);
            var coordKey = latKey + '_' + lonKey;

            if (!coordSeen[coordKey]) {
                coordSeen[coordKey] = true;
                unique.push(church);
            }
        });

        log('Processed', unique.length, 'churches from map API');
        return unique;
    }

    /**
     * Process raw API data into church objects
     */
    function processAPIData(churches) {
        var processed = [];

        churches.forEach(function(church) {
            // Skip if missing coordinates
            if (typeof church.latitude !== 'number' || typeof church.longitude !== 'number') {
                return;
            }

            // Skip centroid churches (low confidence geocoding)
            if (church.geocoding_confidence === 'centroid' || church.geocoding_confidence === 'city_centroid') {
                return;
            }

            // Parse languages
            var languagesArray = Array.isArray(church.languages)
                ? church.languages
                : (church.languages || '').split(',').map(function(lang) {
                    return lang.trim();
                }).filter(function(lang) {
                    return lang.length > 0;
                });

            // Build description parts
            var parts = [];
            if (church.description) {
                parts.push(church.description);
            }

            var isAnglican = State.isAnglican();
            var timesLabel = isAnglican ? 'Service Times' : 'Mass Times';

            if (church.mass_times) {
                parts.push(timesLabel + ':\n' + church.mass_times);
            }
            if (church.confession_times && (!window.FEATURES || window.FEATURES.hasConfession !== false)) {
                parts.push('Confession Times:\n' + church.confession_times);
            }
            if (church.adoration_times && (!window.FEATURES || window.FEATURES.hasAdoration !== false)) {
                parts.push('Adoration:\n' + church.adoration_times);
            }
            if (languagesArray.length > 0) {
                parts.push('Languages: ' + languagesArray.join(', '));
            }

            processed.push({
                id: church.id || '',
                name: church.church_name || '',
                lat: church.latitude,
                lon: church.longitude,
                city: church.city || '',
                state: church.state || '',
                country: church.country || '',
                zip_code: church.zip_code || '',
                website: church.website || '',
                denomination: church.denomination || 'Catholic',
                geocoding_confidence: church.geocoding_confidence || 'unknown',
                description: parts.join('\n\n'),
                languages: languagesArray,
                logo: church.logo || '',
                icon: church.icon || null,
                programs: [church.denomination || 'Catholic'],
                mass_times: church.mass_times || '',
                mass_times_json: church.mass_times_json || null,
                hasConfession: church.confession_times ? true : false,
                hasAdoration: church.adoration_times ? true : false
            });
        });

        // Enhanced deduplication by coordinates + name
        var seen = {};
        var coordSeen = {};
        var unique = [];
        var duplicatesFound = 0;

        processed.forEach(function(church) {
            // Round coordinates to 4 decimal places to catch near-duplicates
            var latKey = church.lat.toFixed(4);
            var lonKey = church.lon.toFixed(4);
            var coordKey = latKey + '_' + lonKey;
            var nameKey = (church.name + '_' + church.city + '_' + church.state).toLowerCase().trim();

            // Check for exact coordinate duplicates (likely data issue)
            if (coordSeen[coordKey]) {
                // Only keep if it has a significantly different name
                var existingChurch = coordSeen[coordKey];
                if (existingChurch.name.toLowerCase() !== church.name.toLowerCase()) {
                    // Different church at same location (e.g., same building)
                    if (!seen[nameKey]) {
                        seen[nameKey] = true;
                        unique.push(church);
                    }
                } else {
                    // Same church, skip duplicate
                    duplicatesFound++;
                    console.warn('Duplicate church filtered:', church.name, 'at', latKey, lonKey);
                }
            } else {
                // First church at this location
                coordSeen[coordKey] = church;
                seen[nameKey] = true;
                unique.push(church);
            }
        });

        if (duplicatesFound > 0) {
            log('Filtered out', duplicatesFound, 'duplicate churches');
        }

        log('Processed', unique.length, 'unique churches');
        return unique;
    }

    /**
     * Main data loading entry point
     */
    function load(callback) {
        // Check cache first
        var cached = loadCachedData();
        if (cached && cached.length > 0) {
            log('Using cached data');
            initializeWithData(cached);
            if (callback) callback(null, cached);

            // Refresh in background (update state only, don't re-render markers)
            setTimeout(function() {
                loadFromAPI(function(err, data) {
                    if (!err && data) {
                        // Only update state, don't trigger full re-initialization
                        // This prevents duplicate marker rendering
                        State.update({
                            allChurches: data,
                            filteredItems: data.slice()
                        });
                        log('Background refresh complete:', data.length, 'churches');
                    }
                });
            }, 100);
            return;
        }

        // Load fresh from API
        loadFromAPI(function(err, data) {
            if (err) {
                showLoadError();
                if (callback) callback(err);
                return;
            }
            initializeWithData(data);
            if (callback) callback(null, data);
        });
    }

    /**
     * Initialize state with loaded data
     */
    function initializeWithData(churches) {
        // Pre-parse mass times JSON for fast day filtering
        if (window.MapFilters && window.MapFilters.preparseMassTimes) {
            window.MapFilters.preparseMassTimes(churches);
        }

        State.update({
            allChurches: churches,
            filteredItems: churches.slice()
        });

        // Preload icons if resolver available
        if (window.iconResolver && churches.length > 0) {
            window.iconResolver.preloadIcons(churches);
        }

        // Dispatch event for other modules
        window.dispatchEvent(new CustomEvent('mapDataLoaded', {
            detail: { churches: churches, count: churches.length }
        }));

        log('Data initialized:', churches.length, 'churches');
    }

    /**
     * Show error when API fails
     */
    function showLoadError() {
        State.update({
            allChurches: [],
            filteredItems: []
        });

        var itemList = document.getElementById('itemList');
        if (itemList) {
            itemList.innerHTML =
                '<div class="no-results" style="background: rgba(255, 82, 82, 0.2); border: 1px solid #ff5252; color: #ff8a80; padding: 16px; border-radius: 8px; margin: 16px; text-align: center;">' +
                '<div style="font-size: 32px; margin-bottom: 8px;">⚠️</div>' +
                '<div style="font-weight: 600; margin-bottom: 4px;">Unable to load church data</div>' +
                '<div style="font-size: 13px; opacity: 0.8;">Please refresh the page or try again later.</div>' +
                '</div>';
        }
    }

    /**
     * Clear the data cache
     */
    function clearCache() {
        try {
            localStorage.removeItem(getCacheKey());
        } catch (e) {
            // Ignore storage errors
        }
        log('Cache cleared');
    }

    // Public API
    window.MapDataLoader = {
        load: load,
        loadFromAPI: loadFromAPI,
        loadCachedData: loadCachedData,
        clearCache: clearCache,
        processAPIData: processAPIData,
        processMapAPIData: processMapAPIData
    };

})(window);


// === marker-icons.js ===
/**
 * MapMarkerIcons - Marker icon creation and favicon resolution
 *
 * @fileoverview Creates marker icon HTML for individual markers,
 * clusters, and stacked markers. Handles favicon fallback chain.
 * @depends MapConstants, MapState
 */
(function(window) {
    'use strict';

    var C = window.MapConstants;
    var State = window.MapState;

    /**
     * Extract domain from website URL
     * @param {string|null} website - Website URL or null
     * @returns {string|null} Domain without www prefix, or null
     */
    function extractDomain(website) {
        if (!website) return null;
        try {
            var url = website.indexOf('http') === 0 ? website : 'https://' + website;
            return new URL(url).hostname.replace(/^www\./, '');
        } catch (e) {
            // Fallback for malformed URLs
            return website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] || null;
        }
    }

    /**
     * Get Google's favicon service URL with better fallback
     * @param {string} domain - Domain name
     * @param {number} [size=32] - Icon size
     * @returns {string} Google favicon URL
     */
    function getGoogleFaviconUrl(domain, size) {
        size = size || 32;
        // Use simpler Google favicon API that works better internationally
        return 'https://www.google.com/s2/favicons?sz=' + size + '&domain=' + encodeURIComponent(domain);
    }

    /**
     * Get diocese icon configuration from state or defaults
     * @returns {Object} Icon config with defaultIconType, defaultFaviconUrl, fallbackEmoji
     */
    function getDioceseIconConfig() {
        var config = State.get('dioceseIconConfig');
        if (config) return config;

        return {
            defaultIconType: 'favicon',
            defaultFaviconUrl: '/favicon.svg',
            fallbackEmoji: '⛪',
            iconSize: C.MARKER_SIZE_DESKTOP
        };
    }

    /**
     * Create marker icon HTML
     * Shows parish favicon from local cache, falls back to Google, then emoji
     *
     * @param {Church} church - Church data
     * @param {number} [zoom] - Unused (kept for API compatibility)
     * @param {number} [size] - Icon size in pixels (auto-detected based on mobile)
     * @returns {string} HTML string for the marker icon
     */
    function createMarkerIcon(church, zoom, size) {
        var isMobile = State.isMobile();
        var markerSize = size || (isMobile ? 40 : 32);
        var imgSize = Math.round(markerSize * 0.65);
        var fallbackEmoji = '⛪';

        // Get website
        var website = church.website || church.url || '';
        var domain = website && website.length > 3 ? extractDomain(website) : null;

        // Show favicon immediately if domain exists
        if (domain) {
            var googleFavicon = getGoogleFaviconUrl(domain);
            return '<div class="church-marker-icon" ' +
                'data-domain="' + domain + '" ' +
                'style="' +
                'width:' + markerSize + 'px;' +
                'height:' + markerSize + 'px;' +
                'background:white;' +
                'border-radius:50%;' +
                'border:2px solid #3b82f6;' +
                'display:flex;' +
                'align-items:center;' +
                'justify-content:center;' +
                'box-shadow:0 2px 8px rgba(0,0,0,0.3);' +
                '">' +
                '<img src="' + googleFavicon + '" ' +
                'style="width:' + imgSize + 'px;height:' + imgSize + 'px;border-radius:4px;" ' +
                'loading="lazy" ' +
                'onerror="this.style.display=\'none\';this.parentElement.innerHTML=\'<span style=font-size:' + imgSize + 'px>' + fallbackEmoji + '</span>\';" />' +
                '</div>';
        }

        // Fallback to emoji if no domain
        return '<div class="church-marker-icon" ' +
            'style="' +
            'width:' + markerSize + 'px;' +
            'height:' + markerSize + 'px;' +
            'background:white;' +
            'border-radius:50%;' +
            'border:2px solid #3b82f6;' +
            'display:flex;' +
            'align-items:center;' +
            'justify-content:center;' +
            'box-shadow:0 2px 8px rgba(0,0,0,0.3);' +
            'transition:transform 0.2s;' +
            '">' +
            '<span style="font-size:' + imgSize + 'px;">' + fallbackEmoji + '</span>' +
            '</div>';
    }

    /**
     * Get cluster color based on count - gradient from blue to purple to orange
     * @param {number} count - Number of items in cluster
     * @returns {Object} { bg, border, shadow } color values
     */
    function getClusterColors(count) {
        // Color scale: small (blue) -> medium (purple) -> large (orange/gold)
        if (count < C.CLUSTER_THRESHOLD_MEDIUM) {
            // Small: Blue gradient
            return {
                bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                border: 'rgba(59, 130, 246, 0.8)',
                shadow: 'rgba(59, 130, 246, 0.4)'
            };
        } else if (count < C.CLUSTER_THRESHOLD_LARGE) {
            // Medium: Purple gradient
            return {
                bg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                border: 'rgba(139, 92, 246, 0.8)',
                shadow: 'rgba(139, 92, 246, 0.4)'
            };
        } else {
            // Large: Gold/Orange gradient
            return {
                bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                border: 'rgba(245, 158, 11, 0.8)',
                shadow: 'rgba(245, 158, 11, 0.4)'
            };
        }
    }

    /**
     * Create stacked marker icon for multiple churches at same location
     * Shows a cloud of favicons/emojis for the churches
     * @param {number} count - Number of churches in stack
     * @param {Array} churches - Array of church objects (optional)
     * @returns {string} HTML string for stacked marker icon
     */
    function createStackedIcon(count, churches) {
        var size;

        if (count < C.CLUSTER_THRESHOLD_MEDIUM) {
            size = C.CLUSTER_SIZE_SMALL;
        } else if (count < C.CLUSTER_THRESHOLD_LARGE) {
            size = C.CLUSTER_SIZE_MEDIUM;
        } else {
            size = C.CLUSTER_SIZE_LARGE;
        }

        var colors = getClusterColors(count);

        // Always try to create favicon cloud when we have churches
        if (churches && churches.length > 0) {
            var maxIcons = Math.min(churches.length, 9); // Show up to 9 favicons
            var iconSize = Math.max(12, Math.floor(size / 4)); // Slightly larger icons
            var faviconHtml = '';

            for (var i = 0; i < maxIcons; i++) {
                var church = churches[i];
                // Try multiple fields for website
                var websiteUrl = church.website || church.url || church.web || '';
                var domain = websiteUrl ? extractDomain(websiteUrl) : null;

                // Position in a grid/cloud pattern for better visibility
                var positions = [
                    {x: 25, y: 25}, {x: 50, y: 20}, {x: 75, y: 25},  // Top row
                    {x: 20, y: 50}, {x: 50, y: 50}, {x: 80, y: 50},  // Middle row
                    {x: 25, y: 75}, {x: 50, y: 80}, {x: 75, y: 75}   // Bottom row
                ];
                var pos = positions[i % positions.length];
                var x = pos.x;
                var y = pos.y;

                if (domain) {
                    var googleFavicon = getGoogleFaviconUrl(domain, 16);
                    faviconHtml += '<img src="' + googleFavicon + '" ' +
                        'style="position:absolute;width:' + iconSize + 'px;height:' + iconSize + 'px;' +
                        'left:' + x + '%;top:' + y + '%;transform:translate(-50%,-50%);' +
                        'border-radius:50%;background:white;border:1px solid rgba(255,255,255,0.8);' +
                        'box-shadow:0 1px 3px rgba(0,0,0,0.2);" ' +
                        'onerror="this.style.display=\'none\';" />';
                } else {
                    faviconHtml += '<span style="position:absolute;font-size:' + (iconSize * 0.8) + 'px;' +
                        'left:' + x + '%;top:' + y + '%;transform:translate(-50%,-50%);">⛪</span>';
                }
            }

            // Add count overlay
            var countSize = count < 10 ? 16 : (count < 100 ? 14 : 12);
            faviconHtml += '<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);' +
                'background:rgba(0,0,0,0.8);color:white;border-radius:50%;padding:4px 8px;' +
                'font-size:' + countSize + 'px;font-weight:bold;min-width:24px;text-align:center;">' + count + '</div>';

            return '<div class="stacked-marker-favicon-cloud" style="' +
                'position:relative;' +
                'width:' + size + 'px;' +
                'height:' + size + 'px;' +
                'background:' + colors.bg + ';' +
                'border:3px solid white;' +
                'border-radius:50%;' +
                'box-shadow:0 3px 12px ' + colors.shadow + ';' +
                '">' + faviconHtml + '</div>';
        }

        // Fallback to number display if no churches data
        var countSize = count < 10 ? 14 : (count < 100 ? 12 : 11);
        return '<div class="stacked-marker-clean" style="' +
            'width:' + size + 'px;' +
            'height:' + size + 'px;' +
            'background:' + colors.bg + ';' +
            'border:3px solid white;' +
            'border-radius:50%;' +
            'display:flex;' +
            'align-items:center;' +
            'justify-content:center;' +
            'box-shadow:0 3px 12px ' + colors.shadow + ';' +
            'color:white;' +
            'font-weight:700;' +
            'font-family:Inter,-apple-system,sans-serif;' +
            '">' +
            '<span style="font-size:' + countSize + 'px;line-height:1;">' + count + '</span>' +
            '</div>';
    }

    /**
     * Create expanded marker icon (shown when stack is expanded)
     * Shows favicon/emoji with drop shadow for visibility
     * @param {Church} church - Church data
     * @returns {string} HTML string for expanded marker
     */
    function createExpandedIcon(church) {
        var iconConfig = getDioceseIconConfig();
        var fallbackEmoji = iconConfig.fallbackEmoji || '⛪';
        var iconHtml;

        if (church.website) {
            var domain = extractDomain(church.website);
            if (domain) {
                var localFavicon = '/assets/favicons/' + domain + '.png';
                var googleFavicon = getGoogleFaviconUrl(domain);
                iconHtml = '<img src="' + localFavicon + '" ' +
                    'alt="" style="width:24px;height:24px;border-radius:4px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.5));" ' +
                    'onerror="this.onerror=function(){this.outerHTML=\'<span style=font-size:20px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.5))>' + fallbackEmoji + '</span>\';};this.src=\'' + googleFavicon + '\';">';
            } else {
                iconHtml = '<span style="font-size:20px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.5));">' + fallbackEmoji + '</span>';
            }
        } else {
            iconHtml = '<span style="font-size:20px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.5));">' + fallbackEmoji + '</span>';
        }

        return '<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;">' + iconHtml + '</div>';
    }

    /**
     * Create popup favicon HTML
     * @param {Church} church - Church data
     * @returns {string} HTML string for popup favicon
     */
    function createPopupFavicon(church) {
        var domain = extractDomain(church.website);
        var defaultFavicon = window.MapUtils ? window.MapUtils.getDefaultFavicon() : '/favicon.svg';

        if (domain) {
            var localFavicon = '/assets/favicons/' + domain + '.png';
            var googleFavicon = getGoogleFaviconUrl(domain, 64);

            return '<img src="' + localFavicon + '" class="popup-favicon" alt="" ' +
                'onerror="this.onerror=function(){this.onerror=function(){this.style.display=\'none\';};this.src=\'' + defaultFavicon + '\';};this.src=\'' + googleFavicon + '\';">';
        }

        return '<img src="' + defaultFavicon + '" class="popup-favicon" alt="" ' +
            'onerror="this.style.display=\'none\';this.parentElement.insertAdjacentHTML(\'afterbegin\',\'<span class=popup-emoji>⛪</span>\');">';
    }

    /**
     * Create favicon grid for cluster display
     * Uses 2x2 grid for 4 or fewer, 3x3 for more
     * @param {Array} churches - Array of church objects
     * @param {number} clusterSize - Size of the cluster container in pixels
     * @returns {string} HTML string for favicon grid
     */
    function createClusterFaviconGrid(churches, clusterSize) {
        if (!churches || churches.length === 0) return '';

        var maxIcons = Math.min(churches.length, 9);
        var gridHtml = '';

        // Use 2x2 grid for small clusters, 3x3 for larger
        var gridSize = maxIcons <= 4 ? 2 : 3;
        var iconSize = Math.max(14, Math.floor(clusterSize / (gridSize + 1.5)));

        // Grid positions based on grid size
        var gridPositions = gridSize === 2
            ? [{row: 0, col: 0}, {row: 0, col: 1}, {row: 1, col: 0}, {row: 1, col: 1}]
            : [
                {row: 0, col: 0}, {row: 0, col: 1}, {row: 0, col: 2},
                {row: 1, col: 0}, {row: 1, col: 1}, {row: 1, col: 2},
                {row: 2, col: 0}, {row: 2, col: 1}, {row: 2, col: 2}
            ];

        // Calculate grid layout
        var padding = 6;
        var availableSpace = clusterSize - (padding * 2);
        var cellSize = availableSpace / gridSize;

        // Vertically center if not filling all rows
        var actualRows = Math.ceil(maxIcons / gridSize);
        var verticalOffset = (gridSize - actualRows) * cellSize / 2;

        for (var i = 0; i < maxIcons; i++) {
            var church = churches[i];
            var pos = gridPositions[i];

            // Calculate position - centered in each cell
            var x = padding + (pos.col * cellSize) + (cellSize / 2);
            var y = padding + verticalOffset + (pos.row * cellSize) + (cellSize / 2);

            // Get website domain
            var websiteUrl = church.website || church.url || church.web || '';
            var domain = websiteUrl ? extractDomain(websiteUrl) : null;

            if (domain) {
                var faviconUrl = getGoogleFaviconUrl(domain, 32);
                gridHtml += '<img src="' + faviconUrl + '" ' +
                    'alt="" ' +
                    'style="position:absolute;' +
                    'width:' + iconSize + 'px;' +
                    'height:' + iconSize + 'px;' +
                    'left:' + x + 'px;' +
                    'top:' + y + 'px;' +
                    'transform:translate(-50%,-50%);' +
                    'background:white;' +
                    'padding:1px;' +
                    'border-radius:4px;' +
                    'box-shadow:0 1px 4px rgba(0,0,0,0.4);" ' +
                    'onerror="this.outerHTML=\'<span style=position:absolute;font-size:' + (iconSize - 2) + 'px;left:' + x + 'px;top:' + y + 'px;transform:translate(-50%,-50%);filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.6))>⛪</span>\';" ' +
                    'loading="lazy" />';
            } else {
                gridHtml += '<span style="position:absolute;' +
                    'font-size:' + (iconSize - 2) + 'px;' +
                    'left:' + x + 'px;' +
                    'top:' + y + 'px;' +
                    'transform:translate(-50%,-50%);' +
                    'filter:drop-shadow(0 1px 3px rgba(0,0,0,0.6));">⛪</span>';
            }
        }

        return gridHtml;
    }

    // Public API
    window.MapMarkerIcons = {
        extractDomain: extractDomain,
        getGoogleFaviconUrl: getGoogleFaviconUrl,
        getDioceseIconConfig: getDioceseIconConfig,
        getClusterColors: getClusterColors,
        createMarkerIcon: createMarkerIcon,
        createStackedIcon: createStackedIcon,
        createExpandedIcon: createExpandedIcon,
        createPopupFavicon: createPopupFavicon,
        createClusterFaviconGrid: createClusterFaviconGrid
    };

})(window);


// === favicon-cache.js ===
/**
 * Favicon Cache System
 * Manages favicon URL caching for performance optimization
 */
(function(window) {
    'use strict';

    var CACHE_KEY = 'favicon_cache_v1';
    var CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
    var memoryCache = {};
    var cacheInitialized = false;

    /**
     * Initialize cache from localStorage
     */
    function initCache() {
        if (cacheInitialized) return;

        try {
            var stored = localStorage.getItem(CACHE_KEY);
            if (stored) {
                var data = JSON.parse(stored);
                var now = Date.now();

                // Clean expired entries
                Object.keys(data).forEach(function(domain) {
                    if (data[domain].expires > now) {
                        memoryCache[domain] = data[domain].url;
                    }
                });
            }
        } catch (e) {
            console.warn('Failed to load favicon cache:', e);
        }

        cacheInitialized = true;
    }

    /**
     * Get cached favicon URL
     * @param {string} domain - Domain to lookup
     * @returns {string|null} Cached URL or null
     */
    function get(domain) {
        initCache();
        return memoryCache[domain] || null;
    }

    /**
     * Store favicon URL in cache
     * @param {string} domain - Domain to cache
     * @param {string} url - Favicon URL
     */
    function set(domain, url) {
        initCache();
        memoryCache[domain] = url;

        // Persist to localStorage periodically
        scheduleSync();
    }

    var syncTimeout;
    function scheduleSync() {
        if (syncTimeout) return;

        syncTimeout = setTimeout(function() {
            syncTimeout = null;
            syncToStorage();
        }, 1000);
    }

    /**
     * Sync memory cache to localStorage
     */
    function syncToStorage() {
        try {
            var data = {};
            var expires = Date.now() + CACHE_TTL;

            Object.keys(memoryCache).forEach(function(domain) {
                data[domain] = {
                    url: memoryCache[domain],
                    expires: expires
                };
            });

            localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        } catch (e) {
            console.warn('Failed to save favicon cache:', e);
        }
    }

    /**
     * Preload favicons for a set of churches
     * @param {Array} churches - Array of church objects
     */
    function preload(churches) {
        if (!churches || churches.length === 0) return;

        churches.slice(0, 20).forEach(function(church) {
            var domain = window.MapMarkerIcons ?
                window.MapMarkerIcons.extractDomain(church.website || church.url) : null;

            if (domain && !memoryCache[domain]) {
                // Create invisible image to trigger browser caching
                var img = new Image();
                img.src = window.MapMarkerIcons.getGoogleFaviconUrl(domain, 16);

                // Store in our cache when loaded
                img.onload = function() {
                    set(domain, img.src);
                };
            }
        });
    }

    /**
     * Clear the favicon cache
     */
    function clear() {
        memoryCache = {};
        try {
            localStorage.removeItem(CACHE_KEY);
        } catch (e) {
            // Ignore
        }
    }

    // Public API
    window.FaviconCache = {
        get: get,
        set: set,
        preload: preload,
        clear: clear,
        syncToStorage: syncToStorage
    };

    // Initialize on load
    initCache();

    // Sync before page unload
    window.addEventListener('beforeunload', syncToStorage);

})(window);

// === marker-popups.js ===
/**
 * MapMarkerPopups - Popup content generation and marker events
 *
 * @fileoverview Creates popup HTML content and sets up marker event handlers.
 * @depends MapConstants, MapState, MapUtils, MapMarkerIcons, MapLiturgy
 */
(function(window) {
    'use strict';

    var C = window.MapConstants;
    var State = window.MapState;
    var Utils = window.MapUtils;
    var Icons = window.MapMarkerIcons;

    /**
     * Create popup content for a church marker
     * @param {Church} church - Church data
     * @returns {string} HTML string for popup content
     */
    function createPopupContent(church) {
        if (!church) {
            State.log('createPopupContent called with null church');
            return '<div class="popup-error">Church data unavailable</div>';
        }

        var websiteUrl = Utils.buildWebsiteUrl(church.website);
        var websiteDisplay = church.website || '';
        if (websiteDisplay) {
            websiteDisplay = websiteDisplay.replace(/^https?:\/\//, '').replace(/\/$/, '');
        }

        // Get favicon for popup
        var popupFaviconHtml = Icons.createPopupFavicon(church);

        // Generate slug for detail page
        var slug = Utils.generateSlug(church.name, church.city);
        var churchUrl = Utils.buildLocalizedUrl('/church/' + slug);

        // Translate church name
        var currentLang = Utils.getCurrentLanguage();
        var displayName = Utils.translateChurchName(church.name, currentLang);

        // Pre-render liturgy from church data
        var liturgyHtml = renderInlineLiturgy(church);

        return '<div class="popup-content">' +
            '<div class="popup-header">' +
                popupFaviconHtml +
                '<div class="popup-title">' + Utils.escapeHtml(displayName) + '</div>' +
            '</div>' +
            '<div class="popup-location">📍 ' +
                Utils.escapeHtml(church.city) + (church.state ? ', ' + Utils.escapeHtml(church.state) : '') +
            '</div>' +
            (websiteUrl ?
                '<div class="popup-website">' +
                    '<a href="' + Utils.escapeHtml(websiteUrl) + '" target="_blank" rel="noopener noreferrer">🌐 ' +
                        Utils.escapeHtml(websiteDisplay) +
                    '</a>' +
                '</div>' : '') +
            '<div class="popup-liturgy">' + liturgyHtml + '</div>' +
            '<a href="' + churchUrl + '" class="popup-details-btn">' +
                Utils.translate('mapPopupViewDetails', 'View Details →') +
            '</a>' +
        '</div>';
    }

    /**
     * Render liturgy data inline (no loading state)
     * @param {Church} church - Church data
     * @returns {string} HTML string for liturgy section
     */
    function renderInlineLiturgy(church) {
        var isAnglican = State.isAnglican && State.isAnglican();

        // Check if we have mass times data
        if (!church.mass_times && !church.mass_times_json) {
            var visitKey = isAnglican ? 'mapVisitWebsiteForServiceTimes' : 'mapVisitWebsiteForMassTimes';
            var visitFallback = isAnglican ? 'Visit website for service times' : 'Visit website for Mass times';
            return '<div class="popup-no-times">' + Utils.translate(visitKey, visitFallback) + '</div>';
        }

        // Parse mass times
        var masses = parseMassTimes(church);

        if (masses.length === 0) {
            var visitKey = isAnglican ? 'mapVisitWebsiteForServiceTimes' : 'mapVisitWebsiteForMassTimes';
            var visitFallback = isAnglican ? 'Visit website for service times' : 'Visit website for Mass times';
            return '<div class="popup-no-times">' + Utils.translate(visitKey, visitFallback) + '</div>';
        }

        // Group by day
        var dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        var today = dayNames[new Date().getDay()];
        var typeToDay = { 'vigil': 'Saturday', 'sunday': 'Sunday', 'weekday': 'Weekday' };

        var byDay = {};
        masses.forEach(function(m) {
            var day = m.day || typeToDay[m.type] || 'Other';
            if (!byDay[day]) byDay[day] = [];
            byDay[day].push(m.time);
        });

        // Build HTML
        var dayOrder = {
            'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
            'Thursday': 4, 'Friday': 5, 'Saturday': 6, 'Weekday': 7
        };

        var sortedDays = Object.keys(byDay).sort(function(a, b) {
            return (dayOrder[a] || 99) - (dayOrder[b] || 99);
        });

        var html = '<div class="popup-mass-times">';

        sortedDays.forEach(function(day) {
            var times = byDay[day].filter(function(t, i, arr) {
                return arr.indexOf(t) === i; // Dedupe
            }).join(', ');

            var isToday = day === today ||
                (day === 'Weekday' && ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].indexOf(today) !== -1);

            var dayClass = isToday ? 'mass-day mass-day-today' : 'mass-day';
            var translatedDay = Utils.translate('day' + day, day);

            html += '<div class="' + dayClass + '">' +
                '<span class="mass-day-label">' + translatedDay + ':</span> ' +
                '<span class="mass-day-times">' + times + '</span>' +
            '</div>';
        });

        html += '</div>';
        return html;
    }

    /**
     * Parse mass times from church data (JSON or text format)
     * @param {Church} church - Church data
     * @returns {MassTime[]} Array of mass time objects
     */
    function parseMassTimes(church) {
        var masses = [];

        // Try JSON format first
        if (church.mass_times_json) {
            try {
                masses = typeof church.mass_times_json === 'string'
                    ? JSON.parse(church.mass_times_json)
                    : church.mass_times_json;

                if (!Array.isArray(masses)) {
                    masses = [];
                }
            } catch (e) {
                State.log('Error parsing mass_times_json for', church.name, e);
                masses = [];
            }
        }

        // Fallback: parse text format
        if (masses.length === 0 && church.mass_times && church.mass_times.indexOf('|') !== -1) {
            masses = parseDescriptionToMasses(church.mass_times);
        }

        return masses;
    }

    /**
     * Parse description text to mass objects
     * @param {string} description - Pipe-delimited mass times string
     * @returns {MassTime[]} Array of mass time objects
     */
    function parseDescriptionToMasses(description) {
        var masses = [];
        if (!description) return masses;

        var parts = description.split('|').map(function(p) { return p.trim(); });

        parts.forEach(function(part) {
            // Skip non-mass entries
            if (part.toLowerCase().indexOf('est.') === 0) return;
            if (part.toLowerCase().indexOf('confession') !== -1) return;
            if (part.toLowerCase().indexOf('adoration') !== -1) return;

            var match = part.match(/^(Sat(?:urday)?\s*Vigil|Sun(?:day)?|Weekday|Daily|Mon|Tue|Wed|Thu|Fri|Sat):\s*(.+)$/i);
            if (!match) return;

            var dayType = match[1].toLowerCase();
            var timesStr = match[2];

            var type = 'weekday';
            var day = null;

            if (dayType.indexOf('vigil') !== -1) {
                type = 'vigil';
                day = 'Saturday';
            } else if (dayType.indexOf('sun') === 0) {
                type = 'sunday';
                day = 'Sunday';
            } else if (dayType === 'sat') {
                type = 'vigil';
                day = 'Saturday';
            } else if (dayType === 'mon') {
                day = 'Monday';
            } else if (dayType === 'tue') {
                day = 'Tuesday';
            } else if (dayType === 'wed') {
                day = 'Wednesday';
            } else if (dayType === 'thu') {
                day = 'Thursday';
            } else if (dayType === 'fri') {
                day = 'Friday';
            }

            var timeParts = timesStr.split(',');
            timeParts.forEach(function(timePart) {
                var timeMatch = timePart.trim().match(/^(\d{1,2}:\d{2}\s*(?:AM|PM)?)/i);
                if (timeMatch) {
                    var time = timeMatch[1].trim();
                    // Normalize AM/PM formatting
                    time = time.replace(/\s*(am|pm)/i, function(m) {
                        return ' ' + m.trim().toUpperCase();
                    });
                    masses.push({ time: time, type: type, day: day });
                }
            });
        });

        return masses;
    }

    /**
     * Set up marker event handlers
     * @param {L.Marker} marker - Leaflet marker
     * @param {Church} church - Church data
     */
    function setupMarkerEvents(marker, church) {
        if (!marker || !church) return;

        // Click to select
        marker.on('click', function(e) {
            if (e.originalEvent) {
                e.originalEvent.stopPropagation();
            }

            if (window.MapSelection && window.MapSelection.selectItem) {
                window.MapSelection.selectItem(church.name, church.city);
            }

            // Analytics
            if (window.trackEvent) {
                window.trackEvent('map_marker_click', {
                    church_name: church.name,
                    church_city: church.city,
                    source: 'marker'
                });
            }
        });

        // Mouseover to update tooltip with next mass
        marker.on('mouseover', function() {
            updateMarkerTooltip(marker, church);
        });
    }

    /**
     * Update marker tooltip with next mass time
     * @param {L.Marker} marker - Leaflet marker
     * @param {Church} church - Church data
     */
    function updateMarkerTooltip(marker, church) {
        if (!marker || !church) return;

        var currentLang = Utils.getCurrentLanguage();
        var displayName = Utils.translateChurchName(church.name, currentLang);
        var tooltipHtml = '<strong>' + Utils.escapeHtml(displayName) + '</strong><br>';

        // Get next mass from liturgy module if available
        var nextMass = null;
        if (window.MapLiturgy && window.MapLiturgy.getNextMassFromChurch) {
            nextMass = window.MapLiturgy.getNextMassFromChurch(church);
        }

        if (nextMass && nextMass.display) {
            var colorClass = nextMass.isToday ? 'tooltip-today' : (nextMass.isTomorrow ? 'tooltip-tomorrow' : '');
            var nextLabel = Utils.translate('mapNextMass', 'Next');
            tooltipHtml += '<span class="tooltip-next-mass ' + colorClass + '">⛪ ' + nextLabel + ': ' +
                Utils.escapeHtml(nextMass.display) + '</span>';
        } else {
            tooltipHtml += '<span class="tooltip-no-times">' +
                Utils.translate('mapVisitWebsite', 'Visit website for times') + '</span>';
        }

        marker.setTooltipContent(tooltipHtml);
    }

    /**
     * Create initial tooltip content (before hover)
     * @param {Church} church - Church data
     * @returns {string} HTML string for tooltip
     */
    function createInitialTooltip(church) {
        var currentLang = Utils.getCurrentLanguage();
        var displayName = Utils.translateChurchName(church.name, currentLang);

        return '<strong>' + Utils.escapeHtml(displayName) + '</strong><br>' +
            '<span class="tooltip-loading">' + Utils.translate('loading', 'Loading...') + '</span>';
    }

    // Public API
    window.MapMarkerPopups = {
        createPopupContent: createPopupContent,
        renderInlineLiturgy: renderInlineLiturgy,
        parseMassTimes: parseMassTimes,
        parseDescriptionToMasses: parseDescriptionToMasses,
        setupMarkerEvents: setupMarkerEvents,
        updateMarkerTooltip: updateMarkerTooltip,
        createInitialTooltip: createInitialTooltip
    };

})(window);


// === hierarchical-clusters.js ===
/**
 * Hierarchical Clustering for Churches
 * Groups by Country -> Diocese -> Churches with flag emojis and size-based colors
 */

(function(window) {
    'use strict';

    // Country flag emojis
    var COUNTRY_FLAGS = {
        'US': '🇺🇸',
        'CA': '🇨🇦',
        'MX': '🇲🇽',
        'GB': '🇬🇧',
        'IE': '🇮🇪',
        'AU': '🇦🇺',
        'NZ': '🇳🇿',
        'PH': '🇵🇭',
        'IN': '🇮🇳',
        'IT': '🇮🇹',
        'FR': '🇫🇷',
        'ES': '🇪🇸',
        'PT': '🇵🇹',
        'DE': '🇩🇪',
        'PL': '🇵🇱',
        'BR': '🇧🇷',
        'AR': '🇦🇷',
        'CO': '🇨🇴',
        'PE': '🇵🇪',
        'CL': '🇨🇱'
    };

    // Size-based color coding for clusters
    var CLUSTER_COLORS = {
        XL: '#dc2626',   // 1000+ churches - Red
        L: '#ea580c',    // 500-999 churches - Orange
        M: '#ca8a04',    // 100-499 churches - Amber
        S: '#65a30d',    // 50-99 churches - Lime
        XS: '#16a34a',   // 10-49 churches - Green
        XXS: '#0891b2'   // <10 churches - Cyan
    };

    /**
     * Get cluster size category
     */
    function getClusterSize(count) {
        if (count >= 1000) return 'XL';
        if (count >= 500) return 'L';
        if (count >= 100) return 'M';
        if (count >= 50) return 'S';
        if (count >= 10) return 'XS';
        return 'XXS';
    }

    /**
     * Get color for cluster based on size
     */
    function getClusterColor(count) {
        return CLUSTER_COLORS[getClusterSize(count)];
    }

    /**
     * Create custom cluster icon with flag and count
     */
    function createClusterIcon(country, count, isCountryLevel) {
        var flag = COUNTRY_FLAGS[country] || '⛪';
        var color = getClusterColor(count);
        var size = Math.min(40 + Math.log(count) * 5, 80); // Dynamic size based on count

        if (isCountryLevel) {
            // Country-level cluster with flag
            return L.divIcon({
                html: '<div class="hierarchical-cluster country-cluster" style="background-color: ' + color + '; width: ' + size + 'px; height: ' + size + 'px;">' +
                      '<div class="cluster-flag">' + flag + '</div>' +
                      '<div class="cluster-count">' + count.toLocaleString() + '</div>' +
                      '</div>',
                className: 'leaflet-hierarchical-cluster',
                iconSize: [size, size],
                iconAnchor: [size/2, size/2]
            });
        } else {
            // Diocese-level cluster
            return L.divIcon({
                html: '<div class="hierarchical-cluster diocese-cluster" style="background-color: ' + color + '; width: ' + size + 'px; height: ' + size + 'px;">' +
                      '<div class="cluster-emoji">⛪</div>' +
                      '<div class="cluster-count">' + count.toLocaleString() + '</div>' +
                      '</div>',
                className: 'leaflet-hierarchical-cluster',
                iconSize: [size, size],
                iconAnchor: [size/2, size/2]
            });
        }
    }

    /**
     * Create a geographic grid cell key for clustering
     * Uses ~100km grid cells to group churches by location
     */
    function getGridCell(lat, lon, cellSize) {
        cellSize = cellSize || 1; // ~100km at equator
        var latCell = Math.floor(lat / cellSize);
        var lonCell = Math.floor(lon / cellSize);
        return latCell + ',' + lonCell;
    }

    /**
     * Get most common diocese name from a group of churches
     */
    function getMostCommonDiocese(churches) {
        var counts = {};
        churches.forEach(function(c) {
            var d = c.diocese_id || 'unknown';
            counts[d] = (counts[d] || 0) + 1;
        });
        var best = 'unknown';
        var bestCount = 0;
        Object.keys(counts).forEach(function(d) {
            if (counts[d] > bestCount) {
                bestCount = counts[d];
                best = d;
            }
        });
        return best;
    }

    /**
     * Group churches hierarchically using geographic grid clustering
     * This prevents churches with incorrect diocese_id from pulling clusters off-location
     */
    function groupChurchesHierarchically(churches) {
        var hierarchy = {};

        // First pass: group by country and geographic grid cell
        var countryGrids = {};

        churches.forEach(function(church) {
            var country = church.country || 'US';

            // Normalize coordinates - API returns latitude/longitude, not lat/lon
            church.lat = church.latitude || church.lat;
            church.lon = church.longitude || church.lon;

            // Skip churches without valid coordinates
            if (!church.lat || !church.lon || isNaN(church.lat) || isNaN(church.lon)) {
                return;
            }

            if (!countryGrids[country]) {
                countryGrids[country] = {};
            }

            // Use grid cell for geographic clustering
            var cellKey = getGridCell(church.lat, church.lon, 1.5); // ~150km cells
            if (!countryGrids[country][cellKey]) {
                countryGrids[country][cellKey] = [];
            }
            countryGrids[country][cellKey].push(church);
        });

        // Second pass: convert grid cells to diocese clusters
        Object.keys(countryGrids).forEach(function(country) {
            hierarchy[country] = {
                count: 0,
                dioceses: {},
                churches: [],
                bounds: {
                    minLat: 90, maxLat: -90,
                    minLon: 180, maxLon: -180
                }
            };

            var grids = countryGrids[country];
            Object.keys(grids).forEach(function(cellKey) {
                var cellChurches = grids[cellKey];
                // Get diocese name from majority of churches in this cell
                var dioceseName = getMostCommonDiocese(cellChurches);
                // Create unique key combining diocese and location
                var clusterKey = dioceseName + '@' + cellKey;

                if (!hierarchy[country].dioceses[clusterKey]) {
                    hierarchy[country].dioceses[clusterKey] = {
                        count: 0,
                        churches: [],
                        displayName: dioceseName,
                        bounds: {
                            minLat: 90, maxLat: -90,
                            minLon: 180, maxLon: -180
                        }
                    };
                }

                cellChurches.forEach(function(church) {
                    hierarchy[country].count++;
                    hierarchy[country].churches.push(church);
                    hierarchy[country].dioceses[clusterKey].count++;
                    hierarchy[country].dioceses[clusterKey].churches.push(church);

                    updateBounds(hierarchy[country].bounds, church);
                    updateBounds(hierarchy[country].dioceses[clusterKey].bounds, church);
                });
            });
        });

        return hierarchy;
    }

    /**
     * Update bounds with church location
     */
    function updateBounds(bounds, church) {
        if (church.lat < bounds.minLat) bounds.minLat = church.lat;
        if (church.lat > bounds.maxLat) bounds.maxLat = church.lat;
        if (church.lon < bounds.minLon) bounds.minLon = church.lon;
        if (church.lon > bounds.maxLon) bounds.maxLon = church.lon;
    }

    /**
     * Get center of bounds
     */
    function getBoundsCenter(bounds) {
        return {
            lat: (bounds.minLat + bounds.maxLat) / 2,
            lon: (bounds.minLon + bounds.maxLon) / 2
        };
    }

    /**
     * Create markers for hierarchical view
     */
    function createHierarchicalMarkers(hierarchy, zoom) {
        var markers = [];

        Object.keys(hierarchy).forEach(function(country) {
            var countryData = hierarchy[country];

            if (zoom < 5) {
                // Show country-level clusters
                var center = getBoundsCenter(countryData.bounds);
                var marker = L.marker([center.lat, center.lon], {
                    icon: createClusterIcon(country, countryData.count, true),
                    country: country,
                    isCountryCluster: true,
                    churchCount: countryData.count
                });

                marker.bindTooltip(
                    '<strong>' + COUNTRY_FLAGS[country] + ' ' + country + '</strong><br>' +
                    countryData.count.toLocaleString() + ' churches<br>' +
                    Object.keys(countryData.dioceses).length + ' dioceses',
                    { permanent: false, direction: 'top' }
                );

                markers.push(marker);
            } else {
                // Show diocese-level clusters
                Object.keys(countryData.dioceses).forEach(function(clusterKey) {
                    var dioceseData = countryData.dioceses[clusterKey];
                    var center = getBoundsCenter(dioceseData.bounds);
                    // Use displayName (actual diocese) or fallback to parsing the key
                    var displayName = dioceseData.displayName || clusterKey.split('@')[0];

                    var marker = L.marker([center.lat, center.lon], {
                        icon: createClusterIcon(country, dioceseData.count, false),
                        country: country,
                        diocese: displayName,
                        isDioceseCluster: true,
                        churchCount: dioceseData.count
                    });

                    marker.bindTooltip(
                        '<strong>' + displayName.replace(/_/g, ' ').replace(/\b\w/g, function(l) { return l.toUpperCase(); }) + '</strong><br>' +
                        COUNTRY_FLAGS[country] + ' ' + country + '<br>' +
                        dioceseData.count.toLocaleString() + ' churches',
                        { permanent: false, direction: 'top' }
                    );

                    markers.push(marker);
                });
            }
        });

        return markers;
    }

    /**
     * Create legend for cluster sizes
     */
    function createClusterLegend() {
        var legend = L.control({ position: 'bottomright' });

        legend.onAdd = function(map) {
            var div = L.DomUtil.create('div', 'cluster-legend');
            div.innerHTML = '<h4>Church Clusters</h4>';

            var sizes = [
                { label: '1000+ churches', color: CLUSTER_COLORS.XL, size: 'XL' },
                { label: '500-999 churches', color: CLUSTER_COLORS.L, size: 'L' },
                { label: '100-499 churches', color: CLUSTER_COLORS.M, size: 'M' },
                { label: '50-99 churches', color: CLUSTER_COLORS.S, size: 'S' },
                { label: '10-49 churches', color: CLUSTER_COLORS.XS, size: 'XS' },
                { label: '<10 churches', color: CLUSTER_COLORS.XXS, size: 'XXS' }
            ];

            sizes.forEach(function(item) {
                div.innerHTML +=
                    '<div class="legend-item">' +
                    '<span class="legend-color" style="background-color: ' + item.color + '"></span>' +
                    '<span class="legend-label">' + item.label + '</span>' +
                    '</div>';
            });

            return div;
        };

        return legend;
    }

    /**
     * Initialize hierarchical clustering
     */
    function init(map, churches) {
        // Group churches hierarchically
        var hierarchy = groupChurchesHierarchically(churches);

        // Create layer group for hierarchical clusters
        var clusterLayer = L.layerGroup();

        // Update clusters based on zoom
        function updateClusters() {
            var zoom = map.getZoom();
            clusterLayer.clearLayers();

            var markers = createHierarchicalMarkers(hierarchy, zoom);
            markers.forEach(function(marker) {
                clusterLayer.addLayer(marker);
            });
        }

        // Initial render
        updateClusters();

        // Update on zoom
        map.on('zoomend', updateClusters);

        // Add to map
        clusterLayer.addTo(map);

        // Add legend
        var legend = createClusterLegend();
        legend.addTo(map);

        return {
            layer: clusterLayer,
            hierarchy: hierarchy,
            update: updateClusters
        };
    }

    // Public API
    window.HierarchicalClusters = {
        init: init,
        createClusterIcon: createClusterIcon,
        getClusterColor: getClusterColor,
        getClusterSize: getClusterSize,
        COUNTRY_FLAGS: COUNTRY_FLAGS,
        CLUSTER_COLORS: CLUSTER_COLORS
    };

})(window);

// === markers.js ===
/**
 * MapMarkers - Core marker layer management
 *
 * @fileoverview Manages marker creation, layer visibility, and stacked markers.
 * @depends MapConstants, MapState, MapMarkerIcons, MapMarkerPopups
 */
(function(window) {
    'use strict';

    var C = window.MapConstants;
    var State = window.MapState;
    var Icons = window.MapMarkerIcons;
    var Popups = window.MapMarkerPopups;
    var log = State.log;

    /**
     * Group churches by location for stacked marker handling
     * @param {Church[]} churches - Array of churches
     * @param {number} [precision=4] - Decimal precision for grouping
     * @returns {Object.<string, Church[]>} Map of location key to churches
     */
    function groupByLocation(churches, precision) {
        precision = precision || C.LOCATION_PRECISION;
        var groups = {};

        churches.forEach(function(church) {
            if (typeof church.lat !== 'number' || typeof church.lon !== 'number') {
                log('Skipping church with invalid coordinates:', church.name);
                return;
            }
            var key = church.lat.toFixed(precision) + ',' + church.lon.toFixed(precision);
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(church);
        });

        return groups;
    }

    /**
     * Create stacked marker for multiple churches at same location
     * @param {Church[]} churches - Churches at this location
     * @param {number} lat - Latitude
     * @param {number} lon - Longitude
     * @returns {L.Marker} Leaflet marker with stack behavior
     */
    function createStackedMarker(churches, lat, lon) {
        var count = churches.length;
        var iconHtml = Icons.createStackedIcon(count, churches);

        var size = count < C.CLUSTER_THRESHOLD_MEDIUM ? C.CLUSTER_SIZE_SMALL :
                   count < C.CLUSTER_THRESHOLD_LARGE ? C.CLUSTER_SIZE_MEDIUM : C.CLUSTER_SIZE_LARGE;

        var stackIcon = L.divIcon({
            className: 'clean-stack-marker',
            html: iconHtml,
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2]
        });

        var marker = L.marker([lat, lon], { icon: stackIcon });
        marker._stackedChurches = churches;
        marker._isExpanded = false;
        marker._expandedMarkers = [];

        marker.on('click', function(e) {
            L.DomEvent.stopPropagation(e);
            if (marker._isExpanded) {
                collapseStack(marker);
            } else {
                expandStack(marker);
            }
        });

        // Tooltip showing church names
        var Utils = window.MapUtils;
        var tooltipContent = '<strong>' + count + ' Churches</strong><br>' +
            churches.slice(0, 5).map(function(c) { return Utils.escapeHtml(c.name); }).join('<br>') +
            (count > 5 ? '<br><em>...and ' + (count - 5) + ' more</em>' : '');
        marker.bindTooltip(tooltipContent, { direction: 'top', offset: [0, -10] });

        return marker;
    }

    /**
     * Expand stacked marker into circular fan
     * @param {L.Marker} stackMarker - The stacked marker to expand
     */
    function expandStack(stackMarker) {
        var map = State.get('map');
        var markersLogoLayer = State.get('markersLogoLayer');
        var churches = stackMarker._stackedChurches;
        var center = stackMarker.getLatLng();
        var Utils = window.MapUtils;

        var zoom = map.getZoom();
        var radiusMeters = 50 * Math.pow(2, 15 - zoom);
        var maxMarkers = Math.min(churches.length, C.MAX_EXPANDED_MARKERS);
        var angleStep = (2 * Math.PI) / maxMarkers;

        churches.slice(0, maxMarkers).forEach(function(church, i) {
            var angle = i * angleStep - Math.PI / 2;
            var offsetLat = (radiusMeters / 111000) * Math.cos(angle);
            var offsetLon = (radiusMeters / (111000 * Math.cos(center.lat * Math.PI / 180))) * Math.sin(angle);

            var iconHtml = Icons.createExpandedIcon(church);
            var expandedIcon = L.divIcon({
                className: 'expanded-marker-container',
                html: iconHtml,
                iconSize: [28, 28],
                iconAnchor: [14, 14]
            });

            var expandedMarker = L.marker([center.lat + offsetLat, center.lng + offsetLon], { icon: expandedIcon });
            expandedMarker._churchData = church;

            expandedMarker.on('click', function() {
                if (window.MapSelection && window.MapSelection.selectItem) {
                    window.MapSelection.selectItem(church.name, church.city);
                }
            });

            var currentLang = Utils.getCurrentLanguage();
            var displayName = Utils.translateChurchName(church.name, currentLang);
            expandedMarker.bindTooltip(Utils.escapeHtml(displayName), { direction: 'top', offset: [0, -8] });

            markersLogoLayer.addLayer(expandedMarker);
            stackMarker._expandedMarkers.push(expandedMarker);
        });

        // Show "more" indicator if needed
        if (churches.length > C.MAX_EXPANDED_MARKERS) {
            var moreIcon = L.divIcon({
                className: 'more-indicator',
                html: '<div class="more-count">+' + (churches.length - C.MAX_EXPANDED_MARKERS) + '</div>',
                iconSize: [24, 24],
                iconAnchor: [12, 12]
            });
            var moreMarker = L.marker([center.lat, center.lng], { icon: moreIcon });
            markersLogoLayer.addLayer(moreMarker);
            stackMarker._expandedMarkers.push(moreMarker);
        }

        stackMarker._isExpanded = true;
        stackMarker.setOpacity(0.3);
        log('Expanded stack of', churches.length, 'churches');
    }

    /**
     * Collapse expanded stack
     * @param {L.Marker} stackMarker - The stacked marker to collapse
     */
    function collapseStack(stackMarker) {
        var markersLogoLayer = State.get('markersLogoLayer');

        stackMarker._expandedMarkers.forEach(function(m) {
            markersLogoLayer.removeLayer(m);
        });

        stackMarker._expandedMarkers = [];
        stackMarker._isExpanded = false;
        stackMarker.setOpacity(1);
        log('Collapsed stack');
    }

    /**
     * Create a marker for a single church
     * @param {Church} church - Church data
     * @returns {L.Marker|null} Leaflet marker or null if invalid data
     */
    function createChurchMarker(church) {
        if (!church || typeof church.lat !== 'number' || typeof church.lon !== 'number') {
            log('Invalid church data for marker:', church);
            return null;
        }

        var isMobile = State.isMobile();
        var iconSize = isMobile ? C.MARKER_SIZE_MOBILE : C.MARKER_SIZE_DESKTOP;
        var iconHtml = Icons.createMarkerIcon(church, null, iconSize);

        var faviconIcon = L.divIcon({
            className: 'clean-marker',
            html: iconHtml,
            iconSize: [iconSize, iconSize],
            iconAnchor: [iconSize / 2, iconSize / 2],
            popupAnchor: [0, -(iconSize / 2)]
        });

        var marker = L.marker([church.lat, church.lon], {
            icon: faviconIcon,
            church: church
        });
        marker._churchData = church;

        // Bind popup
        var popupContent = Popups.createPopupContent(church);
        marker.bindPopup(popupContent);

        // Bind tooltip
        var tooltipContent = Popups.createInitialTooltip(church);
        marker.bindTooltip(tooltipContent, {
            direction: 'top',
            offset: [0, -10],
            className: 'church-tooltip'
        });

        // Set up events
        Popups.setupMarkerEvents(marker, church);

        // Favicons are now loaded immediately in createMarkerIcon

        return marker;
    }

    /**
     * Add markers to map for filtered items.
     * All markers are added to the cluster layer which handles spatial
     * de-confliction, chunked loading, and viewport culling internally.
     */
    var _pendingRenderFrame = null;

    function addMarkersToMap() {
        // Cancel any pending render to avoid double-work
        if (_pendingRenderFrame) {
            cancelAnimationFrame(_pendingRenderFrame);
            _pendingRenderFrame = null;
        }

        var map = State.get('map');
        var markersClusterLayer = State.get('markersClusterLayer');
        var markersLogoLayer = State.get('markersLogoLayer');
        var allChurches = State.get('filteredItems') || [];

        if (!map || !markersClusterLayer || !markersLogoLayer) {
            log('Map or layers not initialized');
            return;
        }

        // Clear both layers
        markersClusterLayer.clearLayers();
        markersLogoLayer.clearLayers();

        // Filter out invalid coordinates and centroid-quality geocodes
        var validChurches = allChurches.filter(function(church) {
            if (!church.lat || !church.lon) return false;
            if (church.geocoding_confidence === 'centroid' || church.geocoding_confidence === 'city_centroid') return false;
            return true;
        });

        var itemMarkers = {};

        // Group churches by location to detect exact-coordinate overlaps
        var locationGroups = groupByLocation(validChurches);
        var groupKeys = Object.keys(locationGroups);

        // Build all markers, then bulk-add to cluster layer for best performance
        var allMarkers = [];

        for (var i = 0; i < groupKeys.length; i++) {
            var key = groupKeys[i];
            var group = locationGroups[key];

            if (group.length > 1) {
                var coords = key.split(',');
                var stackMarker = createStackedMarker(group, parseFloat(coords[0]), parseFloat(coords[1]));
                allMarkers.push(stackMarker);
                group.forEach(function(church) {
                    itemMarkers[church.name + '_' + church.city] = stackMarker;
                });
            } else {
                var church = group[0];
                var marker = createChurchMarker(church);
                if (marker) {
                    allMarkers.push(marker);
                    itemMarkers[church.name + '_' + church.city] = marker;
                }
            }
        }

        // Bulk-add is much faster than one-by-one for markerClusterGroup
        if (allMarkers.length > 0) {
            markersClusterLayer.addLayers(allMarkers);
        }

        State.set('itemMarkers', itemMarkers);
        updateMarkerLayerVisibility();
        log('Added', allMarkers.length, 'markers (' + validChurches.length + ' churches) to cluster layer');
    }

    /**
     * Update which marker layer is visible based on current view
     * All markers now go through markersClusterLayer for spatial de-confliction.
     * markersLogoLayer is only used for expanded stack fan-out markers.
     */
    function updateMarkerLayerVisibility() {
        var map = State.get('map');
        var markersClusterLayer = State.get('markersClusterLayer');
        var markersLogoLayer = State.get('markersLogoLayer');
        var currentView = State.get('currentView');

        if (!map || currentView !== 'markers') return;

        // Cluster layer is always primary for markers view
        if (!map.hasLayer(markersClusterLayer)) {
            map.addLayer(markersClusterLayer);
        }
        // Logo layer stays for expanded fan-out markers
        if (!map.hasLayer(markersLogoLayer)) {
            map.addLayer(markersLogoLayer);
        }
        State.set('markersLayer', markersClusterLayer);
    }

    /**
     * Toggle clustering mode
     * @param {boolean} enabled - Whether clustering should be enabled
     */
    function toggleClustering(enabled) {
        State.set('useClustering', enabled);

        // Update button states
        var clusterButtons = document.querySelectorAll('.cluster-toggle');
        clusterButtons.forEach(function(btn) {
            var isOn = btn.getAttribute('data-cluster') === 'on';
            btn.classList.toggle('active', isOn === enabled);
            btn.setAttribute('aria-pressed', String(isOn === enabled));
        });

        updateMarkerLayerVisibility();
        localStorage.setItem(C.STORAGE_USE_CLUSTERING, enabled.toString());
    }

    // Public API
    /**
     * Cleanup marker layers - removes markers from map
     * Called when switching to non-marker views
     */
    function cleanup() {
        var map = State.get('map');
        var markersClusterLayer = State.get('markersClusterLayer');
        var markersLogoLayer = State.get('markersLogoLayer');

        if (markersClusterLayer && map && map.hasLayer(markersClusterLayer)) {
            map.removeLayer(markersClusterLayer);
        }
        if (markersLogoLayer && map && map.hasLayer(markersLogoLayer)) {
            map.removeLayer(markersLogoLayer);
        }

        log('[Markers] Cleanup complete');
    }

    window.MapMarkers = {
        addMarkersToMap: addMarkersToMap,
        createChurchMarker: createChurchMarker,
        updateMarkerLayerVisibility: updateMarkerLayerVisibility,
        toggleClustering: toggleClustering,
        groupByLocation: groupByLocation,
        createStackedMarker: createStackedMarker,
        expandStack: expandStack,
        collapseStack: collapseStack,
        cleanup: cleanup
    };

    // Global alias for HTML handlers
    window.toggleClustering = toggleClustering;

})(window);


// === geolocation.js ===
/**
 * MapGeolocation - Geolocation and nearby church features
 *
 * @fileoverview Handles user location detection and nearby church sorting.
 * @depends MapConstants, MapState, MapUtils
 */
(function(window) {
    'use strict';

    var C = window.MapConstants;
    var State = window.MapState;
    var Utils = window.MapUtils;
    var log = State.log;

    function getTenantId() {
        if (window.DIOCESE_CONFIG && window.DIOCESE_CONFIG.slug) return window.DIOCESE_CONFIG.slug;
        if (window.SITE_CONFIG && window.SITE_CONFIG.id) return window.SITE_CONFIG.id;
        if (window.DIOCESE) return window.DIOCESE;
        return 'global';
    }

    /**
     * Geolocation error messages
     * @type {Object.<number, string>}
     */
    var GEO_ERROR_MESSAGES = {
        1: 'Location permission denied',
        2: 'Location unavailable',
        3: 'Location request timed out'
    };

    /**
     * Fetch approximate location from server (no prompt)
     * @returns {Promise<{lat:number, lon:number, accuracy:number}|null>}
     */
    function fetchApproxLocation() {
        return fetch('/api/geo', { credentials: 'same-origin' })
            .then(function(response) { return response.json(); })
            .then(function(data) {
                if (!data || !data.success) return null;
                var lat = parseFloat(data.lat);
                var lon = parseFloat(data.lng);
                if (isNaN(lat) || isNaN(lon)) return null;
                return { lat: lat, lon: lon, accuracy: 20000 };
            })
            .catch(function() { return null; });
    }

    /**
     * Check if coordinates are within diocese bounds
     * @param {number} lat - Latitude
     * @param {number} lon - Longitude
     * @param {Object} [bounds] - Optional bounds object with north, south, east, west
     * @returns {boolean} True if within bounds
     */
    function computeBoundsFromChurches(churches) {
        if (!churches || churches.length === 0) return null;

        var minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
        var found = false;

        churches.forEach(function(church) {
            var latVal = (church && typeof church.lat === 'number') ? church.lat : null;
            var lonVal = (church && typeof church.lon === 'number') ? church.lon : null;
            if (latVal == null || lonVal == null || isNaN(latVal) || isNaN(lonVal)) return;
            found = true;
            if (latVal < minLat) minLat = latVal;
            if (latVal > maxLat) maxLat = latVal;
            if (lonVal < minLon) minLon = lonVal;
            if (lonVal > maxLon) maxLon = lonVal;
        });

        if (!found) return null;
        return { north: maxLat, south: minLat, east: maxLon, west: minLon };
    }

    function isWithinDioceseBounds(lat, lon, bounds) {
        if (!bounds) {
            bounds = window.dioceseConfig && window.dioceseConfig.getDioceseBounds
                ? window.dioceseConfig.getDioceseBounds()
                : null;
        }

        // Fallback: infer from loaded data so dedicated tenants don't auto-center off-region.
        if (!bounds) {
            bounds = computeBoundsFromChurches(State.get('allChurches') || []);
        }

        if (!bounds) return false;

        // Leaflet LatLngBounds (computed from data or provided by caller)
        try {
            if (bounds && typeof bounds.contains === 'function') {
                return bounds.contains([lat, lon]);
            }
        } catch (e) {
            // Ignore and fall through to plain-object bounds checks
        }

        // Plain-object bounds: { north, south, east, west }
        if (typeof bounds.south === 'number' &&
            typeof bounds.north === 'number' &&
            typeof bounds.west === 'number' &&
            typeof bounds.east === 'number') {
            return lat >= bounds.south && lat <= bounds.north &&
                   lon >= bounds.west && lon <= bounds.east;
        }

        return false;
    }

    /**
     * Find nearby churches using geolocation
     * Updates UI and sorts churches by distance
     */
    function findNearbyItems() {
        var btn = document.getElementById('geolocationBtn');
        var map = State.get('map');
        var allChurches = State.get('allChurches') || [];

        // Check HTTPS requirement
        if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
            log('Geolocation requires HTTPS');
            showGeolocationError('Location requires secure connection (HTTPS)', btn);
            return;
        }

        // Check if geolocation is available
        if (!navigator.geolocation) {
            log('Geolocation not supported');
            showGeolocationError('Location not supported by browser', btn);
            return;
        }

        // Check if we have churches data
        if (allChurches.length === 0) {
            log('No churches loaded yet');
            showGeolocationError('Map data still loading...', btn);
            // Retry after a delay
            setTimeout(function() {
                var churches = State.get('allChurches') || [];
                if (churches.length > 0) {
                    findNearbyItems();
                }
            }, 1000);
            return;
        }

        updateButton(btn, '🔄', 'Finding your location...', true);

        navigator.geolocation.getCurrentPosition(
            function(position) {
                handleGeolocationSuccess(position, btn, map, allChurches);
            },
            function(error) {
                handleGeolocationError(error, btn);
                // Only use IP fallback for timeout/unavailable errors.
                // If permission was denied, don't silently substitute an
                // approximate location — it confuses the user.
                if (error.code !== 1) {
                    tryIPBasedGeolocation(btn, map, allChurches);
                }
            },
            {
                enableHighAccuracy: false,
                timeout: C.GEO_TIMEOUT_MS || 15000,
                maximumAge: C.GEO_MAX_AGE_MS || 300000
            }
        );
    }

    /**
     * Handle successful geolocation
     * @param {GeolocationPosition} position - Browser position object
     * @param {HTMLElement} btn - Button element to update
     * @param {L.Map} map - Leaflet map instance
     * @param {Church[]} allChurches - All churches array
     */
    function handleGeolocationSuccess(position, btn, map, allChurches) {
        var userLat = position.coords.latitude;
        var userLon = position.coords.longitude;
        var accuracy = position.coords.accuracy || 0;
        var tenant = getTenantId();

        log('User location:', userLat, userLon);

        // Check if user is within diocese bounds
        var isGlobalView = window.dioceseConfig && window.dioceseConfig.isGlobalView && window.dioceseConfig.isGlobalView();
        var userInBounds = isGlobalView || isWithinDioceseBounds(userLat, userLon);

        // Save location for future visits (global view only)
        if (isGlobalView) {
            try {
                localStorage.setItem(C.STORAGE_USER_LOCATION, JSON.stringify({
                    lat: userLat,
                    lon: userLon,
                    accuracy: accuracy,
                    tenant: tenant
                }));
            } catch (e) {
                log('Could not save user location:', e);
            }
        }
        try {
            localStorage.setItem('userLocation', JSON.stringify({
                lat: userLat,
                lng: userLon,
                accuracy: accuracy,
                tenant: tenant
            }));
            localStorage.setItem('userLocationTime', Date.now().toString());
        } catch (e) {
            // Ignore storage failures
        }

        // Calculate distances for all churches
        var churchesWithDistance = allChurches.map(function(church) {
            if (typeof church.lat !== 'number' || typeof church.lon !== 'number') {
                return Object.assign({}, church, { distance: Infinity });
            }
            var distance = Utils.haversineDistance(userLat, userLon, church.lat, church.lon);
            return Object.assign({}, church, { distance: distance });
        }).sort(function(a, b) {
            return a.distance - b.distance;
        });

        // Update state
        State.set('filteredItems', churchesWithDistance);
        State.set('userLocation', { lat: userLat, lon: userLon, accuracy: accuracy });

        // Always center on the user's actual location and show their marker.
        // Previous behavior hid the marker and recentered on diocese center when
        // the user was "outside bounds", which made the location appear wrong.
        var viewLat = userLat;
        var viewLon = userLon;
        var viewZoom = accuracy && accuracy >= 15000 ? 10 : 12;

        addUserLocationMarker(userLat, userLon);

        // Zoom to view location
        if (map) {
            map.setView([viewLat, viewLon], viewZoom);
        }

        // Update UI
        if (window.MapSidebar) {
            if (window.MapSidebar.displayItems) window.MapSidebar.displayItems();
            if (window.MapSidebar.updateStats) window.MapSidebar.updateStats();
        }
        // Re-render markers (skip if using viewport loading)
        var useViewportLoading = State.get('useViewportLoading');
        if (window.MapMarkers && window.MapMarkers.addMarkersToMap && !useViewportLoading) {
            window.MapMarkers.addMarkersToMap();
        }

        // Update button with nearby count
        var nearbyCount = churchesWithDistance.filter(function(c) {
            return c.distance < C.NEARBY_RADIUS_MI;
        }).length;

        updateButton(btn, '📍', nearbyCount + ' churches within ' + C.NEARBY_RADIUS_MI + ' mi', false);

        // Reset button text after delay
        setTimeout(function() {
            updateButton(btn, '📍', 'Find Churches Near Me', false);
        }, 5000);
    }

    /**
     * Handle geolocation error
     * @param {GeolocationPositionError} error - Browser error object
     * @param {HTMLElement} btn - Button element to update
     */
    function handleGeolocationError(error, btn) {
        var message = GEO_ERROR_MESSAGES[error.code] || 'Location error';
        log('Geolocation error:', message, error);

        // Provide more helpful error messages
        if (error.code === 1) {
            message = 'Please enable location access in browser settings';
        } else if (error.code === 2) {
            message = 'Could not determine your location';
        } else if (error.code === 3) {
            message = 'Location request timed out - please try again';
        }

        showGeolocationError(message, btn);
    }

    /**
     * Show geolocation error with consistent UI
     * @param {string} message - Error message to display
     * @param {HTMLElement} btn - Button element to update
     */
    function showGeolocationError(message, btn) {
        // Update button if it exists
        updateButton(btn, '⚠️', message, false);

        // Show toast notification
        var map = State.get('map');
        if (map && map.getContainer) {
            var mapContainer = map.getContainer();
            if (mapContainer) {
                // Remove any existing error toasts
                var existingToast = mapContainer.querySelector('.geo-error-toast');
                if (existingToast) {
                    existingToast.parentNode.removeChild(existingToast);
                }

                // Create new error toast
                var errorToast = document.createElement('div');
                errorToast.className = 'geo-error-toast';
                errorToast.innerHTML = '<strong>Location Error:</strong> ' + message;
                errorToast.style.cssText = 'position: absolute; top: 10px; left: 50%; transform: translateX(-50%); ' +
                    'background: #dc2626; color: white; padding: 12px 20px; border-radius: 8px; ' +
                    'z-index: 1000; font-family: system-ui, sans-serif; box-shadow: 0 4px 6px rgba(0,0,0,0.1); ' +
                    'max-width: 90%; text-align: center;';
                mapContainer.appendChild(errorToast);

                // Auto-remove after 5 seconds
                setTimeout(function() {
                    if (errorToast.parentNode) {
                        errorToast.style.opacity = '0';
                        errorToast.style.transition = 'opacity 0.3s';
                        setTimeout(function() {
                            if (errorToast.parentNode) {
                                errorToast.parentNode.removeChild(errorToast);
                            }
                        }, 300);
                    }
                }, 5000);
            }
        }

        // Reset button text after delay
        if (btn) {
            setTimeout(function() {
                updateButton(btn, '📍', 'Find Churches Near Me', false);
            }, 3000);
        }
    }

    /**
     * Update geolocation button state
     * @param {HTMLElement} btn - Button element
     * @param {string} icon - Icon emoji
     * @param {string} text - Button text
     * @param {boolean} loading - Whether to show loading state
     */
    function updateButton(btn, icon, text, loading) {
        if (!btn) return;

        btn.classList.toggle('loading', loading);
        btn.innerHTML = '<span>' + icon + '</span><span>' + text + '</span>';
    }

    /**
     * Add marker for user's location
     * @param {number} lat - Latitude
     * @param {number} lon - Longitude
     */
    function addUserLocationMarker(lat, lon) {
        var map = State.get('map');
        if (!map) return;

        // Remove existing user marker
        var existingMarker = State.get('userLocationMarker');
        if (existingMarker) {
            map.removeLayer(existingMarker);
        }

        // Create user location marker
        var userIcon = L.divIcon({
            className: 'user-location-marker',
            html: '<div class="user-location-dot"></div>',
            iconSize: [22, 22],
            iconAnchor: [11, 11]
        });

        var marker = L.marker([lat, lon], { icon: userIcon, zIndexOffset: 1000 })
            .addTo(map)
            .bindPopup('<strong>Your Location</strong>');

        // Accessibility
        var el = marker.getElement();
        if (el) {
            el.setAttribute('aria-label', 'Your current location');
            el.setAttribute('role', 'img');
        }

        State.set('userLocationMarker', marker);
    }

    /**
     * Check URL for locate=me parameter and trigger geolocation
     */
    function checkLocateParam() {
        var params = new URLSearchParams(window.location.search);
        if (params.get('locate') !== 'me') return;

        log('locate=me detected, triggering geolocation...');

        // Wait for data to load
        var checkData = function() {
            var allChurches = State.get('allChurches') || [];
            if (allChurches.length > 0) {
                setTimeout(findNearbyItems, 500);
            } else {
                setTimeout(checkData, 200);
            }
        };
        checkData();
    }

    /**
     * Get saved user location from localStorage
     * @returns {{lat: number, lon: number}|null} Saved location or null
     */
    function getSavedLocation() {
        try {
            var saved = localStorage.getItem(C.STORAGE_USER_LOCATION || 'ubimissa_user_location');
            if (saved) {
                var parsed = JSON.parse(saved);
                if (typeof parsed.lat === 'number' && typeof parsed.lon === 'number') {
                    return parsed;
                }
            }
        } catch (e) {
            log('Could not load saved location:', e);
        }
        return null;
    }

    /**
     * Apply a location without prompting (used for IP-based location)
     * @param {{lat:number, lon:number, accuracy?:number}} coords
     * @param {{btn?:HTMLElement, map?:L.Map, allChurches?:Church[]}} [options]
     */
    function applyLocation(coords, options) {
        options = options || {};
        if (!coords || typeof coords.lat !== 'number' || typeof coords.lon !== 'number') return false;

        var map = options.map || State.get('map');
        var allChurches = options.allChurches || State.get('allChurches') || [];

        var mockPosition = {
            coords: {
                latitude: coords.lat,
                longitude: coords.lon,
                accuracy: coords.accuracy || 20000
            }
        };

        handleGeolocationSuccess(mockPosition, options.btn || null, map, allChurches);
        return true;
    }

    /**
     * Try approximate location without prompting
     * @param {{btn?:HTMLElement, map?:L.Map, allChurches?:Church[]}} [options]
     */
    function tryApproximateLocation(options) {
        options = options || {};
        var map = options.map || State.get('map');
        var allChurches = options.allChurches || State.get('allChurches') || [];
        var isGlobalView = options.isGlobal === true ||
            (window.dioceseConfig && window.dioceseConfig.isGlobalView && window.dioceseConfig.isGlobalView());
        var bounds = options.bounds ||
            (window.dioceseConfig && window.dioceseConfig.getDioceseBounds
                ? window.dioceseConfig.getDioceseBounds()
                : null);

        fetchApproxLocation().then(function(coords) {
            if (!coords) return;

            if (!isGlobalView && bounds && !isWithinDioceseBounds(coords.lat, coords.lon, bounds)) {
                log('Approx location outside diocese bounds, skipping auto-locate');
                return;
            }

            if (!isGlobalView && !bounds) {
                log('No bounds available for diocese, skipping auto-locate');
                return;
            }

            applyLocation(coords, { btn: options.btn, map: map, allChurches: allChurches });
        });
    }

    /**
     * Try IP-based geolocation as fallback
     * @param {HTMLElement} btn - Button element
     * @param {L.Map} map - Leaflet map instance
     * @param {Church[]} allChurches - All churches array
     */
    function tryIPBasedGeolocation(btn, map, allChurches) {
        log('Trying IP-based geolocation fallback...');

        fetchApproxLocation().then(function(coords) {
            if (coords) {
                applyLocation(coords, { btn: btn, map: map, allChurches: allChurches });
                return;
            }

            // Try multiple IP geolocation services
            var services = [
                { url: 'https://ipapi.co/json/', parser: function(data) { return { lat: data.latitude, lon: data.longitude }; } },
                { url: 'https://api.ipdata.co?api-key=test', parser: function(data) { return { lat: data.latitude, lon: data.longitude }; } },
                { url: 'https://freegeoip.app/json/', parser: function(data) { return { lat: data.latitude, lon: data.longitude }; } }
            ];

            function tryService(index) {
                if (index >= services.length) {
                    log('All IP geolocation services failed');
                    return;
                }

                var service = services[index];
                fetch(service.url)
                    .then(function(response) { return response.json(); })
                    .then(function(data) {
                        var coordsFromService = service.parser(data);
                        if (coordsFromService.lat && coordsFromService.lon) {
                            log('IP geolocation successful:', coordsFromService);
                            applyLocation({ lat: coordsFromService.lat, lon: coordsFromService.lon, accuracy: 20000 }, {
                                btn: btn,
                                map: map,
                                allChurches: allChurches
                            });
                        } else {
                            tryService(index + 1);
                        }
                    })
                    .catch(function(err) {
                        log('IP geolocation service failed:', service.url, err);
                        tryService(index + 1);
                    });
            }

            tryService(0);
        });
    }

    // Public API
    window.MapGeolocation = {
        findNearbyItems: findNearbyItems,
        addUserLocationMarker: addUserLocationMarker,
        checkLocateParam: checkLocateParam,
        isWithinDioceseBounds: isWithinDioceseBounds,
        getSavedLocation: getSavedLocation,
        applyLocation: applyLocation,
        tryApproximateLocation: tryApproximateLocation,
        tryIPBasedGeolocation: tryIPBasedGeolocation
    };

    // Global alias for HTML handlers
    window.findNearbyItems = findNearbyItems;

    // Auto-check for locate=me parameter on load
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            setTimeout(checkLocateParam, 100);
        });
    } else {
        // DOM already loaded
        setTimeout(checkLocateParam, 100);
    }

})(window);


// === sidebar.js ===
/**
 * MapSidebar - Sidebar UI module
 *
 * @fileoverview Handles sidebar toggle, item list display, stats badges.
 * Geolocation features are in geolocation.js
 * @depends MapConstants, MapState, MapUtils
 */
(function(window) {
    'use strict';

    var C = window.MapConstants;
    var State = window.MapState;
    var Utils = window.MapUtils;
    var log = State.log;

    /**
     * Toggle sidebar visibility
     */
    function toggleSidebar() {
        var sidebar = document.getElementById('sidebar');
        var overlay = document.getElementById('sidebarOverlay');
        var navBtn = document.getElementById('navFiltersBtn');
        var statsBadge = document.getElementById('statsBadge');
        var toggleBtn = document.getElementById('sidebarToggleBtn');
        var map = State.get('map');

        if (!sidebar) return;

        var isMobile = State.isMobile();

        if (isMobile) {
            // Mobile: toggle active class (slide from right)
            var isActive = sidebar.classList.toggle('active');
            if (overlay) overlay.classList.toggle('active', isActive);
            document.body.classList.toggle('sidebar-open', isActive);
            document.documentElement.classList.toggle('sidebar-open', isActive);

            updateNavButton(navBtn, isActive);

            // Show stats badge when sidebar is not active on mobile
            if (statsBadge) statsBadge.style.display = isActive ? 'none' : 'block';

            // Accessibility
            sidebar.setAttribute('aria-hidden', String(!isActive));

            // Trigger map resize
            if (map) {
                setTimeout(function() { map.invalidateSize(); }, C.MAP_RESIZE_DELAY);
            }
        } else {
            document.body.classList.remove('sidebar-open');
            document.documentElement.classList.remove('sidebar-open');

            // Desktop: toggle collapsed class
            var isCollapsed = sidebar.classList.toggle('collapsed');
            localStorage.setItem(C.STORAGE_SIDEBAR_COLLAPSED, isCollapsed);

            updateNavButton(navBtn, !isCollapsed);

            // Show/hide floating toggle button
            if (toggleBtn) {
                toggleBtn.classList.toggle('visible', isCollapsed);
                toggleBtn.setAttribute('aria-expanded', String(!isCollapsed));
            }

            // Show/hide stats badge
            if (statsBadge) statsBadge.style.display = isCollapsed ? 'block' : 'none';

            // Trigger map resize
            if (map) {
                setTimeout(function() { map.invalidateSize(); }, 300);
            }
        }
    }

    /**
     * Update nav button icon and text
     * @param {HTMLElement} navBtn - Navigation button element
     * @param {boolean} isOpen - Whether sidebar is open
     */
    function updateNavButton(navBtn, isOpen) {
        if (!navBtn) return;

        var icon = navBtn.querySelector('.filter-icon');
        var text = navBtn.querySelector('.filter-text');

        if (icon) icon.textContent = isOpen ? '✕' : '☰';
        if (text) text.textContent = isOpen ? 'Close' : 'Filters';

        navBtn.setAttribute('aria-expanded', String(isOpen));
    }

    /**
     * Get sidebar page size from SmartDefaults
     * @returns {number} Page size
     */
    function getSidebarPageSize() {
        var allChurches = State.get('allChurches') || [];
        var SmartDefaults = State.getSmartDefaults();
        var defaults = SmartDefaults.get(allChurches.length);
        return defaults.sidebar.pageSize === Infinity ? 99999 : defaults.sidebar.pageSize;
    }

    /**
     * Render a single church card
     * @param {Church} item - Church data
     * @returns {string} HTML string for card
     */
    function renderChurchCard(item) {
        if (!item) return '';

        var currentLang = Utils.getCurrentLanguage();
        var websiteLabel = Utils.translate('mapItemCardWebsite', 'Website');
        var isAnglican = State.isAnglican();
        var massTimesLabel = isAnglican
            ? Utils.translate('serviceTimes', 'Service Times')
            : Utils.translate('massTimes', 'Mass Times');
        var viewOnMapLabel = Utils.translate('mapItemCardViewOnMap', 'View on Map');

        var websiteUrl = Utils.buildWebsiteUrl(item.website);
        var websiteDisplay = item.website || '';
        if (websiteDisplay) {
            websiteDisplay = websiteDisplay
                .replace(/^https?:\/\//, '')
                .replace(/^www\./, '')
                .replace(/\/$/, '');
            if (websiteDisplay.length > 30) {
                websiteDisplay = websiteDisplay.substring(0, 30) + '...';
            }
        }
        var websiteLinkText = websiteDisplay ? ('🌐 ' + websiteDisplay) : websiteLabel;

        var slug = Utils.generateSlug(item.name, item.city);
        var safeName = Utils.escapeAttr(item.name || '');
        var safeCity = Utils.escapeAttr(item.city || '');
        var displayName = Utils.translateChurchName(item.name, currentLang);

        var distanceHtml = '';
        if (typeof item.distance === 'number' && isFinite(item.distance)) {
            distanceHtml = '<div class="item-distance">📍 ' + item.distance.toFixed(1) + ' mi</div>';
        }

        var churchUrl = Utils.buildLocalizedUrl('/church/' + slug);

        var directionsLabel = Utils.translate('directions', 'Directions');
        var directionsUrl = (typeof item.lat === 'number' && typeof item.lon === 'number')
            ? 'https://www.google.com/maps/dir/?api=1&destination=' + item.lat + ',' + item.lon
            : '';

        return '<div class="item-card" data-name="' + Utils.escapeHtml(item.name) + '" ' +
            'data-city="' + Utils.escapeHtml(item.city) + '" ' +
            'role="button" tabindex="0" ' +
            'onclick="selectItem(\'' + safeName + '\', \'' + safeCity + '\')" ' +
            'onkeydown="if(event.key===\'Enter\')selectItem(\'' + safeName + '\', \'' + safeCity + '\')">' +
            '<div class="item-header">' +
                '<div class="item-name">' + Utils.escapeHtml(displayName) + '</div>' +
                distanceHtml +
            '</div>' +
            '<div class="item-actions">' +
                '<a href="' + churchUrl + '" class="item-action-link item-action-primary" ' +
                    'onclick="event.stopPropagation()">' + massTimesLabel + '</a>' +
                (directionsUrl
                    ? '<a href="' + directionsUrl + '" target="_blank" rel="noopener noreferrer" ' +
                        'onclick="event.stopPropagation()" class="item-action-link item-action-directions">🧭 ' + directionsLabel + '</a>'
                    : '') +
                (websiteUrl
                    ? '<a href="' + Utils.escapeHtml(websiteUrl) + '" target="_blank" rel="noopener noreferrer" ' +
                        'onclick="event.stopPropagation()" class="item-action-link" title="' + Utils.escapeAttr(websiteLabel) + '">' +
                        Utils.escapeHtml(websiteLinkText) + '</a>'
                    : '') +
            '</div>' +
            '<div class="item-location">' + Utils.escapeHtml(item.city) + ', ' + Utils.escapeHtml(item.state) + '</div>' +
        '</div>';
    }

    // Cache for sorted items to avoid re-sorting on every call
    var sortedItemsCache = { items: null, key: null };

    /**
     * Display items in the sidebar list with pagination
     * @param {boolean} [resetPagination=true] - Whether to reset to first page
     */
    function displayItems(resetPagination) {
        var container = document.getElementById('itemList');
        if (!container) return;

        var LABELS = State.getLabels();

        if (resetPagination !== false) {
            State.set('sidebarPage', 0);
        }

        var filteredItems = State.get('filteredItems') || [];
        var viewportOnly = State.get('viewportOnly');

        var itemsInBounds = viewportOnly ? getItemsInBounds() : filteredItems;

        // Create a cache key based on items count and first/last item ids
        var cacheKey = itemsInBounds.length + '_' +
            (itemsInBounds[0] ? itemsInBounds[0].id : '') + '_' +
            (itemsInBounds[itemsInBounds.length - 1] ? itemsInBounds[itemsInBounds.length - 1].id : '');

        // Sort only if items changed (expensive operation)
        var hasDistance = itemsInBounds.length > 0 && typeof itemsInBounds[0].distance === 'number';
        if (sortedItemsCache.key !== cacheKey) {
            if (!hasDistance) {
                itemsInBounds = itemsInBounds.slice().sort(function(a, b) {
                    return (a.name || '').localeCompare(b.name || '');
                });
            }
            sortedItemsCache = { items: itemsInBounds, key: cacheKey };
        } else {
            itemsInBounds = sortedItemsCache.items;
        }

        State.set('cachedSidebarEntities', itemsInBounds);

        var totalCount = itemsInBounds.length;
        var pageSize = getSidebarPageSize();
        var sidebarPage = State.get('sidebarPage') || 0;
        var showCount = Math.min((sidebarPage + 1) * pageSize, totalCount);
        var itemsToShow = itemsInBounds.slice(0, showCount);
        var hasMore = showCount < totalCount;

        if (totalCount === 0) {
            container.innerHTML = renderEmptyState();
            return;
        }

        var paginationHeader =
            '<div class="sidebar-pagination-header">' +
                '<span class="pagination-count">Showing <strong>' + showCount.toLocaleString() +
                '</strong> of <strong>' + totalCount.toLocaleString() + '</strong> ' + LABELS.items + '</span>' +
            '</div>';

        var listHtml = itemsToShow.map(renderChurchCard).join('');

        var loadMoreHtml = hasMore ?
            '<div class="sidebar-load-more">' +
                '<button class="load-more-btn" onclick="MapSidebar.loadMore()" aria-label="Load more churches">' +
                    '<span>Load More</span>' +
                    '<span class="load-more-count">(' + (totalCount - showCount).toLocaleString() + ' remaining)</span>' +
                '</button>' +
            '</div>' : '';

        container.innerHTML = paginationHeader + listHtml + loadMoreHtml;
    }

    /**
     * Render empty state message
     * @returns {string} HTML string
     */
    function renderEmptyState() {
        return '<div class="no-results" role="status">' +
            '<div class="no-results-icon">🙏</div>' +
            '<div class="no-results-title">' +
                Utils.translate('noChurchesInArea', 'No churches in this area yet') +
            '</div>' +
            '<div class="no-results-message">' +
                Utils.translate('zoomOutToExplore', 'Zoom out or drag the map to explore churches') +
            '</div>' +
        '</div>';
    }

    /**
     * Load more items in sidebar
     */
    function loadMore() {
        var sidebarPage = State.get('sidebarPage') || 0;
        State.set('sidebarPage', sidebarPage + 1);

        var container = document.getElementById('itemList');
        var scrollPos = container ? container.scrollTop : 0;

        displayItems(false);

        if (container) {
            container.scrollTop = scrollPos;
        }
    }

    /**
     * Get items within current map bounds
     * @returns {Church[]} Churches in viewport
     */
    function getItemsInBounds() {
        var map = State.get('map');
        var filteredItems = State.get('filteredItems') || [];

        if (!map) return filteredItems;

        var bounds = map.getBounds();
        return filteredItems.filter(function(item) {
            if (typeof item.lat !== 'number' || typeof item.lon !== 'number') return false;
            return bounds.contains([item.lat, item.lon]);
        });
    }

    /**
     * Update statistics display
     */
    function updateStats() {
        var allChurches = State.get('allChurches') || [];
        var total = allChurches.length;
        var itemsInViewport = getItemsInBounds();
        var visibleCount = itemsInViewport.length;
        var percent = total > 0 ? Math.round((visibleCount / total) * 100) : 0;

        // Update sidebar stats
        var totalEl = document.getElementById('totalItems');
        var visibleEl = document.getElementById('visibleCount');
        var percentEl = document.getElementById('visiblePercent');
        var churchesEl = document.getElementById('churchesCount');

        if (totalEl) totalEl.textContent = total;
        if (visibleEl) visibleEl.textContent = visibleCount.toLocaleString();
        if (percentEl) percentEl.textContent = percent + '%';
        if (churchesEl) churchesEl.textContent = total.toLocaleString();

        updateStatsBadge(total, visibleCount);
    }

    /**
     * Update floating stats badge
     * @param {number} total - Total church count
     * @param {number} visible - Visible church count
     */
    function updateStatsBadge(total, visible) {
        var badgeTotal = document.getElementById('statsBadgeTotal');
        var badgeVisible = document.getElementById('statsBadgeVisible');

        if (badgeTotal) badgeTotal.textContent = (total || 0).toLocaleString();
        if (badgeVisible) badgeVisible.textContent = (visible || 0).toLocaleString();
    }

    /**
     * Initialize sidebar state from localStorage
     */
    function initSidebar() {
        var sidebar = document.getElementById('sidebar');
        var statsBadge = document.getElementById('statsBadge');
        var toggleBtn = document.getElementById('sidebarToggleBtn');
        var navBtn = document.getElementById('navFiltersBtn');
        var overlay = document.getElementById('sidebarOverlay');
        var mobileCloseBtn = document.querySelector('.mobile-close-btn');
        var headerCloseBtn = document.querySelector('.header-close');
        var isMobile = State.isMobile();
        var map = State.get('map');

        if (isMobile) {
            var mobileOpen = !!(sidebar && sidebar.classList.contains('active'));
            document.body.classList.toggle('sidebar-open', mobileOpen);
            document.documentElement.classList.toggle('sidebar-open', mobileOpen);
        } else {
            document.body.classList.remove('sidebar-open');
            document.documentElement.classList.remove('sidebar-open');
        }

        // Check if we're on global view (ubimissa.com)
        var isGlobal = window.dioceseConfig && window.dioceseConfig.isGlobalView && window.dioceseConfig.isGlobalView();

        if (!isMobile && sidebar) {
            var savedCollapsed = localStorage.getItem(C.STORAGE_SIDEBAR_COLLAPSED);

            // Start collapsed on global view by default, unless user has explicitly opened it
            if (isGlobal && savedCollapsed === null) {
                sidebar.classList.add('collapsed');
            } else if (savedCollapsed === 'true') {
                sidebar.classList.add('collapsed');
            }
            var isCollapsed = sidebar.classList.contains('collapsed');

            if (toggleBtn) {
                toggleBtn.classList.toggle('visible', isCollapsed);
                toggleBtn.setAttribute('aria-expanded', String(!isCollapsed));
                toggleBtn.setAttribute('aria-controls', 'sidebar');
            }

            if (statsBadge) statsBadge.style.display = isCollapsed ? 'block' : 'none';

            if (map) {
                setTimeout(function() { map.invalidateSize(); }, 100);
            }
        }

        bindToggle(navBtn);
        bindToggle(toggleBtn);
        bindToggle(overlay);
        bindToggle(mobileCloseBtn);
        bindToggle(headerCloseBtn);

        // Set up draggable stats badge on desktop
        if (statsBadge && !isMobile) {
            setupDraggableBadge(statsBadge);
        }

        // Initialize theme selector
        var themeContainer = document.getElementById('mapThemeSelector');
        if (themeContainer && window.MapThemes && window.MapThemes.renderThemeSelector) {
            window.MapThemes.renderThemeSelector('mapThemeSelector');
        }
    }

    /**
     * Set up draggable stats badge
     * @param {HTMLElement} badge - Badge element
     */
    function setupDraggableBadge(badge) {
        var isDragging = false;
        var hasDragged = false;
        var startX, startY, startLeft, startBottom;

        badge.addEventListener('mousedown', function(e) {
            isDragging = true;
            hasDragged = false;
            badge.classList.add('dragging');

            startX = e.clientX;
            startY = e.clientY;

            var rect = badge.getBoundingClientRect();
            startLeft = rect.left;
            startBottom = window.innerHeight - rect.bottom;

            e.preventDefault();
        });

        document.addEventListener('mousemove', function(e) {
            if (!isDragging) return;

            var deltaX = e.clientX - startX;
            var deltaY = e.clientY - startY;

            if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
                hasDragged = true;
            }

            var newLeft = startLeft + deltaX;
            var newBottom = startBottom - deltaY;
            var maxLeft = window.innerWidth - badge.offsetWidth - 10;
            var maxBottom = window.innerHeight - badge.offsetHeight - 10;

            badge.style.left = Math.max(10, Math.min(maxLeft, newLeft)) + 'px';
            badge.style.bottom = Math.max(10, Math.min(maxBottom, newBottom)) + 'px';
            badge.style.right = 'auto';
        });

        document.addEventListener('mouseup', function() {
            if (isDragging) {
                isDragging = false;
                badge.classList.remove('dragging');

                localStorage.setItem(C.STORAGE_BADGE_LEFT, badge.style.left);
                localStorage.setItem(C.STORAGE_BADGE_BOTTOM, badge.style.bottom);
            }
        });

        // Click to open sidebar (only if not dragged)
        badge.addEventListener('click', function() {
            if (!hasDragged) {
                toggleSidebar();
            }
        });

        // Restore saved position
        var savedLeft = localStorage.getItem(C.STORAGE_BADGE_LEFT);
        var savedBottom = localStorage.getItem(C.STORAGE_BADGE_BOTTOM);
        if (savedLeft) {
            badge.style.left = savedLeft;
            badge.style.right = 'auto';
        }
        if (savedBottom) badge.style.bottom = savedBottom;
    }

    function bindToggle(element) {
        if (!element || element.getAttribute('data-sidebar-toggle-bound') === 'true') return;
        element.addEventListener('click', function(event) {
            if (event) event.preventDefault();
            toggleSidebar();
        });
        element.setAttribute('data-sidebar-toggle-bound', 'true');
    }

    // Public API
    window.MapSidebar = {
        toggleSidebar: toggleSidebar,
        displayItems: displayItems,
        loadMore: loadMore,
        updateStats: updateStats,
        updateStatsBadge: updateStatsBadge,
        initSidebar: initSidebar,
        getItemsInBounds: getItemsInBounds,
        getSidebarPageSize: getSidebarPageSize,
        renderChurchCard: renderChurchCard
    };

    // Global aliases for HTML onclick handlers
    window.toggleSidebar = toggleSidebar;
    window.loadMoreSidebar = loadMore;

})(window);


// === filters.js ===
/**
 * MapFilters - Filtering functionality
 *
 * Handles day filter, language filter, search, and filter state.
 * Depends on: MapState, MapUtils
 */
(function(window) {
    'use strict';

    var State = window.MapState;
    var log = State.log;

    /**
     * Apply all active filters to the church list.
     * Preserves distance data and sort order when user has been geolocated.
     */
    function applyFilters() {
        var allChurches = State.get('allChurches') || [];
        var filteredItems = allChurches.slice();

        // Apply state filter
        var stateFilter = document.getElementById('stateFilter');
        if (stateFilter && stateFilter.value) {
            filteredItems = filterByState(filteredItems, stateFilter.value);
        }

        // Apply day filter
        var selectedDay = State.get('selectedDay') || 'all';
        if (selectedDay !== 'all') {
            filteredItems = filterByDayOfWeek(filteredItems, selectedDay);
        }

        // Apply language filter
        var languageFilter = document.getElementById('languageFilter');
        if (languageFilter && languageFilter.value) {
            filteredItems = filterByLanguage(filteredItems, languageFilter.value);
        }

        // Apply search filter
        var searchInput = document.getElementById('churchSearch') || document.getElementById('searchInput');
        if (searchInput && searchInput.value && searchInput.value.trim()) {
            var searchValue = searchInput.value.trim();
            if (isZipSearch(searchValue)) {
                handleZipSearch(searchValue);
            } else {
                filteredItems = filterBySearch(filteredItems, searchValue);
            }
        }

        // Preserve distance data when user has been geolocated.
        // Without this, filtering wipes distance info and sidebar falls back to alphabetical sort.
        var userLocation = State.get('userLocation');
        if (userLocation && typeof userLocation.lat === 'number' && typeof userLocation.lon === 'number') {
            var Utils = window.MapUtils;
            var userLat = userLocation.lat;
            var userLon = userLocation.lon;
            filteredItems = filteredItems.map(function(church) {
                if (typeof church.lat !== 'number' || typeof church.lon !== 'number') {
                    return Object.assign({}, church, { distance: Infinity });
                }
                var distance = Utils.haversineDistance(userLat, userLon, church.lat, church.lon);
                return Object.assign({}, church, { distance: distance });
            }).sort(function(a, b) {
                return a.distance - b.distance;
            });
        }

        State.set('filteredItems', filteredItems);
        log('Filters applied:', filteredItems.length, 'of', allChurches.length);

        // Clear hex summary so visualizations recalculate with filtered data
        if (window._hexSummary && window._hexSummary.computed) {
            window._hexSummary = null;
        }

        // Update UI - sidebar list and stats
        if (window.MapSidebar) {
            if (window.MapSidebar.displayItems) {
                window.MapSidebar.displayItems(true);
            }
            if (window.MapSidebar.updateStats) {
                window.MapSidebar.updateStats();
            }
        }

        // Update markers directly (faster than full view update)
        var currentView = State.get('currentView');
        if (currentView === 'markers' && window.MapMarkers) {
            window.MapMarkers.addMarkersToMap();
        } else if (window.MapViews && window.MapViews.updateMapView) {
            // Only do full view update for non-marker views (hex, heat, etc)
            window.MapViews.updateMapView();
        }

        // Update mode indicator
        if (window.MapViews && window.MapViews.updateModeIndicator) {
            window.MapViews.updateModeIndicator();
        }

        // Dispatch event for other modules to react
        window.dispatchEvent(new CustomEvent('mapFiltersApplied', {
            detail: { filtered: filteredItems.length, total: allChurches.length }
        }));

        return filteredItems;
    }

    /**
     * Filter churches by state
     */
    function filterByState(churches, state) {
        if (!state) return churches;
        return churches.filter(function(church) {
            return church.state === state;
        });
    }

    /**
     * Pre-parse mass times JSON once and cache on each church object.
     * Called after data load to avoid repeated JSON.parse in filter loops.
     */
    function preparseMassTimes(churches) {
        churches.forEach(function(church) {
            if (church._massDaysParsed) return;
            church._massDaysParsed = true;
            church._massDaysSet = null; // set of lowercase day keywords present

            try {
                var days = {};
                var addDay = function(text) {
                    if (!text) return;
                    var lower = text.toLowerCase();
                    var allDays = ['sunday','sun','monday','mon','tuesday','tue','wednesday','wed','thursday','thu','friday','fri','saturday','sat','vigil'];
                    allDays.forEach(function(d) { if (lower.indexOf(d) !== -1) days[d] = true; });
                };

                if (church.mass_times_json) {
                    var masses = typeof church.mass_times_json === 'string'
                        ? JSON.parse(church.mass_times_json)
                        : church.mass_times_json;

                    if (Array.isArray(masses)) {
                        masses.forEach(function(m) { addDay(m.day || m.type || ''); });
                    } else if (typeof masses === 'object' && masses !== null) {
                        Object.keys(masses).forEach(function(k) { addDay(k); });
                        if (masses.weekday && Array.isArray(masses.weekday)) {
                            masses.weekday.forEach(function(e) { addDay(e.day || ''); });
                        }
                    }
                }

                if (church.mass_times) {
                    addDay(church.mass_times);
                }

                church._massDaysSet = days;
            } catch (e) {
                church._massDaysSet = {};
            }
        });
    }

    /**
     * Filter churches by day of week based on pre-parsed mass day data.
     * Uses the cached _massDaysSet for O(1) lookup instead of re-parsing JSON.
     */
    function filterByDayOfWeek(churches, day) {
        if (day === 'all') return churches;

        var dayVariants = getDayVariants(day.toLowerCase());

        return churches.filter(function(church) {
            var daySet = church._massDaysSet;
            if (!daySet) return false;
            return dayVariants.some(function(v) { return daySet[v]; });
        });
    }

    /**
     * Get day name variants for matching
     */
    function getDayVariants(day) {
        var variants = {
            'sunday': ['sunday', 'sun'],
            'monday': ['monday', 'mon'],
            'tuesday': ['tuesday', 'tue'],
            'wednesday': ['wednesday', 'wed'],
            'thursday': ['thursday', 'thu'],
            'friday': ['friday', 'fri'],
            'saturday': ['saturday', 'sat', 'vigil']
        };
        return variants[day] || [day];
    }

    /**
     * Filter churches by language
     */
    function filterByLanguage(churches, language) {
        if (!language) return churches;

        var langLower = language.toLowerCase();
        return churches.filter(function(church) {
            if (!church.languages || church.languages.length === 0) {
                // No explicit languages listed: treat as unknown.
                return false;
            }
            return church.languages.some(function(l) {
                return l.toLowerCase() === langLower;
            });
        });
    }

    /**
     * Normalize text: strip accents/diacritics and lowercase
     * "San José" -> "san jose", "Nuestra Señora" -> "nuestra senora"
     */
    function normalizeText(str) {
        if (!str) return '';
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    /**
     * Expand search terms with common abbreviations
     * "St Mary" -> ["st mary", "saint mary", "san mary", "santo mary", "santa mary"]
     */
    function expandSearchTerms(searchText) {
        var normalized = normalizeText(searchText);
        var terms = [normalized];

        // Abbreviation expansions
        var abbrevs = {
            'st ': ['saint ', 'san ', 'santo ', 'santa '],
            'st. ': ['saint ', 'san ', 'santo ', 'santa '],
            'mt ': ['mount ', 'monte '],
            'mt. ': ['mount ', 'monte '],
            'bl ': ['blessed ', 'beato ', 'beata '],
            'bl. ': ['blessed ', 'beato ', 'beata ']
        };

        for (var abbr in abbrevs) {
            if (normalized.indexOf(abbr) === 0) {
                var rest = normalized.slice(abbr.length);
                abbrevs[abbr].forEach(function(exp) {
                    terms.push(exp + rest);
                });
            }
        }

        return terms;
    }

    /**
     * Filter churches by search text
     * Features:
     * - Accent-insensitive: "San Jose" finds "San José"
     * - Abbreviation expansion: "St Mary" finds "Santa María"
     * - Searches: name, city, state, zip_code, address
     */
    function filterBySearch(churches, searchText) {
        if (!searchText) return churches;

        var searchTerms = expandSearchTerms(searchText);

        return churches.filter(function(church) {
            var nameNorm = normalizeText(church.name);
            var cityNorm = normalizeText(church.city);
            var addressNorm = normalizeText(church.address);
            var stateNorm = church.state ? church.state.toLowerCase() : '';
            var zipStr = church.zip_code ? church.zip_code.toString() : '';

            return searchTerms.some(function(term) {
                return nameNorm.indexOf(term) !== -1 ||
                       cityNorm.indexOf(term) !== -1 ||
                       stateNorm.indexOf(term) !== -1 ||
                       zipStr.indexOf(term) !== -1 ||
                       addressNorm.indexOf(term) !== -1;
            });
        });
    }

    function isZipSearch(searchText) {
        return /^\d{5}(-\d{4})?$/.test(searchText);
    }

    function handleZipSearch(zipText) {
        var map = State.get('map');
        if (!map) return;

        var lastZip = State.get('lastZipSearch');
        if (lastZip === zipText) return;

        State.set('lastZipSearch', zipText);
        var query = zipText + ' USA';

        fetch('https://nominatim.openstreetmap.org/search?format=json&q=' + encodeURIComponent(query) + '&limit=1')
            .then(function(resp) { return resp.json(); })
            .then(function(data) {
                if (data && data.length > 0) {
                    var lat = parseFloat(data[0].lat);
                    var lon = parseFloat(data[0].lon);
                    if (!isNaN(lat) && !isNaN(lon)) {
                        map.setView([lat, lon], 11);
                    }
                }
            })
            .catch(function(err) {
                log('Zip lookup failed:', err);
            });
    }

    /**
     * Set day filter and apply
     */
    function setDayFilter(day) {
        State.set('selectedDay', day);

        // Update button states
        var buttons = document.querySelectorAll('.day-filter-btn');
        buttons.forEach(function(btn) {
            var isActive = btn.getAttribute('data-day') === day;
            btn.classList.toggle('active', isActive);
        });

        applyFilters(); // Now handles UI update internally
    }

    /**
     * Reset all filters to defaults
     */
    function resetFilters() {
        State.set('selectedDay', 'all');
        State.set('lastZipSearch', '');

        // Reset day buttons
        var dayButtons = document.querySelectorAll('.day-filter-btn');
        dayButtons.forEach(function(btn) {
            btn.classList.toggle('active', btn.getAttribute('data-day') === 'all');
        });

        // Reset state filter
        var stateFilter = document.getElementById('stateFilter');
        if (stateFilter) {
            stateFilter.value = '';
        }

        // Reset language filter
        var languageFilter = document.getElementById('languageFilter');
        if (languageFilter) {
            languageFilter.value = '';
        }

        // Reset search
        var searchInput = document.getElementById('churchSearch') || document.getElementById('searchInput');
        if (searchInput) {
            searchInput.value = '';
        }

        applyFilters(); // Now handles UI update internally
        log('Filters reset');
    }

    /**
     * Populate filter dropdowns based on data
     */
    function populateFilters() {
        var allChurches = State.get('allChurches') || [];

        // Show/hide state filter based on diocese
        var stateFilterGroup = document.getElementById('stateFilterGroup');
        var isGlobal = window.dioceseConfig && window.dioceseConfig.isGlobalView && window.dioceseConfig.isGlobalView();
        // Also show if on ubimissa domain or if diocese param not set
        if (!isGlobal) {
            var hostname = window.location.hostname;
            isGlobal = hostname.indexOf('ubimissa') !== -1 || hostname === 'localhost';
        }
        if (stateFilterGroup) {
            stateFilterGroup.style.display = isGlobal ? '' : 'none';
        }

        // Collect all languages
        var languages = {};
        allChurches.forEach(function(church) {
            if (church.languages && church.languages.length > 0) {
                church.languages.forEach(function(lang) {
                    languages[lang] = (languages[lang] || 0) + 1;
                });
            }
        });

        // Populate language filter
        var languageFilter = document.getElementById('languageFilter');
        var languageFilterGroup = document.getElementById('languageFilterGroup');

        // Hide language filter for Anglican (English only)
        var isAnglican = State.isAnglican();
        if (isAnglican && languageFilterGroup) {
            languageFilterGroup.style.display = 'none';
        } else if (languageFilter) {
            // Clear existing options except first
            while (languageFilter.options.length > 1) {
                languageFilter.removeChild(languageFilter.lastChild);
            }

            // Sort by count and add options
            var sorted = Object.keys(languages).sort(function(a, b) {
                return languages[b] - languages[a];
            });

            sorted.forEach(function(lang) {
                var option = document.createElement('option');
                option.value = lang;
                option.textContent = lang + ' (' + languages[lang] + ')';
                languageFilter.appendChild(option);
            });
        }

        log('Filters populated with', Object.keys(languages).length, 'languages');
    }

    /**
     * Set up filter event listeners
     */
    function setupFilterListeners() {
        // State filter
        var stateFilter = document.getElementById('stateFilter');
        if (stateFilter) {
            stateFilter.addEventListener('change', function() {
                applyFilters();
                // Zoom to state bounds when selected
                zoomToStateFilter(stateFilter.value);
            });

            // Check URL params for state filter on load
            var urlParams = new URLSearchParams(window.location.search);
            var stateParam = urlParams.get('state');
            if (stateParam) {
                stateFilter.value = stateParam;
            }
        }

        // Language filter
        var languageFilter = document.getElementById('languageFilter');
        if (languageFilter) {
            languageFilter.addEventListener('change', applyFilters);
        }

        // Search input (debounced)
        var searchInput = document.getElementById('churchSearch') || document.getElementById('searchInput');
        if (searchInput) {
            var debounceTimer;
            searchInput.addEventListener('input', function() {
                clearTimeout(debounceTimer);
                debounceTimer = setTimeout(applyFilters, 300);
            });
        }

        // Day filter buttons
        var dayButtons = document.querySelectorAll('.day-filter-btn');
        dayButtons.forEach(function(btn) {
            btn.addEventListener('click', function() {
                var day = this.getAttribute('data-day');
                setDayFilter(day);
            });
        });
    }

    /**
     * Zoom map to show filtered state
     */
    function zoomToStateFilter(state) {
        var map = State.get('map');
        if (!map) return;

        // Approximate state centers
        var stateBounds = {
            'CA': { center: [36.7783, -119.4179], zoom: 6 },
            'MA': { center: [42.4072, -71.3824], zoom: 8 },
            'NY': { center: [42.1657, -74.9481], zoom: 7 },
            'MN': { center: [46.7296, -94.6859], zoom: 6 },
            'NC': { center: [35.7596, -79.0193], zoom: 7 },
            'IN': { center: [40.2672, -86.1349], zoom: 7 },
            'TX': { center: [31.9686, -99.9018], zoom: 6 },
            'Dublin': { center: [53.3498, -6.2603], zoom: 9 }
        };

        if (state && stateBounds[state]) {
            map.setView(stateBounds[state].center, stateBounds[state].zoom);
        } else {
            // Reset to US view
            map.setView([39.8283, -98.5795], 4);
        }
    }

    // Public API
    window.MapFilters = {
        applyFilters: applyFilters,
        setDayFilter: setDayFilter,
        resetFilters: resetFilters,
        populateFilters: populateFilters,
        setupFilterListeners: setupFilterListeners,
        preparseMassTimes: preparseMassTimes,
        filterByDayOfWeek: filterByDayOfWeek,
        filterByLanguage: filterByLanguage,
        filterBySearch: filterBySearch,
        filterByState: filterByState,
        zoomToStateFilter: zoomToStateFilter
    };

    // Expose globally for HTML onclick handlers
    window.filterByDay = setDayFilter;
    window.resetFilters = resetFilters;

})(window);


// === liturgy.js ===
/**
 * MapLiturgy - Liturgy data fetching and rendering
 *
 * Handles Mass times, Confession, Adoration schedule display.
 * Depends on: MapState, MapUtils
 */
(function(window) {
    'use strict';

    var State = window.MapState;
    var Utils = window.MapUtils;
    var log = State.log;

    /**
     * Fetch liturgy data from API
     */
    function fetchLiturgyData(churchName, city, containerId) {
        var liturgyCache = State.get('liturgyCache') || {};
        var cacheKey = churchName + '_' + city;

        // Check cache first
        if (liturgyCache[cacheKey] !== undefined) {
            renderLiturgyData(liturgyCache[cacheKey], containerId);
            return;
        }

        var params = new URLSearchParams({ church: churchName, city: city });
        fetch('/api/liturgy?' + params)
            .then(function(response) { return response.json(); })
            .then(function(data) {
                if (data.success && data.liturgy) {
                    liturgyCache[cacheKey] = data.liturgy;
                    State.set('liturgyCache', liturgyCache);
                    renderLiturgyData(data.liturgy, containerId);
                } else {
                    // Try fallback from church description
                    var fallback = getFallbackLiturgy(churchName, city);
                    liturgyCache[cacheKey] = fallback;
                    State.set('liturgyCache', liturgyCache);
                    renderLiturgyData(fallback, containerId);
                }
            })
            .catch(function(error) {
                console.error('Error fetching liturgy:', error);
                var fallback = getFallbackLiturgy(churchName, city);
                liturgyCache[cacheKey] = fallback;
                State.set('liturgyCache', liturgyCache);
                renderLiturgyData(fallback, containerId);
            });
    }

    /**
     * Get fallback liturgy from church description
     */
    function getFallbackLiturgy(churchName, city) {
        var allChurches = State.get('allChurches') || [];
        var church = allChurches.find(function(c) {
            return c.name === churchName && c.city === city;
        });

        if (church && church.description && church.description.indexOf(':') !== -1) {
            return {
                mass_times: church.description,
                confession_times: null,
                adoration_times: null
            };
        }
        return null;
    }

    /**
     * Render liturgy data into container
     */
    function renderLiturgyData(liturgy, containerId) {
        var container = document.getElementById(containerId);
        if (!container) return;

        var isAnglican = State.isAnglican();

        if (!liturgy) {
            var visitKey = isAnglican ? 'mapVisitWebsiteForServiceTimes' : 'mapVisitWebsiteForMassTimes';
            var visitFallback = isAnglican ? 'Visit website for service times' : 'Visit website for Mass times';
            container.innerHTML = '<div style="font-size: 11px; color: #8899a6;">' +
                Utils.translate(visitKey, visitFallback) + '</div>';
            return;
        }

        // Try to get mass times from JSON or parse from text
        var masses = parseMassTimes(liturgy);

        if (masses.length === 0) {
            var visitKey = isAnglican ? 'mapVisitWebsiteForServiceTimes' : 'mapVisitWebsiteForMassTimes';
            var visitFallback = isAnglican ? 'Visit website for service times' : 'Visit website for Mass times';
            container.innerHTML = '<div style="font-size: 11px; color: #8899a6;">' +
                Utils.translate(visitKey, visitFallback) + '</div>';
            return;
        }

        // Group by day
        var dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        var today = dayNames[new Date().getDay()];
        var typeToDay = { 'vigil': 'Saturday', 'sunday': 'Sunday', 'weekday': 'Weekday' };

        var byDay = {};
        masses.forEach(function(m) {
            var day = m.day || typeToDay[m.type] || 'Other';
            if (!byDay[day]) byDay[day] = [];
            byDay[day].push(m.time);
        });

        // Build HTML
        var dayOrder = {
            'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
            'Thursday': 4, 'Friday': 5, 'Saturday': 6, 'Weekday': 7
        };

        var sortedDays = Object.keys(byDay).sort(function(a, b) {
            return (dayOrder[a] || 99) - (dayOrder[b] || 99);
        });

        var html = '<div style="font-size: 12px; line-height: 1.5;">';

        sortedDays.forEach(function(day) {
            var times = byDay[day].filter(function(t, i, arr) {
                return arr.indexOf(t) === i;
            }).join(', ');

            var isToday = day === today ||
                (day === 'Weekday' && ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].indexOf(today) !== -1);
            var style = isToday ? 'color: #4ade80; font-weight: 600;' : 'color: #e1e8ed;';

            var translatedDay = Utils.translate('day' + day, day);
            html += '<div style="' + style + '"><span style="color: #8899a6;">' +
                translatedDay + ':</span> ' + times + '</div>';
        });

        html += '</div>';
        container.innerHTML = html;
    }

    /**
     * Parse mass times from liturgy object
     */
    function parseMassTimes(liturgy) {
        var masses = [];

        // Try JSON format first
        if (liturgy.mass_times_json) {
            try {
                masses = typeof liturgy.mass_times_json === 'string'
                    ? JSON.parse(liturgy.mass_times_json)
                    : liturgy.mass_times_json;
            } catch (e) {
                masses = [];
            }
        }

        // Fallback: parse CSV description format
        if (masses.length === 0 && liturgy.mass_times && liturgy.mass_times.indexOf('|') !== -1) {
            masses = parseDescriptionToMasses(liturgy.mass_times);
        }

        return masses;
    }

    /**
     * Parse description text to mass objects
     * Format: "Est. 1923 | Sat Vigil: 5:00 PM (Spanish) | Sun: 6:30 AM, 7:00 AM (English)"
     */
    function parseDescriptionToMasses(description) {
        var masses = [];
        if (!description) return masses;

        var parts = description.split('|').map(function(p) { return p.trim(); });

        parts.forEach(function(part) {
            // Skip establishment year and other entries
            if (part.toLowerCase().indexOf('est.') === 0) return;
            if (part.toLowerCase().indexOf('confession') !== -1) return;
            if (part.toLowerCase().indexOf('adoration') !== -1) return;

            // Match "Day: times" format
            var match = part.match(/^(Sat(?:urday)?\s*Vigil|Sun(?:day)?|Weekday|Daily|Mon|Tue|Wed|Thu|Fri|Sat):\s*(.+)$/i);
            if (!match) return;

            var dayType = match[1].toLowerCase();
            var timesStr = match[2];

            // Determine mass type
            var type = 'weekday';
            if (dayType.indexOf('vigil') !== -1) type = 'vigil';
            else if (dayType.indexOf('sun') === 0) type = 'sunday';
            else if (dayType === 'sat') type = 'vigil';

            // Parse individual times
            var timeParts = timesStr.split(',');
            timeParts.forEach(function(timePart) {
                var timeMatch = timePart.trim().match(/^(\d{1,2}:\d{2}\s*(?:AM|PM)?)/i);
                if (timeMatch) {
                    var time = timeMatch[1].trim();
                    time = time.replace(/\s*(am|pm)/i, function(m) {
                        return ' ' + m.trim().toUpperCase();
                    });
                    masses.push({ time: time, type: type });
                }
            });
        });

        return masses;
    }

    /**
     * Get next upcoming mass time
     */
    function getNextMass(liturgy) {
        var masses = parseMassTimes(liturgy);
        if (masses.length === 0) return null;

        var now = new Date();
        var currentDay = now.getDay();
        var currentHour = now.getHours();
        var currentMin = now.getMinutes();
        var currentMins = currentHour * 60 + currentMin;
        var dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

        var typeToDay = function(type) {
            if (type === 'sunday') return [0];
            if (type === 'vigil') return [6];
            if (type === 'weekday') return [1, 2, 3, 4, 5];
            return [];
        };

        var parseTime = function(timeStr) {
            if (!timeStr) return null;
            var match24 = timeStr.match(/^(\d{1,2}):(\d{2})$/);
            if (match24) {
                return parseInt(match24[1], 10) * 60 + parseInt(match24[2], 10);
            }
            var match12 = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
            if (match12) {
                var h = parseInt(match12[1], 10);
                var m = parseInt(match12[2], 10);
                var ampm = match12[3].toUpperCase();
                if (ampm === 'PM' && h !== 12) h += 12;
                if (ampm === 'AM' && h === 12) h = 0;
                return h * 60 + m;
            }
            return null;
        };

        var nextMass = null;
        var minDiff = Infinity;

        masses.forEach(function(m) {
            var days = typeToDay(m.type);
            var timeMins = parseTime(m.time);
            if (timeMins === null) return;

            days.forEach(function(day) {
                var dayDiff = day - currentDay;
                var totalMins = dayDiff * 24 * 60 + (timeMins - currentMins);

                if (totalMins < 0) {
                    totalMins += 7 * 24 * 60;
                    dayDiff += 7;
                }

                if (totalMins < minDiff) {
                    minDiff = totalMins;
                    var isToday = dayDiff === 0 || (dayDiff === 7 && timeMins > currentMins);
                    var isTomorrow = dayDiff === 1;

                    var dayLabel = dayNames[day];
                    if (isToday) dayLabel = Utils.translate('today', 'Today');
                    else if (isTomorrow) dayLabel = Utils.translate('tomorrow', 'Tomorrow');

                    nextMass = {
                        day: dayLabel,
                        time: Utils.formatTime12Hour(m.time),
                        language: m.language,
                        isToday: isToday,
                        isTomorrow: isTomorrow
                    };
                }
            });
        });

        if (!nextMass) return null;

        var display = nextMass.day + ' ' + nextMass.time;
        if (nextMass.language && nextMass.language !== 'English') {
            display += ' (' + nextMass.language + ')';
        }

        return { display: display, isToday: nextMass.isToday, isTomorrow: nextMass.isTomorrow };
    }

    /**
     * Format confession times compactly
     */
    function formatConfessionCompact(jsonStr) {
        try {
            var confessions = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
            if (!Array.isArray(confessions) || confessions.length === 0) return null;

            var timeGroups = {};
            confessions.forEach(function(c) {
                var timeStr = '';
                if (c.start_time && c.end_time) {
                    timeStr = Utils.formatTime12Hour(c.start_time) + '-' + Utils.formatTime12Hour(c.end_time);
                } else if (c.start_time) {
                    timeStr = Utils.formatTime12Hour(c.start_time);
                }
                if (c.notes) timeStr += ' (' + c.notes + ')';

                if (!timeGroups[timeStr]) timeGroups[timeStr] = [];
                timeGroups[timeStr].push(c.day);
            });

            var parts = Object.keys(timeGroups).map(function(time) {
                var days = timeGroups[time];
                if (days.length >= 5) return time;
                if (days.indexOf('Saturday') !== -1 && days.length === 1) return 'Sat ' + time;
                return time;
            });

            return parts.join(' | ');
        } catch (e) {
            return null;
        }
    }

    /**
     * Format adoration times compactly
     */
    function formatAdorationCompact(jsonStr) {
        try {
            var adoration = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
            if (!Array.isArray(adoration) || adoration.length === 0) return null;

            var parts = adoration.slice(0, 2).map(function(a) {
                var str = '';
                if (a.day) str += a.day + ': ';
                if (a.start_time && a.end_time) {
                    str += Utils.formatTime12Hour(a.start_time) + '-' + Utils.formatTime12Hour(a.end_time);
                } else if (a.start_time) {
                    str += Utils.formatTime12Hour(a.start_time);
                }
                return str;
            });

            var result = parts.join(' | ');
            if (adoration.length > 2) result += ' +more';
            return result;
        } catch (e) {
            return null;
        }
    }

    // Public API
    window.MapLiturgy = {
        fetchLiturgyData: fetchLiturgyData,
        renderLiturgyData: renderLiturgyData,
        getNextMass: getNextMass,
        parseMassTimes: parseMassTimes,
        parseDescriptionToMasses: parseDescriptionToMasses,
        formatConfessionCompact: formatConfessionCompact,
        formatAdorationCompact: formatAdorationCompact
    };

})(window);


// === search.js ===
/**
 * MapSearch - Autocomplete search module
 *
 * Handles search input with favicon previews and keyboard navigation.
 * Depends on: MapState, MapUtils
 */
(function(window) {
    'use strict';

    var State = window.MapState;
    var Utils = window.MapUtils;
    var log = State.log;

    var autocompleteContainer = null;
    var selectedIndex = -1;
    var debounceTimer = null;
    var currentResults = [];

    /**
     * Get favicon URL for a church
     */
    function getChurchFavicon(church) {
        if (!church.website) return null;

        var domain;
        try {
            var url = church.website.indexOf('http') === 0 ? church.website : 'https://' + church.website;
            domain = new URL(url).hostname;
        } catch (e) {
            domain = church.website.replace(/^https?:\/\//, '').split('/')[0];
        }

        // Try local favicon first
        var localPath = '/assets/favicons/' + domain.replace(/^www\./, '') + '.png';
        return localPath;
    }

    /**
     * Get fallback favicon URL (Google's favicon service)
     */
    function getFallbackFavicon(church) {
        if (!church.website) return null;

        var domain;
        try {
            var url = church.website.indexOf('http') === 0 ? church.website : 'https://' + church.website;
            domain = new URL(url).hostname;
        } catch (e) {
            domain = church.website.replace(/^https?:\/\//, '').split('/')[0];
        }

        return 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://' + domain + '&size=32';
    }

    /**
     * Create autocomplete container if not exists
     */
    function createAutocompleteContainer(searchInput) {
        if (autocompleteContainer) return;

        autocompleteContainer = document.createElement('div');
        autocompleteContainer.id = 'searchAutocomplete';
        autocompleteContainer.className = 'search-autocomplete';
        searchInput.parentNode.style.position = 'relative';
        searchInput.parentNode.appendChild(autocompleteContainer);
    }

    /**
     * Perform search and render results
     */
    function performSearch(query) {
        if (!query || query.length < 2) {
            hideAutocomplete();
            return;
        }

        if (isZipQuery(query)) {
            hideAutocomplete();
            return;
        }

        var allChurches = State.get('allChurches') || [];
        var term = query.toLowerCase();

        currentResults = allChurches
            .filter(function(c) {
                return (c.name && c.name.toLowerCase().indexOf(term) !== -1) ||
                       (c.city && c.city.toLowerCase().indexOf(term) !== -1) ||
                       (c.state && c.state.toLowerCase().indexOf(term) !== -1);
            })
            .slice(0, 8);

        if (currentResults.length === 0) {
            hideAutocomplete();
            return;
        }

        renderAutocomplete();
    }

    function isZipQuery(query) {
        return /^\d{5}(-\d{4})?$/.test(query);
    }

    /**
     * Render autocomplete dropdown
     */
    function renderAutocomplete() {
        var searchInput = document.getElementById('searchInput');
        if (!searchInput) return;

        createAutocompleteContainer(searchInput);
        selectedIndex = -1;

        var currentLang = Utils.getCurrentLanguage();
        var html = currentResults.map(function(church, i) {
            var faviconUrl = getChurchFavicon(church);
            var fallbackUrl = getFallbackFavicon(church);
            var defaultEmoji = Utils.getProgramEmoji(church.denomination || 'Catholic');
            var displayName = Utils.translateChurchName(church.name, currentLang);

            var faviconHtml;
            if (faviconUrl) {
                faviconHtml = '<img src="' + faviconUrl + '" class="autocomplete-favicon"' +
                    ' onerror="this.onerror=null; this.src=\'' + (fallbackUrl || '') + '\'; this.onerror=function(){this.style.display=\'none\';this.nextElementSibling.style.display=\'flex\';};">' +
                    '<span class="autocomplete-fallback" style="display:none;">' + defaultEmoji + '</span>';
            } else {
                faviconHtml = '<span class="autocomplete-fallback">' + defaultEmoji + '</span>';
            }

            return '<div class="autocomplete-item" data-index="' + i + '">' +
                '<div class="autocomplete-logo">' + faviconHtml + '</div>' +
                '<div class="autocomplete-info">' +
                    '<div class="autocomplete-name">' + Utils.escapeHtml(displayName) + '</div>' +
                    '<div class="autocomplete-location">' + Utils.escapeHtml(church.city) + ', ' + Utils.escapeHtml(church.state) + '</div>' +
                '</div>' +
            '</div>';
        }).join('');

        autocompleteContainer.innerHTML = html;
        autocompleteContainer.style.display = 'block';

        // Add click handlers
        var items = autocompleteContainer.querySelectorAll('.autocomplete-item');
        for (var i = 0; i < items.length; i++) {
            (function(item) {
                item.addEventListener('click', function() {
                    var idx = parseInt(this.getAttribute('data-index'), 10);
                    selectResult(idx);
                });
                item.addEventListener('mouseenter', function() {
                    selectedIndex = parseInt(this.getAttribute('data-index'), 10);
                    updateSelectedState();
                });
            })(items[i]);
        }
    }

    /**
     * Update visual selection state
     */
    function updateSelectedState() {
        if (!autocompleteContainer) return;

        var items = autocompleteContainer.querySelectorAll('.autocomplete-item');
        for (var i = 0; i < items.length; i++) {
            if (i === selectedIndex) {
                items[i].classList.add('selected');
            } else {
                items[i].classList.remove('selected');
            }
        }
    }

    /**
     * Select a result by index
     */
    function selectResult(index) {
        var church = currentResults[index];
        if (!church) return;

        var searchInput = document.getElementById('searchInput');
        if (searchInput) {
            searchInput.value = church.name;
        }

        hideAutocomplete();

        // Zoom to church
        var map = State.get('map');
        if (map && church.lat && church.lon) {
            map.setView([church.lat, church.lon], 14);
        }

        // Select the church (open popup, highlight)
        if (window.MapSelection && window.MapSelection.selectItem) {
            window.MapSelection.selectItem(church.name, church.city);
        } else if (window.selectItem) {
            window.selectItem(church.name, church.city);
        }

        // Track analytics
        if (window.trackEvent) {
            window.trackEvent('search_select', {
                church_name: church.name,
                church_city: church.city
            });
        }
    }

    /**
     * Hide autocomplete dropdown
     */
    function hideAutocomplete() {
        if (autocompleteContainer) {
            autocompleteContainer.style.display = 'none';
        }
        currentResults = [];
        selectedIndex = -1;
    }

    /**
     * Initialize search autocomplete
     */
    function init() {
        var searchInput = document.getElementById('searchInput');
        if (!searchInput) {
            log('MapSearch: searchInput not found');
            return;
        }

        log('MapSearch: Initializing autocomplete');

        // Input handler with debounce
        searchInput.addEventListener('input', function(e) {
            var value = e.target.value;

            // Apply filters (existing behavior)
            if (window.MapFilters && window.MapFilters.applyFilters) {
                window.MapFilters.applyFilters();
            }

            // Debounced autocomplete
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(function() {
                performSearch(value);
            }, 150);
        });

        // Keyboard navigation
        searchInput.addEventListener('keydown', function(e) {
            if (!autocompleteContainer || autocompleteContainer.style.display === 'none') return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                selectedIndex = Math.min(selectedIndex + 1, currentResults.length - 1);
                updateSelectedState();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                selectedIndex = Math.max(selectedIndex - 1, 0);
                updateSelectedState();
            } else if (e.key === 'Enter' && selectedIndex >= 0) {
                e.preventDefault();
                selectResult(selectedIndex);
            } else if (e.key === 'Escape') {
                hideAutocomplete();
            }
        });

        // Close on click outside
        document.addEventListener('click', function(e) {
            if (!searchInput.contains(e.target) &&
                (!autocompleteContainer || !autocompleteContainer.contains(e.target))) {
                hideAutocomplete();
            }
        });

        // Focus shows results if available
        searchInput.addEventListener('focus', function() {
            if (currentResults.length > 0) {
                renderAutocomplete();
            }
        });
    }

    // Public API
    window.MapSearch = {
        init: init,
        performSearch: performSearch,
        hideAutocomplete: hideAutocomplete
    };

})(window);


// === selection.js ===
/**
 * MapSelection - Church selection and detail panel module
 *
 * Handles church selection, detail panel display, and nearby churches.
 * Depends on: MapState, MapUtils
 */
(function(window) {
    'use strict';

    // Constants
    var POPUP_DELAY_MOBILE_MS = 200;
    var POPUP_DELAY_DESKTOP_MS = 350;

    var State = window.MapState;
    var Utils = window.MapUtils;
    var log = State.log;

    function isMobile() {
        return State.isMobile();
    }

    /**
     * Generate composite key for unique church identification
     */
    function getChurchKey(name, city) {
        return name + '_' + city;
    }

    /**
     * Select a church by name and city
     * @param {string} churchName - Church name
     * @param {string} [churchCity] - Church city (optional for backwards compatibility)
     */
    function selectItem(churchName, churchCity) {
        var allChurches = State.get('allChurches') || [];
        var map = State.get('map');

        // Find the church
        var church = null;
        for (var i = 0; i < allChurches.length; i++) {
            var c = allChurches[i];
            if (c.name === churchName) {
                // If city provided, match both; otherwise take first match (backwards compat)
                if (!churchCity || c.city === churchCity) {
                    church = c;
                    break;
                }
            }
        }

        if (!church) {
            log('MapSelection: Church not found:', churchName, churchCity);
            return;
        }

        State.set('selectedItem', church);
        log('MapSelection: Selected', church.name, church.city);

        // Set selecting flag to prevent marker re-render during map animation
        State.set('isSelecting', true);

        // Center map on church
        if (map && church.lat && church.lon) {
            map.setView([church.lat, church.lon], Math.max(map.getZoom(), 13));
        }

        // Mobile: faster popup, don't auto-open sidebar
        var popupDelay = isMobile() ? POPUP_DELAY_MOBILE_MS : POPUP_DELAY_DESKTOP_MS;

        // Open popup after map animation settles
        var markerKey = getChurchKey(church.name, church.city);
        setTimeout(function() {
            var currentMarkers = State.get('itemMarkers') || {};
            if (currentMarkers[markerKey]) {
                currentMarkers[markerKey].openPopup();
            }
            State.set('isSelecting', false);
        }, popupDelay);

        // Show detail panel on desktop; mobile uses popup only
        if (!isMobile()) {
            showSelectedPanel(church);
        }

        // Highlight sidebar card
        highlightSidebarCard(church.name, church.city);

        // Track analytics
        if (window.trackEvent) {
            window.trackEvent('church_select', {
                church_name: church.name,
                church_city: church.city,
                source: 'map'
            });
        }
    }

    /**
     * Highlight the selected church in the sidebar
     */
    function highlightSidebarCard(churchName, churchCity) {
        // Remove previous highlights
        var cards = document.querySelectorAll('.item-card');
        for (var i = 0; i < cards.length; i++) {
            cards[i].classList.remove('selected');
            cards[i].style.borderColor = '';
        }

        // Highlight selected card using composite key if city provided
        var selector = '.item-card[data-name="' + Utils.escapeHtml(churchName) + '"]';
        if (churchCity) {
            selector += '[data-city="' + Utils.escapeHtml(churchCity) + '"]';
        }
        var selectedCard = document.querySelector(selector);
        if (selectedCard) {
            selectedCard.classList.add('selected');
            selectedCard.style.borderColor = 'var(--color-primary)';
            selectedCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    /**
     * Show the selected church detail panel
     */
    function showSelectedPanel(church) {
        var detailSheet = document.getElementById('detailSheet');
        if (!detailSheet) {
            log('MapSelection: detailSheet not found');
            return;
        }

        var currentLang = Utils.getCurrentLanguage();
        var displayName = Utils.translateChurchName(church.name, currentLang);
        var isAnglican = State.isAnglican();

        // Build panel content
        var titleEl = document.getElementById('detailSheetTitle');
        var infoEl = document.getElementById('detailSheetInfo');
        var actionsEl = document.getElementById('detailSheetActions');

        if (titleEl) {
            titleEl.textContent = displayName;
        }

        if (infoEl) {
            var infoHtml = buildInfoSection(church, isAnglican);
            infoEl.innerHTML = infoHtml;
        }

        if (actionsEl) {
            var actionsHtml = buildActionsSection(church);
            actionsEl.innerHTML = actionsHtml;
        }

        // Show detail sheet with slide animation
        detailSheet.classList.add('visible');
        
        // Add class to wrapper to trigger filter sheet slide
        var wrapper = detailSheet.parentElement;
        if (wrapper && wrapper.classList.contains('sidebar-content-wrapper')) {
            wrapper.classList.add('detail-visible');
        }
    }

    /**
     * Parse established year from description
     */
    function parseEstablishedYear(description) {
        if (!description) return null;
        var match = description.match(/Est\.\s*(\d{4})/i);
        return match ? match[1] : null;
    }

    /**
     * Format mass times for preview display
     */
    function formatMassTimesPreview(massTimesStr) {
        if (!massTimesStr) return null;
        // Truncate long mass times strings
        var preview = massTimesStr.trim();
        if (preview.length > 80) {
            preview = preview.substring(0, 77) + '...';
        }
        return preview;
    }

    /**
     * Build the info section HTML
     */
    function buildInfoSection(church, isAnglican) {
        var html = '';

        // Established year (if available)
        var establishedYear = parseEstablishedYear(church.description);
        if (establishedYear) {
            html += '<div class="selected-info-item established-year">' +
                '<span class="info-icon">🏛️</span>' +
                '<span>Est. ' + establishedYear + '</span>' +
            '</div>';
        }

        // Location
        html += '<div class="selected-info-item">' +
            '<span class="info-icon">📍</span>' +
            '<span>' + Utils.escapeHtml(church.city) + ', ' + Utils.escapeHtml(church.state) + '</span>' +
        '</div>';

        // Website
        if (church.website) {
            var websiteUrl = Utils.buildWebsiteUrl(church.website);
            var websiteDisplay = church.website.replace(/^https?:\/\//, '').replace(/\/$/, '');
            if (websiteDisplay.length > 35) {
                websiteDisplay = websiteDisplay.substring(0, 35) + '...';
            }
            html += '<div class="selected-info-item">' +
                '<span class="info-icon">🌐</span>' +
                '<a href="' + Utils.escapeHtml(websiteUrl) + '" target="_blank" rel="noopener noreferrer">' +
                Utils.escapeHtml(websiteDisplay) + '</a>' +
            '</div>';
        }

        // Mass times preview
        var timesLabel = isAnglican ? 'Services' : 'Mass';
        if (church.mass_times) {
            var massPreview = formatMassTimesPreview(church.mass_times);
            html += '<div class="selected-info-item mass-times-preview">' +
                '<span class="info-icon">⛪</span>' +
                '<span class="mass-preview-text">' + timesLabel + ': ' + Utils.escapeHtml(massPreview) + '</span>' +
            '</div>';
        }

        // Confession times preview
        if (church.confession_times) {
            var confessionPreview = formatMassTimesPreview(church.confession_times);
            html += '<div class="selected-info-item confession-preview">' +
                '<span class="info-icon">✝️</span>' +
                '<span>Confession: ' + Utils.escapeHtml(confessionPreview) + '</span>' +
            '</div>';
        }

        // Adoration indicator
        if (church.has_adoration || church.adoration_times) {
            var adorationText = church.adoration_times || 'Available';
            var adorationPreview = formatMassTimesPreview(adorationText);
            html += '<div class="selected-info-item adoration-preview">' +
                '<span class="info-icon">🕯️</span>' +
                '<span>Adoration: ' + Utils.escapeHtml(adorationPreview) + '</span>' +
            '</div>';
        }

        // Nearby churches section (increased from 10 to 20)
        var nearbyChurches = findNearbyChurches(church, 20);
        if (nearbyChurches.length > 0) {
            html += '<div class="selected-section nearby-section" data-state="collapsed">' +
                '<button class="selected-section-header" onclick="MapSelection.toggleSection(this)">' +
                    '<span>🎯 Nearby Churches (' + nearbyChurches.length + ')</span>' +
                    '<span class="section-toggle-icon">▶</span>' +
                '</button>' +
                '<div class="nearby-preview">' +
                    buildNearbyPreview(nearbyChurches.slice(0, 2)) +
                '</div>' +
                '<div class="selected-section-content">' +
                    buildNearbyList(nearbyChurches) +
                '</div>' +
            '</div>';
        }

        return html;
    }

    /**
     * Find nearby churches
     */
    function findNearbyChurches(church, limit) {
        var allChurches = State.get('allChurches') || [];

        var nearby = [];
        for (var i = 0; i < allChurches.length; i++) {
            var c = allChurches[i];
            if (c.name === church.name) continue;

            var distance = Utils.haversineDistance(church.lat, church.lon, c.lat, c.lon);
            nearby.push({
                name: c.name,
                city: c.city,
                state: c.state,
                distance: distance,
                lat: c.lat,
                lon: c.lon
            });
        }

        // Sort by distance
        nearby.sort(function(a, b) {
            return a.distance - b.distance;
        });

        return nearby.slice(0, limit);
    }

    /**
     * Build nearby preview HTML (collapsed view)
     */
    function buildNearbyPreview(churches) {
        return churches.map(function(c) {
            var distanceText = Utils.formatDistance(c.distance);
            return '<div class="nearby-preview-item" onclick="MapSelection.selectItem(\'' + Utils.escapeAttr(c.name) + '\', \'' + Utils.escapeAttr(c.city) + '\')">' +
                '<span class="preview-name">' + Utils.escapeHtml(c.name) + '</span>' +
                '<span class="preview-distance">• ' + distanceText + '</span>' +
            '</div>';
        }).join('');
    }

    /**
     * Build full nearby list HTML (expanded view)
     */
    function buildNearbyList(churches) {
        return '<div class="nearby-list">' +
            churches.map(function(c) {
                var distanceText = Utils.formatDistance(c.distance);
                return '<div class="nearby-item" onclick="MapSelection.selectItem(\'' + Utils.escapeAttr(c.name) + '\', \'' + Utils.escapeAttr(c.city) + '\')">' +
                    '<div class="nearby-name">' + Utils.escapeHtml(c.name) + '</div>' +
                    '<div class="nearby-meta">' +
                        '<span class="nearby-location">' + Utils.escapeHtml(c.city) + '</span>' +
                        '<span class="nearby-distance">' + distanceText + '</span>' +
                    '</div>' +
                '</div>';
            }).join('') +
        '</div>';
    }

    /**
     * Build the actions section HTML
     */
    function buildActionsSection(church) {
        var html = '';
        var slug = Utils.generateSlug(church.name, church.city);
        var churchUrl = Utils.buildLocalizedUrl('/church/' + slug);
        var isAnglican = State.isAnglican();
        var timesLabel = isAnglican ? 'Service Times' : 'Mass Times';

        // Primary action - View details
        html += '<a href="' + churchUrl + '" class="selected-btn primary">' +
            '📅 ' + timesLabel +
        '</a>';

        // Secondary actions
        if (church.website) {
            var websiteUrl = Utils.buildWebsiteUrl(church.website);
            html += '<button class="selected-btn" onclick="window.open(\'' + Utils.escapeAttr(websiteUrl) + '\', \'_blank\')">' +
                '🌐 Website' +
            '</button>';
        }

        // Directions
        if (church.lat && church.lon) {
            var directionsUrl = 'https://www.google.com/maps/dir/?api=1&destination=' + church.lat + ',' + church.lon;
            html += '<button class="selected-btn" onclick="window.open(\'' + directionsUrl + '\', \'_blank\')">' +
                '🧭 Directions' +
            '</button>';
        }

        // Close button
        html += '<button class="selected-btn close" onclick="MapSelection.close()">' +
            '✕ Close' +
        '</button>';

        return html;
    }

    /**
     * Create the selection panel DOM element
     * @deprecated No longer creates panel, detailSheet is in HTML
     * Kept for backwards compatibility
     */
    function createPanel() {
        // Detail sheet is now in HTML, just return reference
        return document.getElementById('detailSheet');
    }

    /**
     * Toggle collapsible section
     */
    function toggleSection(button) {
        var section = button.parentElement;
        var icon = button.querySelector('.section-toggle-icon');
        var currentState = section.getAttribute('data-state') || 'collapsed';

        if (currentState === 'collapsed') {
            section.setAttribute('data-state', 'expanded');
            if (icon) icon.textContent = '▼';
        } else {
            section.setAttribute('data-state', 'collapsed');
            if (icon) icon.textContent = '▶';
        }
    }

    /**
     * Close the selection panel
     */
    function close() {
        var detailSheet = document.getElementById('detailSheet');
        if (detailSheet) {
            detailSheet.classList.remove('visible');
            
            // Remove class from wrapper to restore filter sheet
            var wrapper = detailSheet.parentElement;
            if (wrapper && wrapper.classList.contains('sidebar-content-wrapper')) {
                wrapper.classList.remove('detail-visible');
            }
        }

        // Also check for old panel ID for backwards compatibility
        var oldPanel = document.getElementById('selectedChurchPanel');
        if (oldPanel) {
            oldPanel.classList.remove('visible');
        }

        State.set('selectedItem', null);

        // Remove sidebar card highlight
        var cards = document.querySelectorAll('.item-card');
        for (var i = 0; i < cards.length; i++) {
            cards[i].classList.remove('selected');
            cards[i].style.borderColor = '';
        }

        // On mobile, close sidebar when detail panel closes
        if (isMobile()) {
            var sidebar = document.getElementById('sidebar');
            if (sidebar && sidebar.classList.contains('active') && window.MapSidebar) {
                window.MapSidebar.toggleSidebar();
            }
        }
    }

    /**
     * Focus on a church marker (zoom + open popup)
     * @param {string} churchName - Church name
     * @param {string} [churchCity] - Church city (optional for backwards compatibility)
     */
    function focusItemOnMap(churchName, churchCity) {
        var allChurches = State.get('allChurches') || [];
        var map = State.get('map');

        var church = null;
        for (var i = 0; i < allChurches.length; i++) {
            var c = allChurches[i];
            if (c.name === churchName && (!churchCity || c.city === churchCity)) {
                church = c;
                break;
            }
        }

        if (!church || !map) return;

        State.set('isSelecting', true);
        map.setView([church.lat, church.lon], 15);

        // Open popup after map animation settles
        var popupDelay = isMobile() ? POPUP_DELAY_MOBILE_MS : POPUP_DELAY_DESKTOP_MS;
        var markerKey = getChurchKey(church.name, church.city);
        setTimeout(function() {
            var currentMarkers = State.get('itemMarkers') || {};
            if (currentMarkers[markerKey]) {
                currentMarkers[markerKey].openPopup();
            }
            State.set('isSelecting', false);
        }, popupDelay);
    }

    // Public API
    window.MapSelection = {
        selectItem: selectItem,
        close: close,
        toggleSection: toggleSection,
        focusItemOnMap: focusItemOnMap,
        showSelectedPanel: showSelectedPanel,
        findNearbyChurches: findNearbyChurches,
        getChurchKey: getChurchKey
    };

    // Global aliases for backwards compatibility
    window.selectItem = selectItem;
    window.focusItemOnMap = focusItemOnMap;
    window.getChurchKey = getChurchKey;

})(window);


// === themes.js ===
/**
 * MapThemes - Color theme management
 *
 * Provides color schemes for map visualizations.
 */
(function (window) {
    'use strict';

    var STORAGE_KEY = 'ubimissa_map_theme';
    var currentTheme = 'plasma';

    // Color scheme definitions - each is an array of color stops
    var THEMES = {
        plasma: {
            name: 'Plasma',
            description: 'Purple to Yellow (Default)',
            stops: [
                { pos: 0.0, r: 13, g: 8, b: 135 },
                { pos: 0.15, r: 75, g: 3, b: 161 },
                { pos: 0.3, r: 137, g: 8, b: 165 },
                { pos: 0.45, r: 192, g: 58, b: 131 },
                { pos: 0.6, r: 234, g: 99, b: 82 },
                { pos: 0.75, r: 251, g: 159, b: 58 },
                { pos: 0.9, r: 249, g: 220, b: 92 },
                { pos: 1.0, r: 240, g: 249, b: 33 },
            ],
        },
        viridis: {
            name: 'Viridis',
            description: 'Purple to Green to Yellow',
            stops: [
                { pos: 0.0, r: 68, g: 1, b: 84 },
                { pos: 0.15, r: 72, g: 40, b: 120 },
                { pos: 0.3, r: 62, g: 74, b: 137 },
                { pos: 0.45, r: 49, g: 104, b: 142 },
                { pos: 0.6, r: 38, g: 130, b: 142 },
                { pos: 0.75, r: 53, g: 183, b: 121 },
                { pos: 0.9, r: 144, g: 215, b: 67 },
                { pos: 1.0, r: 253, g: 231, b: 37 },
            ],
        },
        inferno: {
            name: 'Inferno',
            description: 'Black to Red to Yellow',
            stops: [
                { pos: 0.0, r: 0, g: 0, b: 4 },
                { pos: 0.15, r: 40, g: 11, b: 84 },
                { pos: 0.3, r: 101, g: 21, b: 110 },
                { pos: 0.45, r: 159, g: 42, b: 99 },
                { pos: 0.6, r: 212, g: 72, b: 66 },
                { pos: 0.75, r: 245, g: 125, b: 21 },
                { pos: 0.9, r: 250, g: 193, b: 39 },
                { pos: 1.0, r: 252, g: 255, b: 164 },
            ],
        },
        blues: {
            name: 'Blues',
            description: 'Light to Dark Blue',
            stops: [
                { pos: 0.0, r: 247, g: 251, b: 255 },
                { pos: 0.15, r: 222, g: 235, b: 247 },
                { pos: 0.3, r: 198, g: 219, b: 239 },
                { pos: 0.45, r: 158, g: 202, b: 225 },
                { pos: 0.6, r: 107, g: 174, b: 214 },
                { pos: 0.75, r: 66, g: 146, b: 198 },
                { pos: 0.9, r: 33, g: 113, b: 181 },
                { pos: 1.0, r: 8, g: 69, b: 148 },
            ],
        },
        liturgical: {
            name: 'Liturgical',
            description: 'Gold to Purple (Sacred)',
            stops: [
                { pos: 0.0, r: 255, g: 255, b: 255 },
                { pos: 0.2, r: 255, g: 248, b: 220 },
                { pos: 0.4, r: 212, g: 175, b: 55 },
                { pos: 0.6, r: 128, g: 0, b: 128 },
                { pos: 0.8, r: 75, g: 0, b: 130 },
                { pos: 1.0, r: 48, g: 0, b: 48 },
            ],
        },
        heat: {
            name: 'Heat',
            description: 'Blue to Red (Classic)',
            stops: [
                { pos: 0.0, r: 0, g: 0, b: 128 },
                { pos: 0.25, r: 0, g: 128, b: 255 },
                { pos: 0.5, r: 0, g: 255, b: 128 },
                { pos: 0.75, r: 255, g: 255, b: 0 },
                { pos: 1.0, r: 255, g: 0, b: 0 },
            ],
        },
    };

    /**
     * Initialize theme from localStorage
     */
    function init() {
        var saved = localStorage.getItem(STORAGE_KEY);
        if (saved && THEMES[saved]) {
            currentTheme = saved;
        }
    }

    /**
     * Get color for intensity value (0-1)
     */
    function getColor(intensity, themeName) {
        var theme = THEMES[themeName || currentTheme] || THEMES.plasma;
        var stops = theme.stops;
        var t = Math.max(0, Math.min(1, intensity));

        var lower = stops[0];
        var upper = stops[stops.length - 1];

        for (var i = 0; i < stops.length - 1; i++) {
            if (t >= stops[i].pos && t <= stops[i + 1].pos) {
                lower = stops[i];
                upper = stops[i + 1];
                break;
            }
        }

        var range = upper.pos - lower.pos;
        var localT = range > 0 ? (t - lower.pos) / range : 0;

        var r = Math.round(lower.r + (upper.r - lower.r) * localT);
        var g = Math.round(lower.g + (upper.g - lower.g) * localT);
        var b = Math.round(lower.b + (upper.b - lower.b) * localT);

        return 'rgb(' + r + ',' + g + ',' + b + ')';
    }

    /**
     * Get color as RGB object
     */
    function getColorRGB(intensity, themeName) {
        var color = getColor(intensity, themeName);
        var match = color.match(/rgb\((\d+),(\d+),(\d+)\)/);
        if (match) {
            return { r: parseInt(match[1]), g: parseInt(match[2]), b: parseInt(match[3]) };
        }
        return { r: 128, g: 128, b: 128 };
    }

    /**
     * Get color as hex string
     */
    function getColorHex(intensity, themeName) {
        var rgb = getColorRGB(intensity, themeName);
        var toHex = function (c) {
            var hex = c.toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };
        return '#' + toHex(rgb.r) + toHex(rgb.g) + toHex(rgb.b);
    }

    /**
     * Set current theme
     */
    function setTheme(themeName) {
        if (THEMES[themeName]) {
            currentTheme = themeName;
            localStorage.setItem(STORAGE_KEY, themeName);

            // Dispatch event for other modules to react
            window.dispatchEvent(
                new CustomEvent('themeChanged', {
                    detail: { theme: themeName },
                })
            );

            return true;
        }
        return false;
    }

    /**
     * Get current theme name
     */
    function getTheme() {
        return currentTheme;
    }

    /**
     * Get all available themes
     */
    function getThemes() {
        var result = [];
        for (var key in THEMES) {
            if (THEMES.hasOwnProperty(key)) {
                result.push({
                    id: key,
                    name: THEMES[key].name,
                    description: THEMES[key].description,
                });
            }
        }
        return result;
    }

    /**
     * Generate CSS gradient for legend
     */
    function getGradientCSS(themeName, direction) {
        var theme = THEMES[themeName || currentTheme] || THEMES.plasma;
        var stops = theme.stops;
        var dir = direction || 'to right';

        var gradientStops = stops.map(function (stop) {
            return 'rgb(' + stop.r + ',' + stop.g + ',' + stop.b + ') ' + Math.round(stop.pos * 100) + '%';
        });

        return 'linear-gradient(' + dir + ', ' + gradientStops.join(', ') + ')';
    }

    /**
     * Render theme selector UI
     */
    function renderThemeSelector(containerId) {
        var container = document.getElementById(containerId);
        if (!container) return;

        var themes = getThemes();
        var html =
            '<div class="theme-selector">' +
            '<label class="theme-label">Color Theme</label>' +
            '<div class="theme-options">';

        themes.forEach(function (theme) {
            var isActive = theme.id === currentTheme ? ' active' : '';
            var gradient = getGradientCSS(theme.id);
            html +=
                '<button class="theme-btn' +
                isActive +
                '" data-theme="' +
                theme.id +
                '" title="' +
                theme.description +
                '">' +
                '<span class="theme-preview" style="background: ' +
                gradient +
                ';"></span>' +
                '<span class="theme-name">' +
                theme.name +
                '</span>' +
                '</button>';
        });

        html += '</div></div>';
        container.innerHTML = html;

        // Add click handlers
        container.querySelectorAll('.theme-btn').forEach(function (btn) {
            btn.addEventListener('click', function () {
                var themeName = this.getAttribute('data-theme');
                setTheme(themeName);

                // Update active state
                container.querySelectorAll('.theme-btn').forEach(function (b) {
                    b.classList.remove('active');
                });
                this.classList.add('active');

                // Refresh current view
                if (window.MapViews) {
                    window.MapViews.updateMapView();
                }
            });
        });
    }

    // Initialize on load
    init();

    // Export
    window.MapThemes = {
        init: init,
        getColor: getColor,
        getColorRGB: getColorRGB,
        getColorHex: getColorHex,
        setTheme: setTheme,
        getTheme: getTheme,
        getThemes: getThemes,
        getGradientCSS: getGradientCSS,
        renderThemeSelector: renderThemeSelector,
    };

    // Global alias for onclick handlers
    window.setTheme = function(themeName) {
        setTheme(themeName);
        // Update active state in theme selector UI
        var options = document.querySelectorAll('.theme-option');
        options.forEach(function(opt) {
            opt.classList.toggle('active', opt.getAttribute('data-theme') === themeName);
        });
    };
})(window);


// === mobile-ux.js ===
/**
 * MobileUX - Mobile-specific UX enhancements
 *
 * Provides swipe-to-close sidebar gesture and first-time user hints.
 */
(function(window) {
    'use strict';

    // Constants
    var SWIPE_THRESHOLD_PX = 80;
    var SWIPE_MAX_TRANSLATE_PX = 200;
    var HINT_AUTO_DISMISS_MS = 5000;
    var HINT_STORAGE_KEY = 'ubimissa_map_hint_shown';

    var State = window.MapState;

    function isMobile() {
        return State ? State.isMobile() : window.innerWidth <= 768;
    }

    // ============================================
    // Touch Gesture Support - Swipe to close sidebar
    // ============================================
    var touchStartX = 0;
    var touchStartY = 0;
    var touchCurrentX = 0;
    var sidebarElement = null;
    var isDragging = false;

    function initTouchGestures() {
        sidebarElement = document.getElementById('sidebar');
        if (!sidebarElement) return;

        sidebarElement.addEventListener('touchstart', handleTouchStart, { passive: true });
        sidebarElement.addEventListener('touchmove', handleTouchMove, { passive: false });
        sidebarElement.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    function handleTouchStart(e) {
        if (!isMobile()) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchCurrentX = touchStartX;
        isDragging = false;
    }

    function handleTouchMove(e) {
        if (!isMobile()) return;
        if (!sidebarElement.classList.contains('active')) return;

        touchCurrentX = e.touches[0].clientX;
        var deltaX = touchCurrentX - touchStartX;
        var deltaY = e.touches[0].clientY - touchStartY;

        // Only track horizontal swipes (right swipe to close)
        if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX > 20) {
            isDragging = true;
            e.preventDefault();

            // Visual feedback - translate sidebar
            var translateX = Math.min(deltaX, SWIPE_MAX_TRANSLATE_PX);
            sidebarElement.style.transform = 'translateX(' + translateX + 'px)';
            sidebarElement.style.transition = 'none';
        }
    }

    function handleTouchEnd() {
        if (!isMobile() || !isDragging) {
            resetSidebarTransform();
            return;
        }

        var deltaX = touchCurrentX - touchStartX;

        if (deltaX > SWIPE_THRESHOLD_PX) {
            closeSidebar();
        } else {
            // Snap back with animation
            sidebarElement.style.transform = '';
            sidebarElement.style.transition = 'transform 0.2s ease-out';
        }

        isDragging = false;
    }

    function resetSidebarTransform() {
        if (sidebarElement) {
            sidebarElement.style.transform = '';
            sidebarElement.style.transition = '';
        }
    }

    function closeSidebar() {
        resetSidebarTransform();
        if (window.MapSidebar && window.MapSidebar.toggleSidebar) {
            window.MapSidebar.toggleSidebar();
        }
    }

    // ============================================
    // Map Interaction Hints
    // ============================================
    function showMapHint() {
        if (!isMobile()) return;
        if (localStorage.getItem(HINT_STORAGE_KEY)) return;

        var hint = document.createElement('div');
        hint.id = 'mapHint';
        hint.className = 'map-hint';
        hint.innerHTML =
            '<div class="map-hint-content">' +
                '<div class="map-hint-icon">👆</div>' +
                '<div class="map-hint-text">' +
                    '<strong>Pinch to zoom</strong><br>' +
                    'Tap a marker to see details' +
                '</div>' +
            '</div>' +
            '<button class="map-hint-dismiss" onclick="MobileUX.dismissHint()">Got it</button>';

        document.body.appendChild(hint);

        setTimeout(dismissHint, HINT_AUTO_DISMISS_MS);
    }

    function dismissHint() {
        var hint = document.getElementById('mapHint');
        if (hint) {
            hint.classList.add('dismissed');
            setTimeout(function() {
                if (hint.parentNode) {
                    hint.parentNode.removeChild(hint);
                }
            }, 300);
        }
        localStorage.setItem(HINT_STORAGE_KEY, 'true');
    }

    // ============================================
    // Initialization
    // ============================================
    function init() {
        if (!isMobile()) return;

        initTouchGestures();
        setTimeout(showMapHint, 1500);
    }

    // Auto-init
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        setTimeout(init, 100);
    }

    // Public API
    window.MobileUX = {
        init: init,
        dismissHint: dismissHint,
        closeSidebar: closeSidebar
    };
})(window);


// === viewport-loader.js ===
/**
 * Viewport-based progressive loading with aggregation
 * Loads detailed churches for viewport and aggregate clusters for outside areas
 */
(function(window) {
    'use strict';

    var State = window.MapState;
    var Utils = window.MapUtils;
    var log = State ? State.log : console.log;

    // Cache for loaded viewport data
    var viewportCache = {};
    var aggregateMarkers = [];
    var detailMarkers = [];
    var currentRequest = null;

    /**
     * Load viewport data with aggregates
     * @param {L.Map} map - Leaflet map instance
     * @param {Function} callback - Callback with loaded data
     */
    function loadViewportData(map, callback) {
        if (!map) return;

        var bounds = map.getBounds();
        var zoom = map.getZoom();

        // Create cache key from bounds (rounded to reduce cache misses)
        var cacheKey = [
            Math.round(bounds.getNorth() * 100),
            Math.round(bounds.getSouth() * 100),
            Math.round(bounds.getEast() * 100),
            Math.round(bounds.getWest() * 100),
            zoom
        ].join(',');

        // Check cache first
        if (viewportCache[cacheKey]) {
            log('Using cached viewport data');
            if (callback) callback(viewportCache[cacheKey]);
            return;
        }

        // Cancel previous request if pending
        if (currentRequest) {
            currentRequest.abort();
        }

        // Build viewport API URL
        var apiUrl = '/api/churches/viewport?' +
            'north=' + bounds.getNorth() +
            '&south=' + bounds.getSouth() +
            '&east=' + bounds.getEast() +
            '&west=' + bounds.getWest() +
            '&zoom=' + zoom;

        log('Loading viewport data from', apiUrl);

        // Create abort controller
        var controller = new AbortController();
        currentRequest = controller;

        fetch(apiUrl, { signal: controller.signal })
            .then(function(response) {
                return response.json();
            })
            .then(function(data) {
                if (data.success) {
                    // Cache the response
                    viewportCache[cacheKey] = data;

                    // Clear cache if too large (keep last 10 viewports)
                    var keys = Object.keys(viewportCache);
                    if (keys.length > 10) {
                        delete viewportCache[keys[0]];
                    }

                    log('Loaded', data.viewport.churches.length, 'viewport churches');
                    log('Loaded', data.aggregates.length, 'aggregate clusters');
                    log('Global total:', data.stats.totalGlobal);

                    if (callback) callback(data);
                } else {
                    console.error('Viewport API error:', data.error);
                }
                currentRequest = null;
            })
            .catch(function(error) {
                if (error.name !== 'AbortError') {
                    console.error('Failed to load viewport data:', error);
                }
                currentRequest = null;
            });
    }

    /**
     * Render hybrid map with viewport churches and aggregates
     * @param {Object} data - Response from viewport API
     * @param {L.Map} map - Leaflet map instance
     */
    function renderHybridMap(data, map) {
        if (!data || !map) {
            log('ERROR: renderHybridMap missing data or map', !!data, !!map);
            return;
        }

        log('renderHybridMap: Starting with', data.viewport.churches.length, 'churches,', data.aggregates.length, 'aggregates');

        // Clear previous markers
        clearAllMarkers(map);

        // Render detailed churches in viewport
        renderViewportChurches(data.viewport.churches, map);

        // Render aggregate clusters outside viewport
        renderAggregates(data.aggregates, map, data.zoom);

        // Update stats display
        updateStats(data.stats);

        log('renderHybridMap: Complete');
    }

    /**
     * Render detailed churches in viewport
     * @param {Array} churches - Churches to render
     * @param {L.Map} map - Leaflet map instance
     */
    function renderViewportChurches(churches, map) {
        if (!churches) {
            log('ERROR: No churches to render');
            return;
        }
        if (!window.MapMarkers) {
            log('ERROR: MapMarkers module not loaded');
            return;
        }

        // Always use cluster layer for viewport churches to get proper clustering
        var markersClusterLayer = State.get('markersClusterLayer');
        if (!markersClusterLayer) {
            log('ERROR: markersClusterLayer not available!');
            return;
        }

        log('renderViewportChurches: Processing', churches.length, 'churches');

        var successCount = 0;
        var failCount = 0;

        churches.forEach(function(church, index) {
            // Ensure church data has proper structure for marker creation
            var normalizedChurch = {
                id: church.id,
                name: church.church_name || church.name,
                lat: parseFloat(church.latitude || church.lat),
                lon: parseFloat(church.longitude || church.lng || church.lon),
                website: church.website,
                phone: church.phone,
                address: church.street_address || church.address,
                city: church.city,
                state: church.state,
                country: church.country || '',
                denomination: church.denomination || 'Catholic'
            };

            if (index < 3) {
                log('Church', index, ':', normalizedChurch.name, 'at', normalizedChurch.lat, normalizedChurch.lon);
            }

            var marker = window.MapMarkers.createChurchMarker(normalizedChurch);
            if (marker) {
                markersClusterLayer.addLayer(marker);
                detailMarkers.push(marker);
                successCount++;
            } else {
                failCount++;
                if (failCount <= 3) {
                    log('Failed to create marker for:', normalizedChurch.name);
                }
            }
        });

        log('renderViewportChurches: Created', successCount, 'markers, failed:', failCount);
    }

    /**
     * Render aggregate clusters for areas outside viewport
     * @param {Array} aggregates - Aggregate data
     * @param {L.Map} map - Leaflet map instance
     * @param {number} zoom - Current zoom level
     */
    function renderAggregates(aggregates, map, zoom) {
        if (!aggregates || !map) return;

        var markersLogoLayer = State.get('markersLogoLayer');
        if (!markersLogoLayer) return;

        aggregates.forEach(function(agg) {
            // Show all aggregates at global view, filter only at higher zoom
            // This ensures we see the full distribution of churches worldwide
            if (zoom > 8 && agg.count < 2) return;   // Only filter single churches at high zoom
            if (zoom > 10 && agg.count < 1) return;  // Never filter at local zoom

            var size = getAggregateSize(agg.count);
            var opacity = getAggregateOpacity(agg.count, zoom);

            // Create aggregate marker
            var aggregateIcon = L.divIcon({
                className: 'aggregate-cluster',
                html: createAggregateHtml(agg, size),
                iconSize: [size, size],
                iconAnchor: [size / 2, size / 2]
            });

            var marker = L.marker([agg.lat, agg.lng], {
                icon: aggregateIcon,
                opacity: opacity
            });

            // Add click handler to zoom into aggregate area
            marker.on('click', function() {
                // Calculate appropriate zoom to see this aggregate's churches
                var targetZoom = Math.min(map.getZoom() + 2, 15);

                // Animate to the aggregate center
                map.flyTo([agg.lat, agg.lng], targetZoom, {
                    duration: 0.5
                });
            });

            // Add tooltip
            marker.bindTooltip(
                '<strong>' + agg.count + ' churches</strong><br>' +
                'Click to zoom in',
                { direction: 'top', offset: [0, -size/2] }
            );

            markersLogoLayer.addLayer(marker);
            aggregateMarkers.push(marker);
        });

        log('Rendered', aggregates.length, 'aggregate clusters');
    }

    /**
     * Create HTML for aggregate cluster
     * @param {Object} aggregate - Aggregate data
     * @param {number} size - Cluster size
     * @returns {string} HTML string
     */
    function createAggregateHtml(aggregate, size) {
        var fontSize = size < 40 ? 12 : (size < 60 ? 14 : 16);
        var pulseSize = size * 1.5;

        return '<div style="' +
            'width:' + size + 'px;' +
            'height:' + size + 'px;' +
            'background:radial-gradient(circle, rgba(99,102,241,0.8) 0%, rgba(99,102,241,0.3) 70%);' +
            'border:2px solid rgba(255,255,255,0.8);' +
            'border-radius:50%;' +
            'display:flex;' +
            'align-items:center;' +
            'justify-content:center;' +
            'box-shadow:0 4px 12px rgba(99,102,241,0.4);' +
            'position:relative;' +
            '">' +
            // Pulse effect for large clusters
            (aggregate.count > 50 ?
                '<div style="' +
                'position:absolute;' +
                'width:' + pulseSize + 'px;' +
                'height:' + pulseSize + 'px;' +
                'border:2px solid rgba(99,102,241,0.5);' +
                'border-radius:50%;' +
                'animation:pulse 2s infinite;' +
                'top:50%;left:50%;' +
                'transform:translate(-50%,-50%);' +
                '"></div>' : '') +
            '<span style="' +
            'color:white;' +
            'font-weight:bold;' +
            'font-size:' + fontSize + 'px;' +
            'text-shadow:0 1px 3px rgba(0,0,0,0.5);' +
            '">' + aggregate.count + '</span>' +
            '</div>';
    }

    /**
     * Get size for aggregate based on count
     * @param {number} count - Number of churches
     * @returns {number} Size in pixels
     */
    function getAggregateSize(count) {
        // More granular sizing for better visibility of all clusters
        if (count < 5) return 25;
        if (count < 10) return 30;
        if (count < 25) return 35;
        if (count < 50) return 40;
        if (count < 100) return 45;
        if (count < 250) return 50;
        if (count < 500) return 60;
        if (count < 1000) return 70;
        return 80;
    }

    /**
     * Get opacity for aggregate based on count and zoom
     * @param {number} count - Number of churches
     * @param {number} zoom - Zoom level
     * @returns {number} Opacity value
     */
    function getAggregateOpacity(count, zoom) {
        // Higher opacity at global zoom for better visibility
        var baseOpacity = zoom <= 3 ? 0.9 : zoom <= 6 ? 0.8 : 0.7;

        // Smaller clusters need more opacity to be visible
        if (count < 10) baseOpacity = Math.min(baseOpacity + 0.2, 1);
        else if (count < 50) baseOpacity = Math.min(baseOpacity + 0.1, 1);

        // Larger clusters are already visible
        if (count > 500) baseOpacity = Math.min(baseOpacity + 0.05, 1);

        return Math.max(0.6, baseOpacity); // Minimum 0.6 opacity for visibility
    }

    /**
     * Clear all markers from map
     * @param {L.Map} map - Leaflet map instance
     */
    function clearAllMarkers(map) {
        var markersLogoLayer = State.get('markersLogoLayer');
        var markersClusterLayer = State.get('markersClusterLayer');

        // Clear cluster layer
        if (markersClusterLayer) {
            markersClusterLayer.clearLayers();
        }

        // Clear logo layer
        if (markersLogoLayer) {
            markersLogoLayer.clearLayers();
        }

        // Reset marker arrays
        detailMarkers = [];
        aggregateMarkers = [];
    }

    /**
     * Update stats display
     * @param {Object} stats - Statistics from API
     */
    function updateStats(stats) {
        var statsBadge = document.getElementById('statsBadge');
        if (!statsBadge) return;

        var content = '<div class="stats-content">' +
            '<div class="stat-item">' +
                '<span class="stat-label">Visible:</span> ' +
                '<span class="stat-value">' + stats.totalViewport + '</span>' +
            '</div>' +
            '<div class="stat-item">' +
                '<span class="stat-label">Nearby:</span> ' +
                '<span class="stat-value">' + stats.totalAggregated + '</span>' +
            '</div>' +
            '<div class="stat-item">' +
                '<span class="stat-label">Global:</span> ' +
                '<span class="stat-value">' + stats.totalGlobal.toLocaleString() + '</span>' +
            '</div>' +
            '</div>';

        statsBadge.innerHTML = content;
    }

    /**
     * Initialize viewport loading system
     * @param {L.Map} map - Leaflet map instance
     */
    function initViewportLoading(map) {
        if (!map) {
            log('ViewportLoader: Map not available, retrying in 500ms');
            setTimeout(function() {
                var retryMap = State.get('map');
                if (retryMap) {
                    initViewportLoading(retryMap);
                }
            }, 500);
            return;
        }

        log('ViewportLoader: Initializing with map center:', map.getCenter(), 'zoom:', map.getZoom());

        // Ensure the correct layers are added to the map
        var markersClusterLayer = State.get('markersClusterLayer');
        var markersLogoLayer = State.get('markersLogoLayer');

        if (!markersClusterLayer || !markersLogoLayer) {
            log('ERROR: Marker layers not initialized yet, retrying in 500ms');
            setTimeout(function() {
                initViewportLoading(map);
            }, 500);
            return;
        }

        // Remove any existing layers and re-add them
        // This ensures we're using both layers for viewport loading
        if (map.hasLayer(markersClusterLayer)) {
            map.removeLayer(markersClusterLayer);
        }
        if (map.hasLayer(markersLogoLayer)) {
            map.removeLayer(markersLogoLayer);
        }

        // Add both layers - cluster for churches, logo for aggregates
        map.addLayer(markersClusterLayer);
        map.addLayer(markersLogoLayer);
        log('ViewportLoader: Added both marker layers to map');

        // Set the correct active layers in state
        State.set('markersLayer', markersClusterLayer);
        State.set('useClustering', true);

        // Debounce function for map movement
        var loadDebounced = Utils && Utils.debounce ? Utils.debounce(function() {
            loadViewportData(map, function(data) {
                renderHybridMap(data, map);
            });
        }, 300) : function() {
            loadViewportData(map, function(data) {
                renderHybridMap(data, map);
            });
        };

        // Listen for map movement
        map.on('moveend', loadDebounced);
        map.on('zoomend', loadDebounced);

        // Initial load with a small delay to ensure map is fully ready
        setTimeout(function() {
            loadViewportData(map, function(data) {
                renderHybridMap(data, map);
            });
        }, 100);

        log('Viewport loading initialized');
    }

    // Add CSS for pulse animation
    var style = document.createElement('style');
    style.textContent = `
        @keyframes pulse {
            0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
            50% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
            100% { opacity: 0; transform: translate(-50%, -50%) scale(1.2); }
        }

        .aggregate-cluster {
            cursor: pointer;
            transition: transform 0.2s;
        }

        .aggregate-cluster:hover {
            transform: scale(1.1);
        }

        .stats-content {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .stat-item {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
        }

        .stat-label {
            opacity: 0.7;
        }

        .stat-value {
            font-weight: bold;
        }
    `;
    document.head.appendChild(style);

    // Public API
    window.ViewportLoader = {
        init: initViewportLoading,
        loadViewportData: loadViewportData,
        renderHybridMap: renderHybridMap,
        clearAllMarkers: clearAllMarkers
    };

})(window);


// === heat-layer.js ===
/**
 * MapHeatLayer - Traditional heatmap visualization
 *
 * Renders a continuous heat gradient using leaflet.heat library.
 * This is the blurry gradient heatmap, distinct from hexgrid and 3D bars.
 */
(function (window) {
    'use strict';

    var State = window.MapState;
    var log = State.log;

    var heatLayer = null;

    /**
     * Get heat intensity based on zoom level
     */
    function getIntensityForZoom(zoom) {
        if (zoom <= 4) return 0.3;
        if (zoom <= 6) return 0.4;
        if (zoom <= 8) return 0.5;
        if (zoom <= 10) return 0.6;
        return 0.7;
    }

    /**
     * Get radius based on zoom level - larger for better blending
     */
    function getRadiusForZoom(zoom) {
        if (zoom <= 3) return 10;
        if (zoom <= 4) return 15;
        if (zoom <= 5) return 20;
        if (zoom <= 6) return 25;
        if (zoom <= 7) return 30;
        if (zoom <= 8) return 35;
        if (zoom <= 10) return 40;
        return 50;
    }

    /**
     * Add heat layer to map
     */
    function addHeatLayerToMap() {
        var map = State.get('map');
        var filteredItems = State.get('filteredItems') || [];
        var allChurches = State.get('allChurches') || [];

        // Remove existing layer
        if (heatLayer && map.hasLayer(heatLayer)) {
            map.removeLayer(heatLayer);
        }

        // Use filtered data (respects active filters)
        var sourceData = filteredItems.length > 0 ? filteredItems : allChurches;

        if (sourceData.length === 0) {
            log('No data for heat layer');
            return;
        }

        // Check if leaflet.heat is available
        if (typeof L.heatLayer !== 'function') {
            log('leaflet.heat not loaded, falling back to hexgrid');
            if (window.MapHexgrid) {
                window.MapHexgrid.addHexGridToMap();
            }
            return;
        }

        var zoom = map.getZoom();
        var radius = getRadiusForZoom(zoom);
        var intensity = getIntensityForZoom(zoom);

        // Convert entities to heat points [lat, lng, intensity]
        var heatPoints = sourceData
            .filter(function (entity) {
                return entity.lat && entity.lon;
            })
            .map(function (entity) {
                return [entity.lat, entity.lon, intensity];
            });

        log('Creating heat layer with', heatPoints.length, 'points, radius:', radius);

        // Generate gradient from MapThemes if available
        var gradient;
        if (window.MapThemes && window.MapThemes.getColorHex) {
            gradient = {};
            // Sample at 0.05 intervals to get a smooth gradient
            for (var i = 0; i <= 20; i++) {
                var pos = parseFloat((i / 20).toFixed(2));
                gradient[pos] = window.MapThemes.getColorHex(pos);
            }
        } else {
            // Fallback: Blue -> Cyan -> Green -> Yellow -> Orange -> Red gradient
            gradient = {
                0.0: '#0000ff', // Blue - coldest/sparse
                0.15: '#0066ff', // Light blue
                0.25: '#00ccff', // Cyan
                0.35: '#00ff99', // Cyan-green
                0.45: '#00ff00', // Green
                0.55: '#66ff00', // Yellow-green
                0.65: '#ccff00', // Light yellow
                0.75: '#ffff00', // Yellow
                0.82: '#ffcc00', // Gold
                0.88: '#ff9900', // Orange
                0.94: '#ff6600', // Dark orange
                1.0: '#ff0000', // Red - hottest/dense
            };
        }

        heatLayer = L.heatLayer(heatPoints, {
            radius: radius,
            blur: Math.round(radius * 0.8), // More blur for smoother blending
            maxZoom: 14,
            max: 1.0,
            minOpacity: 0.5,
            gradient: gradient,
        });

        heatLayer.addTo(map);
        State.set('densityHeatLayer', heatLayer);

        log('Heat layer rendered');
    }

    /**
     * Update heat layer on zoom change
     */
    function updateOnZoom() {
        var map = State.get('map');
        if (!heatLayer || !map.hasLayer(heatLayer)) return;

        // Leaflet.heat doesn't support dynamic radius update well,
        // so we need to recreate the layer
        addHeatLayerToMap();
    }

    /**
     * Cleanup heat layer
     */
    function cleanup() {
        var map = State.get('map');
        if (heatLayer && map && map.hasLayer(heatLayer)) {
            map.removeLayer(heatLayer);
        }
        heatLayer = null;
        State.set('densityHeatLayer', null);
    }

    /**
     * Check if heat layer is visible
     */
    function isVisible() {
        var map = State.get('map');
        return heatLayer && map && map.hasLayer(heatLayer);
    }

    // Public API
    window.MapHeatLayer = {
        addHeatLayerToMap: addHeatLayerToMap,
        updateOnZoom: updateOnZoom,
        cleanup: cleanup,
        isVisible: isVisible,
        getRadiusForZoom: getRadiusForZoom,
    };
})(window);


// === hexgrid.js ===
/**
 * MapHexgrid - H3 hexagonal grid visualization
 *
 * Handles hex polygon rendering and aggregation.
 * Click on a hex to filter to churches in that area.
 */
(function (window) {
    'use strict';

    var State = window.MapState;
    var log = State.log;

    /**
     * Get H3 resolution for zoom level
     * Higher resolution = smaller hexes = more detail when zoomed in
     */
    function getHexResolutionForZoom(z) {
        if (z <= 3) return 2; // Country view - very large hexes
        if (z <= 4) return 2; // Region view - keep large for better clustering
        if (z <= 5) return 3; // Multi-state view
        if (z <= 6) return 4; // State view
        if (z <= 7) return 5; // Metro view
        if (z <= 9) return 6; // City view
        if (z <= 11) return 7; // Neighborhood view
        return 8; // Street view
    }

    /**
     * Get heat color - delegates to MapThemes if available, falls back to plasma
     */
    function getHeatColor(intensity) {
        if (window.MapThemes && window.MapThemes.getColor) {
            return window.MapThemes.getColor(intensity);
        }
        // Fallback plasma gradient
        var t = Math.max(0, Math.min(1, intensity));
        var stops = [
            { pos: 0.0, r: 13, g: 8, b: 135 },
            { pos: 0.15, r: 75, g: 3, b: 161 },
            { pos: 0.3, r: 137, g: 8, b: 165 },
            { pos: 0.45, r: 192, g: 58, b: 131 },
            { pos: 0.6, r: 234, g: 99, b: 82 },
            { pos: 0.75, r: 251, g: 159, b: 58 },
            { pos: 0.9, r: 249, g: 220, b: 92 },
            { pos: 1.0, r: 240, g: 249, b: 33 },
        ];
        var lower = stops[0];
        var upper = stops[stops.length - 1];
        for (var i = 0; i < stops.length - 1; i++) {
            if (t >= stops[i].pos && t <= stops[i + 1].pos) {
                lower = stops[i];
                upper = stops[i + 1];
                break;
            }
        }
        var range = upper.pos - lower.pos;
        var localT = range > 0 ? (t - lower.pos) / range : 0;
        var r = Math.round(lower.r + (upper.r - lower.r) * localT);
        var g = Math.round(lower.g + (upper.g - lower.g) * localT);
        var b = Math.round(lower.b + (upper.b - lower.b) * localT);
        return 'rgb(' + r + ',' + g + ',' + b + ')';
    }

    /**
     * Get heat color as RGB object
     */
    function getHeatColorRGB(intensity) {
        if (window.MapThemes && window.MapThemes.getColorRGB) {
            return window.MapThemes.getColorRGB(intensity);
        }
        var color = getHeatColor(intensity);
        var match = color.match(/rgb\((\d+),(\d+),(\d+)\)/);
        if (match) {
            return { r: parseInt(match[1]), g: parseInt(match[2]), b: parseInt(match[3]) };
        }
        return { r: 128, g: 128, b: 128 };
    }

    /**
     * Aggregate hexes to a lower resolution
     */
    function aggregateHexesToResolution(hexes, targetResolution) {
        var aggregated = {};

        hexes.forEach(function (hex) {
            try {
                var parentHex = h3.cellToParent(hex.h, targetResolution);
                if (!aggregated[parentHex]) {
                    var center = h3.cellToLatLng(parentHex);
                    aggregated[parentHex] = {
                        h: parentHex,
                        c: 0,
                        lat: center[0],
                        lon: center[1],
                    };
                }
                aggregated[parentHex].c += hex.c;
            } catch (e) {
                /* ignore invalid hexes */
            }
        });

        return Object.values(aggregated);
    }

    /**
     * Handle hex polygon click - filter to churches in hex
     */
    function handleHexClick(hexData, hexBoundary) {
        var allChurches = State.get('allChurches') || [];
        var LABELS = State.getLabels();
        log('Hex clicked:', hexData.c, LABELS.items);

        var bounds = L.latLngBounds(hexBoundary);
        var hexEntities = allChurches.filter(function (entity) {
            return bounds.contains([entity.lat, entity.lon]);
        });

        log('Found', hexEntities.length, 'entities in clicked hex');

        State.update({
            filteredItems: hexEntities,
            hexFilterActive: true,
            hexFilterBounds: bounds,
        });

        if (window.MapSidebar) {
            window.MapSidebar.displayItems(true);
            window.MapSidebar.updateStats();
        }

        var map = State.get('map');
        if (map) {
            map.fitBounds(bounds.pad(0.2));
        }

        showHexFilterIndicator(hexEntities.length);
    }

    /**
     * Show hex filter indicator
     */
    function showHexFilterIndicator(count) {
        var LABELS = State.getLabels();
        var indicator = document.getElementById('hexFilterIndicator');
        if (!indicator) {
            indicator = document.createElement('div');
            indicator.id = 'hexFilterIndicator';
            indicator.className = 'hex-filter-indicator';
            var sidebar = document.getElementById('sidebar');
            if (sidebar) {
                var itemList = sidebar.querySelector('.items-list') || sidebar.querySelector('#itemList');
                if (itemList) {
                    sidebar.insertBefore(indicator, itemList);
                } else {
                    sidebar.appendChild(indicator);
                }
            }
        }

        indicator.innerHTML =
            '<div class="hex-filter-content">' +
            '<span class="hex-filter-icon">📍</span>' +
            '<span class="hex-filter-text">Showing <strong>' +
            count.toLocaleString() +
            '</strong> ' + LABELS.items + ' in this area</span>' +
            '</div>' +
            '<button class="hex-filter-clear" onclick="MapHexgrid.clearHexFilter()">Clear Filter ✕</button>';
        indicator.style.display = 'flex';
    }

    /**
     * Clear hex filter
     */
    function clearHexFilter() {
        var allChurches = State.get('allChurches') || [];
        var config = State.getConfig();
        var map = State.get('map');

        State.update({
            hexFilterActive: false,
            hexFilterBounds: null,
            filteredItems: allChurches.slice(),
        });

        // Re-apply other filters
        if (window.MapFilters && window.MapFilters.applyFilters) {
            window.MapFilters.applyFilters();
        } else {
            // Fallback: update sidebar manually
            if (window.MapSidebar) {
                window.MapSidebar.displayItems(true);
                window.MapSidebar.updateStats();
            }
        }

        var indicator = document.getElementById('hexFilterIndicator');
        if (indicator) {
            indicator.style.display = 'none';
        }

        if (map && config.map) {
            var mapCenter = config.map.center || [39.5, -98.5];
            var mapZoom = config.map.zoom || 4;
            map.setView(mapCenter, mapZoom);
        }

        log('Hex filter cleared');
    }

    /**
     * Build enhanced tooltip with city breakdown
     */
    function buildEnhancedTooltip(hex, latLngs, LABELS) {
        var html = '<strong>' + hex.c.toLocaleString() + '</strong> ' + LABELS.items;

        var fullDataLoaded = State.get('fullDataLoaded');
        if (fullDataLoaded !== false) {
            var allChurches = State.get('allChurches') || [];
            var bounds = L.latLngBounds(latLngs);

            // Find entities in this hex
            var hexEntities = allChurches.filter(function (entity) {
                return bounds.contains([entity.lat, entity.lon]);
            });

            // Aggregate by city
            var cityCounts = {};
            hexEntities.forEach(function (entity) {
                var city = entity.city || 'Unknown';
                cityCounts[city] = (cityCounts[city] || 0) + 1;
            });

            // Sort by count and take top 3
            var sortedCities = Object.keys(cityCounts)
                .map(function (city) {
                    return { name: city, count: cityCounts[city] };
                })
                .sort(function (a, b) {
                    return b.count - a.count;
                });

            var topCities = sortedCities.slice(0, 3);
            var remainingCount = sortedCities.length - 3;

            if (topCities.length > 0) {
                html += '<div class="hex-cities">';
                topCities.forEach(function (city) {
                    html += '<div>' + city.name + ' (' + city.count + ')</div>';
                });
                if (remainingCount > 0) {
                    html += '<div class="hex-more">+' + remainingCount + ' more</div>';
                }
                html += '</div>';
            }
        }

        html += '<em style="font-size:10px;opacity:0.7;">Click to filter</em>';
        return html;
    }

    /**
     * Fix antimeridian crossing - normalize longitudes
     */
    function fixAntimeridian(boundary) {
        if (!boundary || boundary.length === 0) return boundary;

        var lons = boundary.map(function (c) {
            return c[1];
        });
        var minLon = Math.min.apply(null, lons);
        var maxLon = Math.max.apply(null, lons);

        if (maxLon - minLon > 180) {
            boundary = boundary.map(function (coord) {
                var lon = coord[1];
                if (lon > 0) lon -= 360;
                return [coord[0], lon];
            });
            lons = boundary.map(function (c) {
                return c[1];
            });
            minLon = Math.min.apply(null, lons);
            maxLon = Math.max.apply(null, lons);
        }

        if (maxLon - minLon > 50) {
            return null;
        }

        return boundary;
    }

    /**
     * Get hex boundary - uses h3-js
     */
    function getHexBoundary(hex) {
        if (hex.b && Array.isArray(hex.b)) {
            return fixAntimeridian(hex.b);
        }
        if (typeof h3 !== 'undefined') {
            var boundary = h3.cellToBoundary(hex.h);
            var coords = boundary.map(function (coord) {
                return [coord[0], coord[1]];
            });
            return fixAntimeridian(coords);
        }
        return null;
    }

    /**
     * Render hex polygons
     */
    function renderHexPolygons(hexes, zoom) {
        var hexLayer = State.get('hexLayer');
        var map = State.get('map');
        var LABELS = State.getLabels();

        var sourceResolution = (window._hexSummary && window._hexSummary.resolution) || 4;
        var targetResolution = getHexResolutionForZoom(zoom);

        var displayHexes;
        if (typeof h3 !== 'undefined') {
            if (targetResolution < sourceResolution) {
                displayHexes = aggregateHexesToResolution(hexes, targetResolution);
                log('Aggregated', hexes.length, 'hexes to', displayHexes.length, 'at resolution', targetResolution);
            } else {
                displayHexes = hexes;
                log('Using source hexes at resolution', sourceResolution);
            }
        } else {
            displayHexes = hexes;
            log('h3 not available, using source hexes');
        }

        // Calculate min/max
        var maxCount = 0;
        var minCount = Infinity;
        for (var i = 0; i < displayHexes.length; i++) {
            var c = displayHexes[i].c;
            if (c > maxCount) maxCount = c;
            if (c > 0 && c < minCount) minCount = c;
        }
        if (minCount === Infinity) minCount = 1;

        var bounds = map.getBounds().pad(0.5);
        var visibleHexes = displayHexes.filter(function (hex) {
            return bounds.contains([hex.lat, hex.lon]);
        });

        log('Rendering', visibleHexes.length, 'hexes at resolution', targetResolution);

        var logMinCount = Math.log(minCount);
        var logMaxCount = Math.log(maxCount);
        var logRange = logMaxCount - logMinCount;

        for (var j = 0; j < visibleHexes.length; j++) {
            var hex = visibleHexes[j];
            var latLngs = getHexBoundary(hex);
            if (!latLngs) continue;

            var logIntensity = logRange > 0 ? (Math.log(hex.c) - logMinCount) / logRange : 0.5;
            var color = getHeatColor(logIntensity);
            var strokeIntensity = Math.min(1, logIntensity + 0.15);
            var strokeColor = getHeatColor(strokeIntensity);

            var polygon = L.polygon(latLngs, {
                fillColor: color,
                fillOpacity: 0.82,
                color: strokeColor,
                weight: 2,
                opacity: 1.0,
            });

            polygon.bindTooltip(buildEnhancedTooltip(hex, latLngs, LABELS), {
                permanent: false,
                direction: 'top',
                className: 'hex-tooltip',
            });

            polygon._hexData = hex;
            polygon.on('click', function () {
                handleHexClick(this._hexData, getHexBoundary(this._hexData));
            });

            hexLayer.addLayer(polygon);
        }

        map.addLayer(hexLayer);
    }

    /**
     * Compute hex aggregation from entity data
     */
    function computeHexesFromData(entities, resolution) {
        var hexData = {};

        for (var i = 0; i < entities.length; i++) {
            var entity = entities[i];
            if (!entity.lat || !entity.lon) continue;
            try {
                var h3Index = h3.latLngToCell(entity.lat, entity.lon, resolution);
                if (!hexData[h3Index]) {
                    var center = h3.cellToLatLng(h3Index);
                    hexData[h3Index] = {
                        h: h3Index,
                        c: 0,
                        lat: center[0],
                        lon: center[1],
                    };
                }
                hexData[h3Index].c++;
            } catch (e) {
                // Skip invalid coordinates
            }
        }

        return Object.values(hexData);
    }

    /**
     * Add hex grid to map
     */
    function addHexGridToMap() {
        var map = State.get('map');
        var hexLayer = State.get('hexLayer');
        var densityHeatLayer = State.get('densityHeatLayer');
        var filteredItems = State.get('filteredItems') || [];
        var allChurches = State.get('allChurches') || [];

        hexLayer.clearLayers();
        if (densityHeatLayer && map.hasLayer(densityHeatLayer)) {
            map.removeLayer(densityHeatLayer);
            State.set('densityHeatLayer', null);
        }

        var zoom = map.getZoom();

        // Use pre-computed hex summary if available
        var hexSummary = window._hexSummary;
        if (hexSummary && hexSummary.hexes && Array.isArray(hexSummary.hexes)) {
            var hexes = hexSummary.hexes.map(function (h) {
                return {
                    h: h.h3 || h.h,
                    c: h.count || h.c,
                    lat: h.center ? h.center[0] : h.lat,
                    lon: h.center ? h.center[1] : h.lon,
                    b: h.boundary || h.b,
                };
            });
            log('Rendering from pre-computed hex summary (' + hexes.length + ' hexes)');
            renderHexPolygons(hexes, zoom);
            return;
        }

        // Compute hexes on the fly from loaded data
        // Use filtered data to respect active filters
        var sourceData = filteredItems.length > 0 ? filteredItems : allChurches;
        if (sourceData.length > 0) {
            var resolution = getHexResolutionForZoom(zoom);
            log('Computing hexes from', sourceData.length, 'entities at resolution', resolution);
            var computedHexes = computeHexesFromData(sourceData, resolution);

            if (computedHexes.length > 0) {
                window._hexSummary = {
                    hexes: computedHexes,
                    resolution: resolution,
                    total_entities: sourceData.length,
                    computed: true,
                };
                renderHexPolygons(computedHexes, zoom);
                return;
            }
        }

        // No data available - switch to markers view
        log('No data available for hexgrid, switching to markers view');
        if (window.MapViews) {
            window.MapViews.setView('markers');
        }
    }

    /**
     * Cleanup hexgrid
     */
    function cleanup() {
        var map = State.get('map');
        var hexLayer = State.get('hexLayer');

        if (hexLayer) {
            hexLayer.eachLayer(function (polygon) {
                if (polygon.off) {
                    polygon.off('click');
                    polygon.unbindTooltip();
                }
            });
            if (map && map.hasLayer(hexLayer)) {
                map.removeLayer(hexLayer);
            }
            hexLayer.clearLayers();
        }

        var indicator = document.getElementById('hexFilterIndicator');
        if (indicator) {
            indicator.style.display = 'none';
        }

        var timeout = State.get('hexgridRenderTimeout');
        if (timeout) {
            clearTimeout(timeout);
            State.set('hexgridRenderTimeout', null);
        }

        log('[Hexgrid] Cleanup complete');
    }

    /**
     * Show click hint overlay on first hexgrid view
     */
    function showClickHint() {
        var HINT_KEY = 'ubimissa_hex_hint_shown';

        try {
            if (localStorage.getItem(HINT_KEY)) {
                return;
            }
        } catch (e) {
            return;
        }

        var hint = document.createElement('div');
        hint.className = 'hex-click-hint';
        hint.innerHTML =
            '<span class="hex-click-hint-icon">👆</span>' +
            '<span class="hex-click-hint-text">' +
            '<strong>Click on any hex</strong> to filter churches in that area' +
            '</span>';

        var mapContainer = document.getElementById('map');
        if (mapContainer) {
            mapContainer.appendChild(hint);

            try {
                localStorage.setItem(HINT_KEY, '1');
            } catch (e) {
                // Ignore
            }

            setTimeout(function () {
                if (hint.parentNode) {
                    hint.parentNode.removeChild(hint);
                }
            }, 5500);
        }
    }

    // Public API
    window.MapHexgrid = {
        addHexGridToMap: addHexGridToMap,
        renderHexPolygons: renderHexPolygons,
        handleHexClick: handleHexClick,
        clearHexFilter: clearHexFilter,
        getHexResolutionForZoom: getHexResolutionForZoom,
        getHeatColor: getHeatColor,
        getHeatColorRGB: getHeatColorRGB,
        aggregateHexesToResolution: aggregateHexesToResolution,
        computeHexesFromData: computeHexesFromData,
        cleanup: cleanup,
        showClickHint: showClickHint,
    };

    // Global alias
    window.clearHexFilter = clearHexFilter;
})(window);


// === heatmap.js ===
/**
 * MapHeatmap - 3D column visualization
 *
 * Handles 3D column/bar rendering for church density.
 * Click on a bar to filter to churches in that area.
 */
(function (window) {
    'use strict';

    var State = window.MapState;
    var log = State.log;

    /**
     * Get H3 resolution for heatmap zoom
     */
    function getResolutionForZoom(z) {
        if (z <= 3) return 2;
        if (z <= 4) return 3;
        if (z <= 5) return 3;
        if (z <= 6) return 4;
        if (z <= 8) return 5;
        if (z <= 10) return 6;
        return 7;
    }

    /**
     * Add 3D columns to map
     */
    function addHeatmapToMap() {
        var map = State.get('map');
        var columnsLayer = State.get('columnsLayer');
        var filteredItems = State.get('filteredItems') || [];
        var allChurches = State.get('allChurches') || [];
        var LABELS = State.getLabels();

        // Remove existing
        if (columnsLayer) {
            if (map.hasLayer(columnsLayer)) {
                map.removeLayer(columnsLayer);
            }
            columnsLayer.clearLayers();
        } else {
            columnsLayer = L.layerGroup();
            State.set('columnsLayer', columnsLayer);
        }

        // Use filtered data to respect active filters
        var sourceData = filteredItems.length > 0 ? filteredItems : allChurches;

        if (sourceData.length === 0) {
            log('No data to visualize in heatmap');
            return;
        }

        var zoom = map.getZoom();
        var resolution = getResolutionForZoom(zoom);

        // Aggregate into hex cells
        var hexData = {};
        for (var i = 0; i < sourceData.length; i++) {
            var church = sourceData[i];
            if (!church.lat || !church.lon) continue;
            try {
                var h3Index = h3.latLngToCell(church.lat, church.lon, resolution);
                var hex = hexData[h3Index];
                if (!hex) {
                    var center = h3.cellToLatLng(h3Index);
                    hex = hexData[h3Index] = {
                        hex: h3Index,
                        lat: center[0],
                        lon: center[1],
                        count: 0,
                        churches: [],
                        cities: {},
                    };
                }
                hex.count++;
                hex.churches.push(church.name);

                // Track city counts for popup
                var city = church.city || 'Unknown';
                hex.cities[city] = (hex.cities[city] || 0) + 1;
            } catch (e) {
                console.warn('H3 error:', e);
            }
        }

        var hexArray = Object.values(hexData);

        // Calculate maxCount
        var maxCount = 0;
        for (var j = 0; j < hexArray.length; j++) {
            if (hexArray[j].count > maxCount) maxCount = hexArray[j].count;
        }

        // Sort by latitude for proper 3D stacking
        hexArray.sort(function (a, b) {
            return b.lat - a.lat;
        });

        // Pre-compute constants
        var logMax = Math.log(maxCount + 1);
        var heightMultiplier = zoom <= 5 ? 200 : zoom <= 7 ? 160 : 120;
        var baseWidth = zoom <= 5 ? 12 : zoom <= 7 ? 16 : 20;

        // Create 3D columns
        for (var k = 0; k < hexArray.length; k++) {
            var hexItem = hexArray[k];
            var logCount = Math.log(hexItem.count + 1);
            var ratio = logCount / logMax;

            var height = Math.max(25, ratio * heightMultiplier);

            // Get color with fallback
            var rgb = window.MapHexgrid && window.MapHexgrid.getHeatColorRGB
                ? window.MapHexgrid.getHeatColorRGB(ratio)
                : { r: 255, g: 165, b: 0 }; // Orange fallback
            var r = rgb.r, g = rgb.g, b = rgb.b;

            // Pre-compute color values
            var r05 = Math.round(r * 0.5);
            var g05 = Math.round(g * 0.5);
            var b05 = Math.round(b * 0.5);
            var r30 = Math.min(255, r + 30);
            var g30 = Math.min(255, g + 30);
            var b30 = Math.min(255, b + 30);

            var baseColor = 'rgb(' + r05 + ',' + g05 + ',' + b05 + ')';
            var midColor = 'rgb(' + r + ',' + g + ',' + b + ')';
            var topColor = 'rgb(' + r30 + ',' + g30 + ',' + b30 + ')';

            var columnWidth = Math.max(baseWidth, baseWidth + ratio * 12);
            var columnDepth = columnWidth * 0.5;

            // Side face colors
            var sideBottom = 'rgba(' + Math.round(r * 0.4) + ',' + Math.round(g * 0.3) + ',' + Math.round(b * 0.2) + ',1)';
            var sideTop = 'rgba(' + Math.round(r * 0.6) + ',' + Math.round(g * 0.5) + ',' + Math.round(b * 0.4) + ',1)';
            var topFaceStart = 'rgba(' + Math.round(r * 0.8) + ',' + Math.round(g * 0.8) + ',' + Math.round(b * 0.9) + ',1)';
            var glowSize = 8 + ratio * 15;

            // Build HTML for 3D column
            var columnHtml = [
                '<div class="column-3d" data-count="', hexItem.count, '" style="width:', columnWidth,
                'px;height:', height, 'px;position:relative;transform-style:preserve-3d;transform:rotateX(-25deg) rotateY(-35deg);cursor:pointer;">',
                '<div style="position:absolute;width:', columnWidth, 'px;height:', height,
                'px;background:linear-gradient(to top,', baseColor, ' 0%,', midColor, ' 50%,', topColor, ' 100%);box-shadow:inset -2px 0 4px rgba(0,0,0,0.3),inset 2px 0 4px rgba(255,255,255,0.1);"></div>',
                '<div style="position:absolute;width:', columnDepth, 'px;height:', height, 'px;left:', columnWidth,
                'px;background:linear-gradient(to top,', sideBottom, ' 0%,', sideTop, ' 100%);transform:skewY(-45deg);transform-origin:left top;"></div>',
                '<div style="position:absolute;width:', columnWidth, 'px;height:', columnDepth, 'px;top:-', columnDepth,
                'px;background:linear-gradient(135deg,', topFaceStart, ' 0%,', topColor, ' 100%);transform:skewX(-45deg);transform-origin:left bottom;box-shadow:0 0 ', glowSize,
                'px rgba(', r, ',', g, ',', b, ',0.6);"></div></div>'
            ].join('');

            var icon = L.divIcon({
                className: 'column-marker',
                html: columnHtml,
                iconSize: [columnWidth + columnDepth + 10, height + columnDepth + 10],
                iconAnchor: [(columnWidth + columnDepth) / 2, height + columnDepth],
            });

            var marker = L.marker([hexItem.lat, hexItem.lon], {
                icon: icon,
                zIndexOffset: Math.round(1000 - hexItem.lat * 10),
            });

            // Store hex data for click handler
            marker._hexData = hexItem;
            marker._allChurches = allChurches;

            // Click to filter
            marker.on('click', function (e) {
                if (e.originalEvent) e.originalEvent.stopPropagation();
                L.DomEvent.stopPropagation(e);

                var hex = this._hexData;
                var churches = this._allChurches;
                log('3D bar clicked:', hex.count, 'entities');

                var hexChurchNames = hex.churches;
                var hexChurches = churches.filter(function (c) {
                    return hexChurchNames.indexOf(c.name) !== -1;
                });

                if (hexChurches.length > 0) {
                    State.update({
                        filteredItems: hexChurches,
                        hexFilterActive: true,
                    });

                    this.closePopup();

                    if (window.MapSidebar) {
                        window.MapSidebar.displayItems(true);
                        window.MapSidebar.updateStats();
                    }
                    if (window.MapViews) {
                        window.MapViews.updateModeIndicator();
                    }

                    // Show filter indicator
                    if (window.MapHexgrid && window.MapHexgrid.clearHexFilter) {
                        showBarFilterIndicator(hexChurches.length);
                    }

                    map.fitBounds([
                        [hex.lat - 0.15, hex.lon - 0.15],
                        [hex.lat + 0.15, hex.lon + 0.15]
                    ], { padding: [100, 100], maxZoom: 12 });
                }
            });

            // Build popup with city breakdown
            var cityEntries = Object.entries(hex.cities || {}).sort(function (a, b) {
                return b[1] - a[1];
            });
            var cityHtml = '';
            if (cityEntries.length > 0) {
                cityHtml = '<div style="border-top:1px solid #333;padding-top:8px;margin-top:8px;">' +
                    cityEntries.slice(0, 3).map(function (ct) {
                        var pct = Math.round((ct[1] / hexItem.count) * 100);
                        return '<div style="display:flex;justify-content:space-between;padding:2px 0;font-size:11px;">' +
                            '<span style="color:#94a3b8;">' + ct[0] + '</span>' +
                            '<span style="color:#FFA028;font-weight:600;">' + pct + '%</span></div>';
                    }).join('') + '</div>';
            }

            marker.bindPopup(
                '<div style="min-width: 240px;">' +
                '<div style="font-size: 32px; font-weight: 800; color: ' + midColor + '; text-shadow: 0 0 20px ' + midColor + ';">' +
                hexItem.count.toLocaleString() + '</div>' +
                '<div style="font-size: 14px; color: #aaa; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 2px;">' +
                LABELS.items + '</div>' +
                cityHtml +
                '<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #333; font-size: 11px; color: #666;">' +
                hexItem.churches.slice(0, 3).join(' • ') +
                (hexItem.churches.length > 3 ? '<br><span style="color:#888">+' + (hexItem.churches.length - 3) + ' more</span>' : '') +
                '</div>' +
                '<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #444; font-size: 10px; color: #4ea3ff; font-weight: 600; text-align: center;">' +
                'Click bar to filter map</div></div>',
                { className: 'column-popup', closeOnClick: false, autoClose: false }
            );

            columnsLayer.addLayer(marker);
        }

        map.addLayer(columnsLayer);
        log('3D columns rendered:', hexArray.length, 'columns, max count:', maxCount);
    }

    /**
     * Show bar filter indicator
     */
    function showBarFilterIndicator(count) {
        var LABELS = State.getLabels();
        var indicator = document.getElementById('hexFilterIndicator');
        if (!indicator) {
            indicator = document.createElement('div');
            indicator.id = 'hexFilterIndicator';
            indicator.className = 'hex-filter-indicator';
            var sidebar = document.getElementById('sidebar');
            if (sidebar) {
                var itemList = sidebar.querySelector('.items-list') || sidebar.querySelector('#itemList');
                if (itemList) {
                    sidebar.insertBefore(indicator, itemList);
                } else {
                    sidebar.appendChild(indicator);
                }
            }
        }

        indicator.innerHTML =
            '<div class="hex-filter-content">' +
            '<span class="hex-filter-icon">📍</span>' +
            '<span class="hex-filter-text">Showing <strong>' + count.toLocaleString() +
            '</strong> ' + LABELS.items + ' in this area</span></div>' +
            '<button class="hex-filter-clear" onclick="MapHexgrid.clearHexFilter()">Clear Filter ✕</button>';
        indicator.style.display = 'flex';
    }

    /**
     * Cleanup heatmap layers
     */
    function cleanup() {
        var map = State.get('map');
        var columnsLayer = State.get('columnsLayer');

        if (columnsLayer) {
            columnsLayer.eachLayer(function (marker) {
                if (marker.off) {
                    marker.off('click');
                    marker.unbindPopup();
                }
            });
            if (map && map.hasLayer(columnsLayer)) {
                map.removeLayer(columnsLayer);
            }
            columnsLayer.clearLayers();
        }

        var timeout = State.get('heatmapRenderTimeout');
        if (timeout) {
            clearTimeout(timeout);
            State.set('heatmapRenderTimeout', null);
        }

        log('[Heatmap] Cleanup complete');
    }

    // Public API
    window.MapHeatmap = {
        addHeatmapToMap: addHeatmapToMap,
        cleanup: cleanup,
        getResolutionForZoom: getResolutionForZoom,
    };
})(window);


// === views.js ===
/**
 * MapViews - View mode management
 *
 * View modes:
 * - 'logos' / 'favicons' - Individual church markers with favicons
 * - 'clusters' / 'markers' - Clustered markers
 * - 'heat' - Traditional blurry heat gradient (leaflet.heat)
 * - 'hexgrid' - H3 hexagonal polygons
 * - 'bars' / 'heatmap' - 3D column visualization
 */
(function (window) {
    'use strict';

    var State = window.MapState;
    var log = State.log;

    // Constants
    var CONSTANTS = {
        DEBOUNCE_MS: 150,
        STORAGE_VIEW_MODE: 'ubimissa_view_mode',
        STORAGE_AUTO_SWITCH: 'ubimissa_auto_switch',
        AUTO_SWITCH_ZOOM_IN: 13,
        AUTO_SWITCH_ZOOM_OUT: 7,
        AUTO_SWITCH_COOLDOWN_MS: 1000,
    };

    // Cached DOM elements
    var cachedViewButtons = null;

    /**
     * View registry - maps view names to init/cleanup functions
     */
    var viewRegistry = {
        markers: {
            init: function () {
                if (window.MapMarkers) {
                    window.MapMarkers.addMarkersToMap();
                    window.MapMarkers.updateMarkerLayerVisibility();
                }
            },
            cleanup: function () {
                if (window.MapMarkers) window.MapMarkers.cleanup();
            },
        },
        heat: {
            init: function () {
                if (window.MapHeatLayer) {
                    window.MapHeatLayer.addHeatLayerToMap();
                } else {
                    log('Heat layer module not available, using hexgrid');
                    State.set('currentView', 'hexgrid');
                    if (window.MapHexgrid) window.MapHexgrid.addHexGridToMap();
                }
            },
            cleanup: function () {
                if (window.MapHeatLayer) window.MapHeatLayer.cleanup();
            },
        },
        hexgrid: {
            init: function () {
                if (window.MapHexgrid) {
                    window.MapHexgrid.addHexGridToMap();
                    window.MapHexgrid.showClickHint();
                }
            },
            cleanup: function () {
                if (window.MapHexgrid) window.MapHexgrid.cleanup();
            },
        },
        bars: {
            init: function () {
                if (window.MapHeatmap) window.MapHeatmap.addHeatmapToMap();
            },
            cleanup: function () {
                if (window.MapHeatmap) window.MapHeatmap.cleanup();
            },
        },
    };

    // Track previous view for cleanup
    var previousView = null;

    /**
     * Update map view based on current mode
     */
    function updateMapView() {
        var map = State.get('map');
        var currentView = State.get('currentView');

        log('Switching to view:', currentView, '(previous:', previousView, ')');

        // Smooth crossfade transition
        var mapEl = document.getElementById('map');
        if (mapEl) {
            mapEl.style.transition = 'opacity 0.15s ease-out';
            mapEl.style.opacity = '0.85';
        }

        // Cleanup previous view using registry
        if (previousView && viewRegistry[previousView] && viewRegistry[previousView].cleanup) {
            log('Cleaning up previous view:', previousView);
            viewRegistry[previousView].cleanup();
        }

        // Clear computed hex summary when switching away from hexgrid
        if (currentView !== 'hexgrid' && window._hexSummary && window._hexSummary.computed) {
            window._hexSummary = null;
        }

        // Initialize new view using registry
        if (viewRegistry[currentView]) {
            log('Initializing view:', currentView);
            viewRegistry[currentView].init();
        }

        // Track for next cleanup
        previousView = currentView;

        // Persist view preference to localStorage
        try {
            localStorage.setItem(CONSTANTS.STORAGE_VIEW_MODE, currentView);
        } catch (e) {
            // localStorage not available
        }

        // Fade back in
        requestAnimationFrame(function () {
            if (mapEl) mapEl.style.opacity = '1';
        });
    }

    /**
     * Normalize view name (handle aliases)
     */
    function normalizeViewName(view) {
        var aliases = {
            favicons: 'markers',
            logos: 'markers',
            heatmap: 'bars', // Legacy: 'heatmap' was 3D bars
            '3d': 'bars',
            '3dbars': 'bars',
            hex: 'hexgrid',
            clusters: 'markers',
        };
        return aliases[view] || view;
    }

    /**
     * Set view mode
     */
    function setView(view) {
        var fullDataLoaded = State.get('fullDataLoaded');

        log('Setting view to:', view);

        // Normalize view name
        var normalizedView = normalizeViewName(view);

        // Handle marker-based views (logos vs clusters)
        if (view === 'favicons' || view === 'logos') {
            State.set('currentView', 'markers');
            State.set('useClustering', false); // Individual logos, no grouping
        } else if (view === 'clusters' || view === 'markers') {
            State.set('currentView', 'markers');
            State.set('useClustering', true); // Grouped mosaic clusters
        } else {
            State.set('currentView', normalizedView);
        }

        // Check what data is needed
        var hasHexSummary = window._hexSummary && window._hexSummary.hexes;
        var needsFullData =
            view === 'clusters' ||
            view === 'markers' ||
            view === 'bars' ||
            view === 'heatmap' ||
            view === 'heat' ||
            (view === 'hexgrid' && !hasHexSummary);

        if (needsFullData && !fullDataLoaded && window.MapDataLoader && window.MapDataLoader.triggerLazyLoad) {
            log(view + ' view requires full data - triggering lazy load');
            window.MapDataLoader.triggerLazyLoad(function () {
                updateModeIndicator();
                updateMapView();
                if (window.MapSidebar) {
                    window.MapSidebar.displayItems(true);
                }
            });
            updateViewButtons(view);
            return;
        }

        updateViewButtons(view);
        updateModeIndicator();
        updateMapView();
    }

    /**
     * Update view mode button states
     */
    function updateViewButtons(view) {
        // Cache view buttons on first access
        if (!cachedViewButtons) {
            cachedViewButtons = document.querySelectorAll('.view-mode-btn');
        }

        // Remove active from all view buttons
        for (var i = 0; i < cachedViewButtons.length; i++) {
            cachedViewButtons[i].classList.remove('active');
            cachedViewButtons[i].setAttribute('aria-pressed', 'false');
        }

        // Map view names to button IDs
        var viewToButton = {
            logos: 'logosBtn',
            favicons: 'logosBtn',
            markers: 'clustersBtn',
            clusters: 'clustersBtn',
            heat: 'heatBtn',
            hexgrid: 'hexgridBtn',
            bars: 'barsBtn',
            heatmap: 'barsBtn',
        };

        // Activate the correct button
        var buttonId = viewToButton[view];
        if (buttonId) {
            var btn = document.getElementById(buttonId);
            if (btn) {
                btn.classList.add('active');
                btn.setAttribute('aria-pressed', 'true');
            }
        }
    }

    /**
     * Update mode indicator
     */
    function updateModeIndicator() {
        var map = State.get('map');
        var currentView = State.get('currentView');
        var filteredItems = State.get('filteredItems') || [];
        var allChurches = State.get('allChurches') || [];
        var LABELS = State.getLabels();

        var indicator = document.getElementById('modeIndicator');
        var modeText = document.getElementById('modeText');
        if (!indicator || !modeText) return;

        var zoom = map.getZoom();
        var count = filteredItems.length || allChurches.length;

        if (currentView === 'markers') {
            var useClustering = State.get('useClustering');
            indicator.style.display = 'block';
            if (useClustering) {
                modeText.textContent = 'Clustered • ' + count.toLocaleString() + ' ' + LABELS.items;
            } else {
                modeText.textContent = 'Logos • ' + count.toLocaleString() + ' ' + LABELS.items;
            }
        } else if (currentView === 'heat') {
            indicator.style.display = 'block';
            modeText.textContent = 'Heat Map • ' + count.toLocaleString() + ' ' + LABELS.items;
        } else if (currentView === 'hexgrid') {
            indicator.style.display = 'block';
            var hexRes = window.MapHexgrid ? window.MapHexgrid.getHexResolutionForZoom(zoom) : 5;
            modeText.textContent = 'Hex Grid • ' + count.toLocaleString() + ' ' + LABELS.items + ' • H3 res ' + hexRes;
        } else if (currentView === 'bars') {
            indicator.style.display = 'block';
            var res = window.MapHeatmap ? window.MapHeatmap.getResolutionForZoom(zoom) : 5;
            modeText.textContent = '3D Bars • ' + count.toLocaleString() + ' ' + LABELS.items + ' • H3 res ' + res;
        } else {
            indicator.style.display = 'block';
            modeText.textContent = count.toLocaleString() + ' ' + LABELS.items;
        }
    }

    /**
     * Set clustering mode (legacy compatibility)
     */
    function setClustering(useClustering) {
        State.set('useClustering', useClustering);
        State.set('currentView', 'markers');

        var fullDataLoaded = State.get('fullDataLoaded');
        var lazyDataPaths = State.get('_lazyDataPaths');

        if (!fullDataLoaded && lazyDataPaths && window.MapDataLoader) {
            window.MapDataLoader.triggerLazyLoad(function () {
                updateModeIndicator();
                updateMapView();
                if (window.MapSidebar) {
                    window.MapSidebar.displayItems(true);
                }
            });
            return;
        }

        updateModeIndicator();
        updateMapView();
    }

    /**
     * Check if auto-switch should occur based on zoom level
     */
    function checkAutoSwitch(zoom) {
        var autoSwitchEnabled = State.get('autoSwitchEnabled');
        var currentView = State.get('currentView');
        var lastAutoSwitch = State.get('lastAutoSwitch') || 0;
        var allChurches = State.get('allChurches') || [];
        var now = Date.now();

        if (now - lastAutoSwitch < CONSTANTS.AUTO_SWITCH_COOLDOWN_MS) return false;
        if (autoSwitchEnabled !== true) return false;
        if (allChurches.length < 5000) return false;

        // Auto-switch: aggregated views -> markers when zoomed in
        if ((currentView === 'hexgrid' || currentView === 'bars' || currentView === 'heat') && zoom >= CONSTANTS.AUTO_SWITCH_ZOOM_IN) {
            log('Auto-switch: zoomed in, switching to clusters');
            State.set('lastAutoSwitch', now);
            setView('clusters');
            return true;
        }

        // Auto-switch: markers -> hexgrid when zoomed out
        if (currentView === 'markers' && zoom <= CONSTANTS.AUTO_SWITCH_ZOOM_OUT) {
            log('Auto-switch: zoomed out, switching to hexgrid');
            State.set('lastAutoSwitch', now);
            setView('hexgrid');
            return true;
        }

        return false;
    }

    /**
     * Toggle auto-switch feature
     */
    function setAutoSwitch(enabled) {
        State.set('autoSwitchEnabled', enabled);
        try {
            localStorage.setItem(CONSTANTS.STORAGE_AUTO_SWITCH, enabled ? 'true' : 'false');
        } catch (e) {
            // localStorage not available
        }
        log('Auto-switch', enabled ? 'enabled' : 'disabled');
    }

    /**
     * Initialize zoom handlers
     */
    function initZoomHandlers() {
        var map = State.get('map');
        var debounceMs = CONSTANTS.DEBOUNCE_MS;

        // Restore saved auto-switch preference
        try {
            var savedAutoSwitch = localStorage.getItem(CONSTANTS.STORAGE_AUTO_SWITCH);
            if (savedAutoSwitch !== null) {
                State.set('autoSwitchEnabled', savedAutoSwitch === 'true');
            }
        } catch (e) {
            // localStorage not available
        }

        map.on('zoomend', function () {
            var zoom = map.getZoom();
            var currentView = State.get('currentView');

            if (checkAutoSwitch(zoom)) return;

            // Update mode indicator for all views
            updateModeIndicator();

            if (currentView === 'markers') {
                if (window.MapMarkers) {
                    window.MapMarkers.updateMarkerLayerVisibility();
                }
            } else if (currentView === 'heat') {
                var timeout = State.get('heatRenderTimeout');
                if (timeout) clearTimeout(timeout);
                State.set('heatRenderTimeout', setTimeout(function () {
                    log('Heat: Zoom changed, re-rendering');
                    if (window.MapHeatLayer) {
                        window.MapHeatLayer.addHeatLayerToMap();
                    }
                }, debounceMs));
            } else if (currentView === 'bars') {
                var heatmapTimeout = State.get('heatmapRenderTimeout');
                if (heatmapTimeout) clearTimeout(heatmapTimeout);
                State.set('heatmapRenderTimeout', setTimeout(function () {
                    log('3D bars: Zoom changed, re-rendering');
                    if (window.MapHeatmap) {
                        window.MapHeatmap.addHeatmapToMap();
                    }
                }, debounceMs));
            } else if (currentView === 'hexgrid') {
                var hexTimeout = State.get('hexgridRenderTimeout');
                if (hexTimeout) clearTimeout(hexTimeout);
                State.set('hexgridRenderTimeout', setTimeout(function () {
                    log('Hexgrid: Zoom changed, re-rendering');
                    if (window._hexSummary && window._hexSummary.computed) {
                        window._hexSummary = null;
                    }
                    if (window.MapHexgrid) {
                        window.MapHexgrid.addHexGridToMap();
                    }
                }, debounceMs));
            }
        });

        map.on('moveend', function () {
            updateViewportFiltering();

            var currentView = State.get('currentView');

            // Recalculate visualizations on pan
            if (currentView === 'hexgrid' && window._hexSummary && window._hexSummary.hexes) {
                var hexTimeout = State.get('hexgridRenderTimeout');
                if (hexTimeout) clearTimeout(hexTimeout);
                State.set('hexgridRenderTimeout', setTimeout(function () {
                    var hexLayer = State.get('hexLayer');
                    if (hexLayer) hexLayer.clearLayers();
                    if (window.MapHexgrid) {
                        window.MapHexgrid.renderHexPolygons(window._hexSummary.hexes, map.getZoom());
                    }
                }, 100));
            } else if (currentView === 'heat') {
                var heatTimeout = State.get('heatRenderTimeout');
                if (heatTimeout) clearTimeout(heatTimeout);
                State.set('heatRenderTimeout', setTimeout(function () {
                    if (window.MapHeatLayer) {
                        window.MapHeatLayer.addHeatLayerToMap();
                    }
                }, 150));
            } else if (currentView === 'bars') {
                var barsTimeout = State.get('heatmapRenderTimeout');
                if (barsTimeout) clearTimeout(barsTimeout);
                State.set('heatmapRenderTimeout', setTimeout(function () {
                    if (window.MapHeatmap) {
                        window.MapHeatmap.addHeatmapToMap();
                    }
                }, 150));
            }
        });
    }

    /**
     * Update viewport-based filtering
     * Cluster layer handles marker visibility; just update stats/indicator.
     */
    function updateViewportFiltering() {
        if (window.MapSidebar && window.MapSidebar.updateStats) {
            window.MapSidebar.updateStats();
        }
        updateModeIndicator();
    }

    /**
     * Get saved view preference from localStorage
     */
    function getSavedView() {
        try {
            return localStorage.getItem(CONSTANTS.STORAGE_VIEW_MODE);
        } catch (e) {
            return null;
        }
    }

    /**
     * Restore saved view preference
     */
    function restoreSavedView() {
        var savedView = getSavedView();
        if (savedView && viewRegistry[savedView]) {
            log('Restoring saved view preference:', savedView);
            return savedView;
        }
        return null;
    }

    // Public API
    window.MapViews = {
        updateMapView: updateMapView,
        setView: setView,
        setClustering: setClustering,
        updateViewButtons: updateViewButtons,
        updateModeIndicator: updateModeIndicator,
        initZoomHandlers: initZoomHandlers,
        updateViewportFiltering: updateViewportFiltering,
        setAutoSwitch: setAutoSwitch,
        checkAutoSwitch: checkAutoSwitch,
        normalizeViewName: normalizeViewName,
        getSavedView: getSavedView,
        restoreSavedView: restoreSavedView,
        viewRegistry: viewRegistry,
    };

    // Global aliases for HTML onclick handlers
    window.setView = setView;
    window.setAutoSwitch = setAutoSwitch;
})(window);


// === index.js ===
/**
 * MapApp - Main orchestration module for Church Finder
 *
 * Initializes all map modules and sets up event handlers.
 * Load order: state -> utils -> init -> data-loader -> sidebar -> markers -> filters -> liturgy
 */
(function(window) {
    'use strict';

    var initialized = false;

    /**
     * Wait for required dependencies before initialization
     */
    function waitForDependencies(callback, retries) {
        retries = retries || 0;

        // Check if all required modules are loaded
        var requiredModules = {
            'MapState': window.MapState,
            'MapUtils': window.MapUtils,
            'MapInit': window.MapInit,
            'MapDataLoader': window.MapDataLoader,
            'MapSidebar': window.MapSidebar,
            'MapMarkers': window.MapMarkers
        };

        var missingModules = [];
        for (var name in requiredModules) {
            if (!requiredModules[name]) {
                missingModules.push(name);
            }
        }

        if (missingModules.length > 0) {
            if (retries > 20) {
                console.error('Failed to load dependencies after 20 attempts. Missing modules:', missingModules);
                // Show error to user
                var mapElement = document.getElementById('map');
                if (mapElement) {
                    mapElement.innerHTML = '<div style="color: #ef4444; padding: 20px; text-align: center;">' +
                        'Map failed to load. Missing modules: ' + missingModules.join(', ') +
                        '<br>Please refresh the page.</div>';
                }
                return;
            }

            // Retry after 250ms
            setTimeout(function() {
                console.log('Waiting for modules (attempt ' + (retries + 1) + '):', missingModules);
                waitForDependencies(callback, retries + 1);
            }, 250);
            return;
        }

        // All dependencies loaded, proceed
        callback();
    }

    /**
     * Initialize the map application
     */
    function init() {
        if (initialized) {
            console.warn('MapApp already initialized');
            return;
        }

        // Wait for dependencies then initialize
        waitForDependencies(function() {
            var State = window.MapState;
            var config = State.getConfig();

        State.log('MapApp: Starting initialization...');

        // Initialize debug mode
        State.initDebug();

        // Initialize map
        if (window.MapInit) {
            window.MapInit.init();
        } else {
            console.error('MapInit module not loaded');
            return;
        }

        // Initialize sidebar
        if (window.MapSidebar && window.MapSidebar.initSidebar) {
            window.MapSidebar.initSidebar();
        }

        // Initialize filter listeners
        if (window.MapFilters && window.MapFilters.setupFilterListeners) {
            window.MapFilters.setupFilterListeners();
        }

        // Viewport loading disabled - use standard marker clustering for simplicity and speed
        // Can be re-enabled with ?viewport=true if needed
        var urlParams = new URLSearchParams(window.location.search);
        var forceViewport = urlParams.get('viewport') === 'true';

        if (forceViewport && window.ViewportLoader) {
            State.log('Using viewport loading (forced via URL param)');
            setTimeout(function() {
                var map = State.get('map');
                if (map && window.ViewportLoader) {
                    window.ViewportLoader.init(map);
                }
            }, 200);
            State.set('useViewportLoading', true);
        }

        // Initialize zoom handlers (for marker updates)
        if (window.MapViews && window.MapViews.initZoomHandlers) {
            window.MapViews.initZoomHandlers();
        }

        // Initialize search autocomplete
        if (window.MapSearch && window.MapSearch.init) {
            window.MapSearch.init();
        }

        // Load data (skip if using viewport loading - it handles its own data)
        var useViewportLoading = State.get('useViewportLoading');
        if (useViewportLoading) {
            State.log('Using viewport loading, skipping standard data load');
            // Initialize empty data state for viewport loading
            State.update({
                allChurches: [],
                filteredItems: []
            });
        } else {
            var dataSources = config.data && config.data.primary;
            if (dataSources && dataSources.length > 0) {
                State.log('Starting data load for', config.site && config.site.name || 'Church Map');

                if (window.MapDataLoader) {
                    window.MapDataLoader.load(function() {
                        onDataLoaded();
                    });
                }
            } else {
                State.log('No CSV data configured, loading from API');
                loadFromApi();
            }
        }

        // Set up map event handlers
        var map = State.get('map');
        if (map) {
            // On zoom change: update sidebar stats (cluster layer handles marker visibility)
            map.on('zoomend', debounce(function() {
                if (window.MapSidebar && window.MapSidebar.updateStats) {
                    window.MapSidebar.updateStats();
                }
            }, 300));

            // On pan: only update stats (don't re-render markers - would close popups)
            map.on('moveend', debounce(function() {
                // Update visible count when viewport changes
                if (window.MapSidebar && window.MapSidebar.updateStats) {
                    window.MapSidebar.updateStats();
                }
            }, 150));
        }

            initialized = true;
            State.log('MapApp: Initialization complete');
        }); // End of waitForDependencies callback
    }

    /**
     * Load churches from API endpoint
     * @param {Function} successCallback - Optional callback on success
     * @param {Function} errorCallback - Optional callback on error
     */
    function loadFromApi(successCallback, errorCallback) {
        var State = window.MapState;
        var config = State.getConfig();

        State.log('Loading churches from API...');

        // Build API URL with optional filters
        var apiUrl = '/api/churches/all';
        var urlParams = new URLSearchParams(window.location.search);
        var stateParam = urlParams.get('state');
        var dioceseParam = urlParams.get('diocese');
        var diocese = State.getDiocese();
        var queryParts = ['compact=1']; // Use compact mode for faster initial load

        if (stateParam) {
            queryParts.push('state=' + encodeURIComponent(stateParam));
        } else if (dioceseParam && dioceseParam !== 'global') {
            queryParts.push('diocese=' + encodeURIComponent(dioceseParam));
        } else if (diocese && diocese !== 'global') {
            queryParts.push('diocese=' + encodeURIComponent(diocese));
        }

        if (queryParts.length > 0) {
            apiUrl += '?' + queryParts.join('&');
        }

        fetch(apiUrl, {
            headers: {
                'Accept': 'application/json',
                'Accept-Encoding': 'gzip, deflate'
            }
        })
            .then(function(r) {
                if (!r.ok) {
                    throw new Error('API request failed: ' + r.status);
                }
                return r.json();
            })
            .then(function(data) {
                var churches = data.churches || data || [];
                State.log('Loaded', churches.length, 'churches from API');

                // Store diocese icon config for markers
                if (data.iconDefaults) {
                    State.set('dioceseIconConfig', data.iconDefaults);
                    State.log('Diocese icon config:', data.iconDefaults.defaultFaviconUrl);
                }

                // Normalize data format
                var normalized = churches.map(function(c) {
                    return {
                        id: c.id,
                        name: c.church_name || c.name,
                        city: c.city,
                        state: c.state,
                        country: c.country || 'US',
                        lat: parseFloat(c.latitude),
                        lon: parseFloat(c.longitude),
                        website: c.website,
                        phone: c.phone,
                        address: c.street_address || c.address,
                        zip_code: c.zip_code,
                        diocese_id: c.diocese_id,
                        denomination: c.denomination || 'Catholic',
                        slug: c.slug,
                        description: c.description,
                        mass_times: c.mass_times,
                        confession_times: c.confession_times,
                        adoration_times: c.adoration_times,
                        has_mass: c.has_mass,
                        has_confession: c.has_confession,
                        has_adoration: c.has_adoration
                    };
                }).filter(function(c) {
                    return !isNaN(c.lat) && !isNaN(c.lon);
                });

                State.update({
                    allChurches: normalized,
                    filteredItems: normalized.slice()
                });

                // Expose globally for backwards compatibility
                window.allChurches = normalized;

                // Always update UI/filters
                onDataLoaded();

                if (successCallback) {
                    successCallback(normalized);
                }

                // Dispatch event for integrations
                window.dispatchEvent(new CustomEvent('mapDataLoaded', {
                    detail: { entities: normalized, count: normalized.length }
                }));

                // Check URL parameters
                initFromUrl();

                // Auto-location handled after data load without prompting
            })
            .catch(function(err) {
                console.error('Failed to load churches from API:', err);
                State.log('API load failed:', err.message);

                if (errorCallback) {
                    errorCallback(err);
                    return; // Don't retry if error callback provided (caller handles it)
                }

                // Retry once after a delay
                setTimeout(function() {
                    State.log('Retrying API load...');
                    fetch(apiUrl)
                        .then(function(r) { return r.json(); })
                        .then(function(data) {
                            var churches = data.churches || data || [];
                            State.log('Retry successful:', churches.length, 'churches loaded');

                            var normalized = churches.map(function(c) {
                                return {
                                    id: c.id,
                                    name: c.church_name || c.name,
                                    city: c.city,
                                    state: c.state,
                                    lat: parseFloat(c.latitude),
                                    lon: parseFloat(c.longitude),
                                    website: c.website,
                                    phone: c.phone,
                                    address: c.street_address || c.address,
                                    zip_code: c.zip_code,
                                    diocese_id: c.diocese_id,
                                    denomination: c.denomination || 'Catholic',
                                    slug: c.slug,
                                    description: c.description,
                                    mass_times: c.mass_times,
                                    confession_times: c.confession_times,
                                    adoration_times: c.adoration_times,
                                    has_mass: c.has_mass,
                                    has_confession: c.has_confession,
                                    has_adoration: c.has_adoration
                                };
                            }).filter(function(c) {
                                return !isNaN(c.lat) && !isNaN(c.lon);
                            });

                            State.update({
                                allChurches: normalized,
                                filteredItems: normalized.slice()
                            });

                            window.allChurches = normalized;
                            
                            if (successCallback) {
                                successCallback(normalized);
                            } else {
                                onDataLoaded();
                            }
                        })
                        .catch(function(retryErr) {
                            console.error('Retry also failed:', retryErr);
                            if (errorCallback) errorCallback(retryErr);
                        });
                }, 1000);
            });
    }

    /**
     * Called after data is loaded
     */
    function onDataLoaded() {
        var State = window.MapState;
        var config = State.getConfig();
        var allChurches = State.get('allChurches') || [];

        State.log('Data loaded:', allChurches.length, 'churches');

        // Populate filters
        if (window.MapFilters) {
            window.MapFilters.populateFilters();
        }

        // Update stats
        if (window.MapSidebar && window.MapSidebar.updateStats) {
            window.MapSidebar.updateStats();
        }

        // Display in sidebar
        if (window.MapSidebar) {
            window.MapSidebar.displayItems();
        }

        // Set default view based on domain and saved preference
        var hostname = window.location.hostname;
        var savedView = window.MapViews && window.MapViews.getSavedView ? window.MapViews.getSavedView() : null;
        var defaultView;

        if (savedView) {
            // User has a saved preference, use it
            defaultView = savedView;
            State.log('Using saved view preference:', savedView);
        } else {
            // Default: logos view (individual markers) for speed
            defaultView = 'logos';
            State.log('Defaulting to logos view for speed');
        }

        // Apply the view
        if (window.MapViews && window.MapViews.setView) {
            window.MapViews.setView(defaultView);
        } else {
            // Fallback: just add markers directly
            var useViewportLoading = State.get('useViewportLoading');
            if (window.MapMarkers && !useViewportLoading) {
                window.MapMarkers.addMarkersToMap();
            }
        }

        // Check for state/diocese URL params - if present, fit to data bounds
        var urlParams = new URLSearchParams(window.location.search);
        var stateParam = urlParams.get('state');
        var dioceseParam = urlParams.get('diocese');
        var map = State.get('map');

        if (map && allChurches.length > 0 && (stateParam || dioceseParam)) {
            // Fit map to the loaded data bounds (for state/diocese views)
            var bounds = L.latLngBounds(allChurches.map(function(c) {
                return [c.lat, c.lon];
            }));
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
            State.log('Fit bounds for', stateParam || dioceseParam, 'view');
        } else if (map && allChurches.length > 0 && !window.MAP_CENTER && !(config.map && config.map.center)) {
            // No configured defaults: fit to all data
            var allBounds = L.latLngBounds(allChurches.map(function(c) {
                return [c.lat, c.lon];
            }));
            map.fitBounds(allBounds, { padding: [50, 50] });
        } else if (map) {
            // Use diocese-aware defaults first (set by /api/config), then fall back to MAP_CONFIG.
            var center = window.MAP_CENTER ||
                (config.map && config.map.center) ||
                [39.8283, -98.5795];
            var zoom = window.MAP_ZOOM ||
                (config.map && config.map.zoom) ||
                4;
            map.setView(center, zoom);
        }

        // Auto-apply approximate location (no prompt) for a better default view
        maybeAutoLocate(map, allChurches);
    }

    /**
     * Use saved or approximate location without prompting
     */
    function maybeAutoLocate(map, allChurches) {
        var State = window.MapState;
        if (!map || !allChurches || allChurches.length === 0) return;
        if (!window.MapGeolocation) return;

        var isGlobalView = window.dioceseConfig && window.dioceseConfig.isGlobalView && window.dioceseConfig.isGlobalView();
        var dioceseBounds = window.dioceseConfig && window.dioceseConfig.getDioceseBounds
            ? window.dioceseConfig.getDioceseBounds()
            : null;
        var dataBounds = null;

        if (!dioceseBounds && allChurches.length > 0) {
            try {
                dataBounds = L.latLngBounds(allChurches.map(function(c) {
                    return [c.lat, c.lon];
                }));
            } catch (e) {
                dataBounds = null;
            }
        }

        var effectiveBounds = dioceseBounds || dataBounds;
        function isWithinEffectiveBounds(lat, lon) {
            if (isGlobalView) return true;
            if (!effectiveBounds) return false;
            if (window.MapGeolocation && window.MapGeolocation.isWithinDioceseBounds) {
                return window.MapGeolocation.isWithinDioceseBounds(lat, lon, effectiveBounds);
            }
            // Fallback: Leaflet bounds
            try {
                if (effectiveBounds && typeof effectiveBounds.contains === 'function') {
                    return effectiveBounds.contains([lat, lon]);
                }
            } catch (e) {
                // ignore
            }
            // Fallback: plain-object bounds
            if (typeof effectiveBounds.south === 'number' &&
                typeof effectiveBounds.north === 'number' &&
                typeof effectiveBounds.west === 'number' &&
                typeof effectiveBounds.east === 'number') {
                return lat >= effectiveBounds.south && lat <= effectiveBounds.north &&
                    lon >= effectiveBounds.west && lon <= effectiveBounds.east;
            }
            return false;
        }

        var urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('locate') || urlParams.get('select') || urlParams.get('state') || urlParams.get('diocese')) {
            return;
        }

        // Restore saved location if available
        if (window.MapGeolocation.getSavedLocation && window.MapGeolocation.applyLocation) {
            var savedLoc = window.MapGeolocation.getSavedLocation();
            if (savedLoc && typeof savedLoc.lat === 'number' && typeof savedLoc.lon === 'number') {
                if (isWithinEffectiveBounds(savedLoc.lat, savedLoc.lon)) {
                    window.MapGeolocation.applyLocation({ lat: savedLoc.lat, lon: savedLoc.lon, accuracy: savedLoc.accuracy || 0 }, {
                        map: map,
                        allChurches: allChurches
                    });
                    State.log('Restored saved user location');
                    return;
                }
            }
        }

        // Otherwise, attempt approximate location without prompting
        if (window.MapGeolocation.tryApproximateLocation) {
            window.MapGeolocation.tryApproximateLocation({
                map: map,
                allChurches: allChurches,
                bounds: effectiveBounds,
                isGlobal: isGlobalView
            });
        }
    }

    /**
     * Reset map to initial state
     */
    function reset() {
        var State = window.MapState;
        var config = State.getConfig();
        var allChurches = State.get('allChurches') || [];
        var map = State.get('map');

        State.set('filteredItems', allChurches.slice());
        State.set('selectedDay', 'all');

        // Reset filter dropdowns
        var stateFilter = document.getElementById('stateFilter');
        var cityFilter = document.getElementById('cityFilter');
        var searchInput = document.getElementById('searchInput');

        if (stateFilter) stateFilter.value = '';
        if (cityFilter) cityFilter.value = '';
        if (searchInput) searchInput.value = '';

        // Reset map view
        if (map) {
            var center = window.MAP_CENTER ||
                (config.map && config.map.center) ||
                [39.8283, -98.5795];
            var zoom = window.MAP_ZOOM ||
                (config.map && config.map.zoom) ||
                4;
            map.setView(center, zoom);
        }

        // Update UI
        if (window.MapSidebar && window.MapSidebar.updateStats) {
            window.MapSidebar.updateStats();
        }
        if (window.MapSidebar && window.MapSidebar.displayItems) {
            window.MapSidebar.displayItems();
        }
        // Re-render markers (skip if using viewport loading)
        var useViewportLoading = State.get('useViewportLoading');
        if (window.MapMarkers && !useViewportLoading) {
            window.MapMarkers.addMarkersToMap();
        }
    }

    /**
     * Initialize from URL parameters
     * Supports: ?select=ChurchName, ?locate=me
     */
    function initFromUrl() {
        var State = window.MapState;
        var params = new URLSearchParams(window.location.search);

        // Handle locate=me (geolocation)
        if (params.get('locate') === 'me') {
            State.log('URL: locate=me detected');
            if (window.MapGeolocation && window.MapGeolocation.findNearbyItems) {
                setTimeout(function() {
                    window.MapGeolocation.findNearbyItems();
                }, 500);
            } else {
                State.log('MapGeolocation module not loaded, cannot execute geolocation');
                // Fallback to show error message to user
                var mapElement = document.getElementById('map');
                if (mapElement) {
                    var errorMsg = document.createElement('div');
                    errorMsg.className = 'geo-error-toast';
                    errorMsg.innerHTML = 'Location feature temporarily unavailable. Please try again.';
                    errorMsg.style.cssText = 'position: absolute; top: 10px; left: 50%; transform: translateX(-50%); background: #ef4444; color: white; padding: 12px 20px; border-radius: 8px; z-index: 1000; font-family: system-ui, sans-serif;';
                    mapElement.appendChild(errorMsg);
                    setTimeout(function() {
                        if (errorMsg.parentNode) errorMsg.parentNode.removeChild(errorMsg);
                    }, 5000);
                }
            }
        }

        // Handle select parameter (auto-select a church)
        var selectName = params.get('select');
        if (selectName) {
            State.log('URL: select=' + selectName);
            var allChurches = State.get('allChurches') || [];
            var church = null;

            // Find by exact name match first
            for (var i = 0; i < allChurches.length; i++) {
                if (allChurches[i].name === selectName) {
                    church = allChurches[i];
                    break;
                }
            }

            // Try case-insensitive match
            if (!church) {
                var selectLower = selectName.toLowerCase();
                for (var j = 0; j < allChurches.length; j++) {
                    if (allChurches[j].name && allChurches[j].name.toLowerCase() === selectLower) {
                        church = allChurches[j];
                        break;
                    }
                }
            }

            if (church) {
                setTimeout(function() {
                    var map = State.get('map');
                    if (map && church.lat && church.lon) {
                        map.setView([church.lat, church.lon], 14);
                    }

                    // Select via MapSelection if available
                    if (window.MapSelection && window.MapSelection.selectItem) {
                        window.MapSelection.selectItem(church.name, church.city);
                    } else if (window.selectItem) {
                        window.selectItem(church.name, church.city);
                    }

                    // Open marker popup (using composite key)
                    var itemMarkers = State.get('itemMarkers') || {};
                    var markerKey = church.name + '_' + church.city;
                    if (itemMarkers[markerKey]) {
                        itemMarkers[markerKey].openPopup();
                    }
                }, 500);
            } else {
                State.log('URL: Church not found:', selectName);
            }
        }
    }

    /**
     * Simple debounce function
     */
    function debounce(fn, delay) {
        var timer = null;
        return function() {
            var context = this;
            var args = arguments;
            clearTimeout(timer);
            timer = setTimeout(function() {
                fn.apply(context, args);
            }, delay);
        };
    }

    // Public API
    window.MapApp = {
        init: init,
        reset: reset,
        onDataLoaded: onDataLoaded,
        loadFromApi: loadFromApi
    };

    // Auto-initialize on DOMContentLoaded if modules are loaded
    document.addEventListener('DOMContentLoaded', function() {
        // Check if all required modules are loaded
        if (window.MapState && window.MapInit && window.MapSidebar && window.MapMarkers) {
            // Delay slightly to ensure all scripts are parsed
            setTimeout(init, 50);
        } else {
            console.warn('MapApp: Not all modules loaded, waiting for manual init');
            console.log('  MapState:', !!window.MapState);
            console.log('  MapInit:', !!window.MapInit);
            console.log('  MapSidebar:', !!window.MapSidebar);
            console.log('  MapMarkers:', !!window.MapMarkers);
        }
    });

})(window);


// === analytics-integration.js ===
/**
 * Analytics Integration for Church Map
 *
 * Hooks into map events and sends analytics to ChurchAnalytics tracker.
 * Designed to work alongside existing event system.
 */
(function(window) {
    'use strict';

    var Analytics = window.ChurchAnalytics;
    var State = window.MapState;

    // Skip if analytics not loaded
    if (!Analytics) {
        console.warn('[MapAnalytics] ChurchAnalytics not loaded, skipping integration');
        return;
    }

    /**
     * Initialize analytics integration
     */
    function init() {
        // Track map load
        trackMapLoad();

        // Set up event listeners
        setupMapEvents();
        setupFilterEvents();
        setupSearchEvents();
        setupPopupEvents();

        // Expose trackEvent globally for markers.js and other modules
        window.trackEvent = trackEvent;

        console.log('[MapAnalytics] Integration initialized');
    }

    /**
     * Track a generic event (bridge function for legacy code)
     */
    function trackEvent(eventType, data) {
        if (!Analytics) return;

        switch (eventType) {
            case 'map_marker_click':
                Analytics.parishView(
                    data.slug || (data.church_name + '-' + data.church_city).toLowerCase().replace(/\s+/g, '-'),
                    data.church_name,
                    data.church_id
                );
                break;

            case 'popup_website_click':
                Analytics.websiteClick(data.slug, data.church_name, data.website_url);
                break;

            case 'popup_directions_click':
                Analytics.directionsClick(data.slug, data.church_name);
                break;

            case 'popup_phone_click':
                Analytics.phoneClick(data.slug, data.church_name);
                break;

            case 'popup_email_click':
                Analytics.emailClick(data.slug, data.church_name);
                break;

            case 'filter_change':
                Analytics.filterChange(data.filter_type, data.filter_value);
                break;

            case 'search':
                Analytics.search(data.query, data.filters, data.results_count);
                break;

            case 'reminder_signup':
                Analytics.reminderSignup(data.type, data.parish_slug);
                break;

            default:
                // Generic track
                Analytics.track(eventType, data);
        }
    }

    /**
     * Track initial map load
     */
    function trackMapLoad() {
        // Wait for map to be ready
        var checkMap = setInterval(function() {
            if (State && State.get('map')) {
                clearInterval(checkMap);

                var diocese = State.getDiocese ? State.getDiocese() : (window.DIOCESE || 'global');
                var churchCount = State.get('allChurches') ? State.get('allChurches').length : 0;

                Analytics.mapLoad({
                    metadata: {
                        diocese: diocese,
                        church_count: churchCount,
                        view_mode: State.get('currentView') || 'markers'
                    }
                });
            }
        }, 100);

        // Give up after 10 seconds
        setTimeout(function() {
            clearInterval(checkMap);
        }, 10000);
    }

    /**
     * Set up map interaction events
     */
    function setupMapEvents() {
        // Wait for map
        var checkMap = setInterval(function() {
            var map = State && State.get('map');
            if (!map) return;

            clearInterval(checkMap);

            // Track view mode changes
            var originalView = State.get('currentView');
            setInterval(function() {
                var currentView = State.get('currentView');
                if (currentView !== originalView) {
                    Analytics.mapInteraction('view_change', {
                        from_view: originalView,
                        to_view: currentView
                    });
                    originalView = currentView;
                }
            }, 500);

            // Track significant zoom changes (debounced)
            var lastZoom = map.getZoom();
            var zoomTimeout = null;

            map.on('zoomend', function() {
                var newZoom = map.getZoom();
                if (Math.abs(newZoom - lastZoom) >= 2) {
                    if (zoomTimeout) clearTimeout(zoomTimeout);
                    zoomTimeout = setTimeout(function() {
                        Analytics.mapInteraction('zoom', {
                            from_zoom: lastZoom,
                            to_zoom: newZoom,
                            direction: newZoom > lastZoom ? 'in' : 'out'
                        });
                        lastZoom = newZoom;
                    }, 1000);
                }
            });

        }, 100);

        setTimeout(function() {
            clearInterval(checkMap);
        }, 10000);
    }

    /**
     * Set up filter change tracking
     */
    function setupFilterEvents() {
        // Day filter
        var daySelect = document.getElementById('dayFilter');
        if (daySelect) {
            daySelect.addEventListener('change', function() {
                Analytics.filterChange('day', this.value);
            });
        }

        // Time filter
        var timeSelect = document.getElementById('timeFilter');
        if (timeSelect) {
            timeSelect.addEventListener('change', function() {
                Analytics.filterChange('time', this.value);
            });
        }

        // Language filter
        var langSelect = document.getElementById('languageFilter');
        if (langSelect) {
            langSelect.addEventListener('change', function() {
                Analytics.filterChange('language', this.value);
            });
        }

        // Sacrament toggles
        ['confessionToggle', 'adorationToggle'].forEach(function(id) {
            var toggle = document.getElementById(id);
            if (toggle) {
                toggle.addEventListener('change', function() {
                    var type = id.replace('Toggle', '');
                    Analytics.filterChange(type, this.checked ? 'on' : 'off');
                });
            }
        });
    }

    /**
     * Set up search tracking
     */
    function setupSearchEvents() {
        var searchInput = document.getElementById('searchInput') ||
                          document.getElementById('churchSearch') ||
                          document.querySelector('.search-input');

        if (!searchInput) return;

        var searchTimeout = null;
        var lastQuery = '';

        searchInput.addEventListener('input', function() {
            var query = this.value.trim();

            // Only track after user stops typing (debounce)
            if (searchTimeout) clearTimeout(searchTimeout);

            if (query.length >= 2 && query !== lastQuery) {
                searchTimeout = setTimeout(function() {
                    // Get current filters
                    var filters = {};
                    var dayFilter = document.getElementById('dayFilter');
                    var timeFilter = document.getElementById('timeFilter');
                    var langFilter = document.getElementById('languageFilter');

                    if (dayFilter && dayFilter.value) filters.day = dayFilter.value;
                    if (timeFilter && timeFilter.value) filters.time = timeFilter.value;
                    if (langFilter && langFilter.value) filters.language = langFilter.value;

                    // Get result count
                    var resultCount = State && State.get('filteredItems') ?
                        State.get('filteredItems').length : 0;

                    Analytics.search(query, filters, resultCount);
                    lastQuery = query;
                }, 1500);
            }
        });
    }

    /**
     * Set up popup link tracking
     */
    function setupPopupEvents() {
        // Use event delegation on document for popup links
        document.addEventListener('click', function(e) {
            var target = e.target;

            // Walk up to find link
            while (target && target !== document && target.tagName !== 'A') {
                target = target.parentElement;
            }

            if (!target || target.tagName !== 'A') return;

            // Check if inside a popup
            var popup = target.closest('.leaflet-popup');
            if (!popup) return;

            var href = target.getAttribute('href') || '';
            var parishSlug = popup.getAttribute('data-parish-slug') ||
                             extractSlugFromPopup(popup);
            var parishName = extractNameFromPopup(popup);

            // Determine link type
            if (href.indexOf('maps.google.com') !== -1 ||
                href.indexOf('maps.apple.com') !== -1 ||
                href.indexOf('directions') !== -1) {
                Analytics.directionsClick(parishSlug, parishName);
            } else if (href.indexOf('tel:') === 0) {
                Analytics.phoneClick(parishSlug, parishName);
            } else if (href.indexOf('mailto:') === 0) {
                Analytics.emailClick(parishSlug, parishName);
            } else if (target.classList.contains('popup-website') ||
                       target.textContent.toLowerCase().indexOf('website') !== -1 ||
                       (href.indexOf('http') === 0 && href.indexOf(location.host) === -1)) {
                Analytics.websiteClick(parishSlug, parishName, href);
            }
        });
    }

    /**
     * Extract parish slug from popup content
     */
    function extractSlugFromPopup(popup) {
        // Try to find a link to church detail page
        var detailLink = popup.querySelector('a[href*="/church/"]');
        if (detailLink) {
            var match = detailLink.getAttribute('href').match(/\/church\/([^\/\?]+)/);
            if (match) return match[1];
        }

        // Fall back to extracting from name
        var nameEl = popup.querySelector('.popup-name, .popup-header h3, h3');
        if (nameEl) {
            return nameEl.textContent.trim()
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-');
        }

        return 'unknown';
    }

    /**
     * Extract parish name from popup content
     */
    function extractNameFromPopup(popup) {
        var nameEl = popup.querySelector('.popup-name, .popup-header h3, h3');
        return nameEl ? nameEl.textContent.trim() : 'Unknown Parish';
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        // Small delay to ensure other modules are loaded
        setTimeout(init, 100);
    }

    // Export for testing
    window.MapAnalytics = {
        init: init,
        trackEvent: trackEvent
    };

})(window);

